import { FC, useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import { listMappedManualPlansForPart } from '../../QHSE/core/_requests'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { procurementTenantCgaId, procurementTenantVesselId } from '../core/procurementTenantContext'
import { libraryCrudAccess } from '../core/libraryPermissionAccess'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import {
  getInventory,
  getGeneralInventoryItems,
  getSubcatalogueTypes,
  createInventory,
  deleteInventory,
  getInventoryCategories,
  getInventoryItemHeads,
  createInventoryItemHead,
  updateInventoryItemHead,
  deleteInventoryItemHead,
  getInventoryItemSubHeads,
  createInventoryItemSubHead,
  updateInventoryItemSubHead,
  deleteInventoryItemSubHead,
  getInventoryItemSubCategories,
  createInventoryItemSubCategory,
  updateInventoryItemSubCategory,
  deleteInventoryItemSubCategory,
  getSubAccounts,
  getAllSubAccounts,
  getSubCatalogues,
  getAllSubCatalogues,
  getPartsBySubCatalogue,
  createInventoryCategory,
  updateInventoryCategory,
  createAccountingAccount,
  updateAccountingAccount,
  createSubAccount,
  updateSubAccount,
  createSubCatalogue,
  updateSubCatalogue,
  getEquipmentList,
  getCompanyAdminList,
  getUnitsOfMeasure,
  getPartStorageLocations,
  getAllInventoryCategoriesForCompany,
  getAllInventoryCategoriesForAllCompanies,
  getCompanyGroupAdmins,
  getSubCompanyAdmins,
  getVesselsByCompany,
  getVesselById,
  getAccountingAccounts,
  getAccountingAccountsWithParams,
  getAllAccountingAccounts,
  getPartById,
  deleteInventoryCategory,
  deleteAccountingAccount,
  deleteSubAccount,
  deleteSubCatalogue,
  deletePart
} from '../core/_requests'
import {
  getEquipmentList as getMachineryEquipmentList,
  getSubcomponentsByEquipment,
  getSubcomponents,
  getPartsBySubcomponent,
  getParts,
  deleteSubcomponent,
  deleteEquipment as deleteMachineryEquipment,
  deletePart as deleteMachineryPart,
  createEquipment as machineryCreateEquipment,
  updateEquipment as machineryUpdateEquipment,
  createSubcomponent as machineryCreateSubcomponent,
  updateSubcomponent as machineryUpdateSubcomponent,
  createPartForSubcomponent as machineryCreatePart,
  updatePart as machineryUpdatePart,
} from '../../Technical/core/_requests'
import {
  InventoryItemDisplay,
  InventoryItem,
  SubcatalogueType,
  InventoryCategory,
  InventoryItemHead,
  InventoryItemSubHead,
  CreateInventoryItemHeadRequest,
  InventoryItemSubCategory,
  AccountingAccount,
  SubAccount,
  SubCatalogue,
  CompanyAdmin,
  CompanyGroupAdmin,
  SubCompanyAdmin,
  Vessel,
  UnitOfMeasure,
  PartStorageLocation,
  CreateInventoryRequest
} from '../core/_models'
import type { ManualPlanDto } from '../../QHSE/core/_models'
import {EquipmentDto, SubcomponentDto, PartDto} from '../../Technical/core/_models'
import GeneralItemModal from './GeneralItemModal'
import AddItemModal from './AddItemModal'
import UpdateInventoryModal from './UpdateInventoryModal'
import { ManualPlanViewer } from '../../QHSE/components/ManualPlanViewer'
import CategoryModal from './CategoryModal'
import EditCategoryModal from './EditCategoryModal'
import HeadModal from './HeadModal'
import EditHeadModal from './EditHeadModal'
import SubHeadModal from './SubHeadModal'
import EditSubHeadModal from './EditSubHeadModal'
import AccountingCodeModal from './AccountingCodeModal'
import EditAccountingCodeModal from './EditAccountingCodeModal'
import SubAccountingModal from './SubAccountingModal'
import EditSubAccountingModal from './EditSubAccountingModal'
import SubCatalogueModal from './SubCatalogueModal'
import EditSubCatalogueModal from './EditSubCatalogueModal'
import PartStorageLocationLibraryModal from './PartStorageLocationLibraryModal'
import {EquipmentModal as MachineryEquipmentModal} from '../../Technical/components/Machinery/EquipmentModal'
import {SubcomponentModal as MachinerySubcomponentModal} from '../../Technical/components/Machinery/SubcomponentModal'
import {PartModal as MachineryPartModal} from '../../Technical/components/Machinery/PartModal'
import SearchableSelect from '../../../shared/components/SearchableSelect'

// ? Add a tiny <Cell> helper for per-cell spinner
const Cell: FC<{ value?: string; loading?: boolean }> = ({ value, loading }) => {
  if (loading) {
    return <span className="spinner-border spinner-border-sm text-muted" role="status" aria-hidden="true" />;
  }
  return <span>{value && value.trim() !== '' ? value : 'Â'}</span>;
};

// ? Simple table skeleton to reuse across tabs
const TableSkeleton: FC<{ rows?: number; cols: number }> = ({ rows = 8, cols }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={`sk-${i}`}>
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${i}-${c}`} className="py-3">
            <div className="placeholder-wave">
              <div className="placeholder w-100" style={{ height: 14, borderRadius: 4 }} />
            </div>
          </td>
        ))}
      </tr>
    ))}
  </>
);

// ?? Column counts for each table (match the <thead> th count)
const COLS = {
  inventory: 19,
  category: 5,
  head: 6,
  subHead: 6,
  generalItem: 7,
  equipment: 5,
  accounting: 5,
  subAccounting: 6,
  subCatalogue: 5,
  part: 6,
  subComponent: 5,
};

const formatCurrency = (value: number | string | null | undefined) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) {
    return '0.00';
  }
  return num.toFixed(2);
};

const formatYesNo = (val: boolean | null | undefined) => {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  return '-';
};

const formatDateSafe = (val: string | null | undefined) => {
  if (!val) return '-';
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

const valueOrDash = (val: string | number | null | undefined) => {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'string' && val.trim() === '') return '-';
  return String(val);
};

interface ViewInventoryModalProps {
  visible: boolean
  onClose: () => void
  item: InventoryItemDisplay | null
}

const ViewInventoryModal: FC<ViewInventoryModalProps> = ({ visible, onClose, item }) => {
  if (!visible || !item) return null

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { bg: string; text: string } } = {
      'Active': { bg: 'bg-success', text: 'Active' },
      'Obsolete': { bg: 'bg-secondary', text: 'Obsolete' },
      'Reserved': { bg: 'bg-warning', text: 'Reserved' }
    }
    const statusInfo = statusMap[status] || { bg: 'bg-secondary', text: status }
    return <span className={`badge ${statusInfo.bg} text-white`}>{statusInfo.text}</span>
  }

  const getStockStatusBadge = (stockStatus: string) => {
    if (!stockStatus) return null
    const statusMap: { [key: string]: { bg: string; text: string } } = {
      'Low Stock': { bg: 'bg-warning', text: 'Low Stock' },
      'Critical': { bg: 'bg-danger', text: 'Critical' }
    }
    const statusInfo = statusMap[stockStatus] || { bg: 'bg-info', text: stockStatus }
    return <span className={`badge ${statusInfo.bg} text-white ms-2`}>{statusInfo.text}</span>
  }

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered' role='document'>
        <div className='modal-content bg-white'>
          <div className='modal-header border-bottom'>
            <div>
              <h3 className='modal-title fw-bold text-dark mb-1'>Inventory Item Details</h3>
              <p className='text-muted mb-0 fs-7'>{item.itemId}</p>
            </div>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>

          <div className='modal-body' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Status and Basic Info */}
            <div className='row mb-4'>
              <div className='col-md-12'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <h5 className='fw-bold text-primary mb-0'>General Information</h5>
                  <div>
                    {item.stockStatus ? getStockStatusBadge(item.stockStatus) : getStatusBadge(item.status)}
                  </div>
                </div>
              </div>
            </div>

            <div className='row g-4 mb-4'>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7'>Item ID</label>
                <p className='fs-6 text-dark fw-bold'>{item.itemId}</p>
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7'>Item Name</label>
                <p className='fs-6 text-dark'>{item.itemName}</p>
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7'>Part Number</label>
                <p className='fs-6 text-dark'>{item.partNumber}</p>
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7'>Category</label>
                <p className='fs-6 text-dark'>{item.category}</p>
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7'>Part Type</label>
                <p className='fs-6 text-dark'>{item.partType || '?'}</p>
              </div>
              {item.description && (
                <div className='col-md-12'>
                  <label className='form-label fw-semibold text-muted fs-7'>Description</label>
                  <p className='fs-6 text-dark'>{item.description}</p>
                </div>
              )}
            </div>

            {/* Stock Information */}
            <div className='row mb-4'>
              <div className='col-md-12'>
                <h5 className='fw-bold text-primary mb-3'>Stock Information</h5>
              </div>
            </div>

            <div className='row g-4 mb-4'>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-muted fs-7'>Current Quantity</label>
                <p className='fs-6 text-dark fw-bold'>{item.currentQty} {item.uom}</p>
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-muted fs-7'>Reorder Level</label>
                <p className='fs-6 text-dark'>{item.reorderLevel}</p>
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-muted fs-7'>Unit of Measure</label>
                <p className='fs-6 text-dark'>{item.uom}</p>
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-muted fs-7'>Min Threshold</label>
                <p className='fs-6 text-dark'>{item.minThreshold}</p>
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-muted fs-7'>Max Threshold</label>
                <p className='fs-6 text-dark'>{item.maxThreshold}</p>
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-semibold text-muted fs-7'>Unit Cost</label>
                <p className='fs-6 text-dark fw-bold text-success'>${formatCurrency(item.unitCost)}</p>
              </div>
            </div>

            {/* Location & Identification */}
            <div className='row mb-4'>
              <div className='col-md-12'>
                <h5 className='fw-bold text-primary mb-3'>Location & Identification</h5>
              </div>
            </div>

            <div className='row g-4 mb-4'>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7'>Location</label>
                <p className='fs-6 text-dark'>{item.location}</p>
              </div>
              {item.serialNumber && (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold text-muted fs-7'>Serial Number</label>
                  <p className='fs-6 text-dark'>{item.serialNumber}</p>
                </div>
              )}
              {item.manufacturer && (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold text-muted fs-7'>Manufacturer</label>
                  <p className='fs-6 text-dark'>{item.manufacturer}</p>
                </div>
              )}
              {item.expiryDate && (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold text-muted fs-7'>Expiry Date</label>
                  <p className='fs-6 text-dark'>{new Date(item.expiryDate).toLocaleDateString()}</p>
                </div>
              )}
            </div>

            {/* Additional Information */}
            {(item.subAccounting || item.subComponent || item.equipmentHierarchy || item.supplier || item.company || item.vesselType) && (
              <>
                <div className='row mb-4'>
                  <div className='col-md-12'>
                    <h5 className='fw-bold text-primary mb-3'>Additional Information</h5>
                  </div>
                </div>

                <div className='row g-4'>
                  {item.subAccounting && (
                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-muted fs-7'>Sub Accounting</label>
                      <p className='fs-6 text-dark'>{item.subAccounting}</p>
                    </div>
                  )}
                  {item.subComponent && (
                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-muted fs-7'>Sub Component</label>
                      <p className='fs-6 text-dark'>{item.subComponent}</p>
                    </div>
                  )}
                  {item.equipmentHierarchy && (
                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-muted fs-7'>Equipment Hierarchy</label>
                      <p className='fs-6 text-dark'>{item.equipmentHierarchy}</p>
                    </div>
                  )}
                  {item.supplier && (
                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-muted fs-7'>Supplier</label>
                      <p className='fs-6 text-dark'>{item.supplier}</p>
                    </div>
                  )}
                  {item.company && (
                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-muted fs-7'>Company</label>
                      <p className='fs-6 text-dark'>{item.company}</p>
                    </div>
                  )}
                  {(item.vesselName || item.vesselType) && (
                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-muted fs-7'>Vessel</label>
                      <p className='fs-6 text-dark'>{item.vesselName || item.vesselType}</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className='modal-footer border-top'>
            <button type='button' className='btn btn-light' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const InventoryList: FC = () => {
  const { currentUser } = useAuth()
  const [activeTab, setActiveTab] = useState<'catalogue' | 'addItem' | 'hierarchy'>('catalogue')
  const showLegacyInventoryTabs = false
  const [generalItemsLibraryOpen, setGeneralItemsLibraryOpen] = useState(false)
  const [addItemSubTab, setAddItemSubTab] = useState<
    'category' | 'head' | 'subHead' | 'generalItem' | 'equipment' | 'subComponent' | 'part' | 'accountingCode' | 'subAccounting' | 'subCatalogue'
  >('category')
  const showGeneralItemLibraryContent = generalItemsLibraryOpen
  const isShowingGeneralItems = false // render general items only inside modal, not inline
  const [searchTerm, setSearchTerm] = useState('')

  // Company filter state for all tabs
  const [categoryCompanyFilter, setCategoryCompanyFilter] = useState<string>('')
  const [categoryVesselFilter, setCategoryVesselFilter] = useState<string>('')
  const [accountingCodeCompanyFilter, setAccountingCodeCompanyFilter] = useState<string>('')
  const [subAccountingCompanyFilter, setSubAccountingCompanyFilter] = useState<string>('')
  const [subCatalogueCompanyFilter, setSubCatalogueCompanyFilter] = useState<string>('')
  const [partCompanyFilter, setPartCompanyFilter] = useState<string>('')
  const [subComponentCompanyFilter, setSubComponentCompanyFilter] = useState<string>('')
  const [equipmentCompanyFilter, setEquipmentCompanyFilter] = useState<string>('')
  const [companiesForFilter, setCompaniesForFilter] = useState<CompanyGroupAdmin[]>([])
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false)
  const [vesselsForFilter, setVesselsForFilter] = useState<Vessel[]>([])
  const [isLoadingVessels, setIsLoadingVessels] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [inventoryData, setInventoryData] = useState<InventoryItemDisplay[]>([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false)
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<InventoryItemDisplay | null>(null)
  const [categories, setCategories] = useState<SubcatalogueType[]>([])
  const [parts, setParts] = useState<PartDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [totalElements, setTotalElements] = useState(0)
  const [vesselId, setVesselId] = useState<number | null>(null) // Dynamic vesselId - null means fetch all for superadmin

  // Individual modal states for each tab
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false)
  const [isEditCategoryModalVisible, setIsEditCategoryModalVisible] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<InventoryCategory | null>(null)
  const [isHeadModalVisible, setIsHeadModalVisible] = useState(false)
  const [isEditHeadModalVisible, setIsEditHeadModalVisible] = useState(false)
  const [selectedHead, setSelectedHead] = useState<InventoryItemHead | null>(null)
  const [isSubHeadModalVisible, setIsSubHeadModalVisible] = useState(false)
  const [isEditSubHeadModalVisible, setIsEditSubHeadModalVisible] = useState(false)
  const [selectedSubHead, setSelectedSubHead] = useState<InventoryItemSubHead | null>(null)
  const [isGeneralItemModalVisible, setIsGeneralItemModalVisible] = useState(false)
  const [isAccountingCodeModalVisible, setIsAccountingCodeModalVisible] = useState(false)
  const [isEditAccountingCodeModalVisible, setIsEditAccountingCodeModalVisible] = useState(false)
  const [selectedAccountingCode, setSelectedAccountingCode] = useState<AccountingAccount | null>(null)
  const [isSubAccountingModalVisible, setIsSubAccountingModalVisible] = useState(false)
  const [isEditSubAccountingModalVisible, setIsEditSubAccountingModalVisible] = useState(false)
  const [selectedSubAccount, setSelectedSubAccount] = useState<SubAccount | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      if (params.get('openGeneralItems') === '1') {
        setGeneralItemsLibraryOpen(true)
      }
    }
  }, [])
  const [isSubCatalogueModalVisible, setIsSubCatalogueModalVisible] = useState(false)
  const [isEditSubCatalogueModalVisible, setIsEditSubCatalogueModalVisible] = useState(false)
  const [selectedSubCatalogue, setSelectedSubCatalogue] = useState<SubCatalogue | null>(null)
  const [isPartModalVisible, setIsPartModalVisible] = useState(false)
  const [selectedPart, setSelectedPart] = useState<PartDto | null>(null)
  const [isSubComponentModalVisible, setIsSubComponentModalVisible] = useState(false)
  const [selectedSubComponent, setSelectedSubComponent] = useState<SubcomponentDto | null>(null)
  const [isEquipmentModalVisible, setIsEquipmentModalVisible] = useState(false)
  const [equipmentModalMode, setEquipmentModalMode] = useState<'create' | 'edit'>('create')
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentDto | null>(null)
  const [subComponentParentEquipmentId, setSubComponentParentEquipmentId] = useState<number | null>(null)
  const [partParentEquipmentId, setPartParentEquipmentId] = useState<number | null>(null)
  const [partParentSubComponentId, setPartParentSubComponentId] = useState<number | null>(null)
  const [expandedEquipments, setExpandedEquipments] = useState<Set<number>>(new Set())
  const [expandedSubcomponents, setExpandedSubcomponents] = useState<Set<number>>(new Set())
  const [hierarchyLoading, setHierarchyLoading] = useState(false)
  const [viewDetail, setViewDetail] = useState<{ open: boolean; kind?: 'equipment' | 'subcomponent' | 'part'; data?: any }>({ open: false })
  const [mappedManualsForPart, setMappedManualsForPart] = useState<ManualPlanDto[]>([])
  const [mappedManualsLoading, setMappedManualsLoading] = useState(false)
  const [selectedMappedManual, setSelectedMappedManual] = useState<{
    id: number
    name: string
    uploadedDate: string
    file: {name: string}
    remarks?: string
  } | null>(null)
  const [showSubComponentCreateModal, setShowSubComponentCreateModal] = useState(false)
  const [showPartCreateModal, setShowPartCreateModal] = useState(false)
  const [subcomponentCompanyFilter, setSubcomponentCompanyFilter] = useState<string>('')
  const [subcomponentVesselOptions, setSubcomponentVesselOptions] = useState<Vessel[]>([])
  const [subcomponentVesselId, setSubcomponentVesselId] = useState<string>('')
  const [subcomponentEquipmentOptions, setSubcomponentEquipmentOptions] = useState<EquipmentDto[]>([])
  const [subcomponentName, setSubcomponentName] = useState<string>('')
  const [subcomponentCode, setSubcomponentCode] = useState<string>('')
  const [subcomponentDesc, setSubcomponentDesc] = useState<string>('')
  const [subcomponentCriticality, setSubcomponentCriticality] = useState<boolean>(false)
  const [subcomponentSaving, setSubcomponentSaving] = useState(false)
  const [partCompanyPicker, setPartCompanyPicker] = useState<string>('')
  const [partVesselOptions, setPartVesselOptions] = useState<Vessel[]>([])
  const [partVesselId, setPartVesselId] = useState<string>('')
  const [partEquipmentOptions, setPartEquipmentOptions] = useState<EquipmentDto[]>([])
  const [partEquipmentId, setPartEquipmentId] = useState<string>('')
  const [partSubcomponentOptions, setPartSubcomponentOptions] = useState<SubcomponentDto[]>([])
  const [partSubcomponentId, setPartSubcomponentId] = useState<string>('')
  const [isStorageLibraryVisible, setIsStorageLibraryVisible] = useState(false)
  const [partName, setPartName] = useState<string>('')
  const [partCode, setPartCode] = useState<string>('')
  const [partLocation, setPartLocation] = useState<string>('')
  const [partManufacturer, setPartManufacturer] = useState<string>('')
  const [partSerialNumber, setPartSerialNumber] = useState<string>('')
  const [partNumber, setPartNumber] = useState<string>('')
  const [partUnitId, setPartUnitId] = useState<string>('')
  const [partRob, setPartRob] = useState<string>('0')
  const [unitOptions, setUnitOptions] = useState<UnitOfMeasure[]>([])
  const [storageLocations, setStorageLocations] = useState<PartStorageLocation[]>([])
  const [storageLocationId, setStorageLocationId] = useState<string>('')
  const [storageSubLocationId, setStorageSubLocationId] = useState<string>('')
  const [storageSegmentId, setStorageSegmentId] = useState<string>('')
  const [isSparePart, setIsSparePart] = useState<boolean>(false)
  const [partSaving, setPartSaving] = useState(false)

  // Refresh triggers for modal sub catalogues
  const [addItemModalRefreshTrigger, setAddItemModalRefreshTrigger] = useState(0)

  // Data states for each tab
  const [categoryData, setCategoryData] = useState<InventoryCategory[]>([])
  const [headData, setHeadData] = useState<InventoryItemHead[]>([])
  const [subHeadData, setSubHeadData] = useState<InventoryItemSubHead[]>([])
  const [generalItemData, setGeneralItemData] = useState<InventoryItem[]>([])
  const [accountingCodeData, setAccountingCodeData] = useState<AccountingAccount[]>([])
  const [subAccountingData, setSubAccountingData] = useState<SubAccount[]>([])
  const [subCatalogueData, setSubCatalogueData] = useState<SubCatalogue[]>([])
  const [partData, setPartData] = useState<PartDto[]>([])
  const [subComponentData, setSubComponentData] = useState<SubcomponentDto[]>([])
  const [equipmentData, setEquipmentData] = useState<EquipmentDto[]>([])
  const [companyOptions, setCompanyOptions] = useState<CompanyGroupAdmin[]>([])
  const [isLoadingSubComponents, setIsLoadingSubComponents] = useState(false)
  // ?? Loading states for Add Item tables
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [headLoading, setHeadLoading] = useState(false);
  const [subHeadLoading, setSubHeadLoading] = useState(false);
  const [generalItemLoading, setGeneralItemLoading] = useState(false);
  const [accountingLoading, setAccountingLoading] = useState(false);
  const [subAccountingLoading, setSubAccountingLoading] = useState(false);
  const [subCatalogueLoading, setSubCatalogueLoading] = useState(false);
  const [partLoading, setPartLoading] = useState(false);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  // subComponent already has: isLoadingSubComponents

  // Pagination states for category table
  const [categoryCurrentPage, setCategoryCurrentPage] = useState(1)
  const [categoryRowsPerPage, setCategoryRowsPerPage] = useState(10)

  // Pagination states for head table
  const [headCurrentPage, setHeadCurrentPage] = useState(1)
  const [headRowsPerPage, setHeadRowsPerPage] = useState(10)

  // Pagination states for sub head table
  const [subHeadCurrentPage, setSubHeadCurrentPage] = useState(1)
  const [subHeadRowsPerPage, setSubHeadRowsPerPage] = useState(10)

  // Pagination states for general items table
  const [generalItemCurrentPage, setGeneralItemCurrentPage] = useState(1)
  const [generalItemRowsPerPage, setGeneralItemRowsPerPage] = useState(10)
  const [generalItemCompanyFilter, setGeneralItemCompanyFilter] = useState<string>('ALL')
  const [generalItemVesselFilter, setGeneralItemVesselFilter] = useState<string>('ALL')
  const [generalItemLocationFilter, setGeneralItemLocationFilter] = useState<string>('ALL')

  // Pagination states for accounting code table
  const [accountingCodeCurrentPage, setAccountingCodeCurrentPage] = useState(1)
  const [accountingCodeRowsPerPage, setAccountingCodeRowsPerPage] = useState(10)

  // Pagination states for sub accounting table
  const [subAccountingCurrentPage, setSubAccountingCurrentPage] = useState(1)
  const [subAccountingRowsPerPage, setSubAccountingRowsPerPage] = useState(10)

  // Pagination states for sub catalogue table
  const [subCatalogueCurrentPage, setSubCatalogueCurrentPage] = useState(1)
  const [subCatalogueRowsPerPage, setSubCatalogueRowsPerPage] = useState(10)

  // Pagination states for part table
  const [partCurrentPage, setPartCurrentPage] = useState(1)
  const [partRowsPerPage, setPartRowsPerPage] = useState(10)

  // Pagination states for sub component table
  const [subComponentCurrentPage, setSubComponentCurrentPage] = useState(1)
  const [subComponentRowsPerPage, setSubComponentRowsPerPage] = useState(10)

  const [sortConfig, setSortConfig] = useState<{
    key: keyof InventoryItemDisplay | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' })

  // Sort configurations for each tab
  const [categorySortConfig, setCategorySortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  })

  const [headSortConfig, setHeadSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  })

  const [subHeadSortConfig, setSubHeadSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  })

  const [generalItemSortConfig, setGeneralItemSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  })

  const [accountingCodeSortConfig, setAccountingCodeSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  })

  const [subAccountingSortConfig, setSubAccountingSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  })

  const [subCatalogueSortConfig, setSubCatalogueSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  })

  const [partSortConfig, setPartSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  })

  const [subComponentSortConfig, setSubComponentSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc',
  })

  // ?? Loader states for table skeleton (boot vs subsequent fetches)
  const [bootLoading, setBootLoading] = useState(true);        // first ever load
  const [listLoading, setListLoading] = useState(false);        // subsequent list fetches
  const [noDataDelayPassed, setNoDataDelayPassed] = useState(false); // avoid flicker before "No data"

  // ?? Marks when lazy field-mapping (name, uom, subAccounting, etc.) is done
  const [mapReady, setMapReady] = useState(false);

  // Optional: number of columns used in table <td colSpan>
  const COLUMNS_COUNT = 19;

  // Permission-based controls
  const {
    canAny: canAnyProcurementPermission,
    can: canProcurementPermission,
    loading: procurementPermissionsLoading,
    getScope: getProcurementScope,
  } = useProcurementPermissions()
  const canViewInventory = canAnyProcurementPermission([
    PROCUREMENT_PERMS.READ_LIBRARY_CATEGORY,
    PROCUREMENT_PERMS.LIBRARY_HEAD_VIEW,
    PROCUREMENT_PERMS.LIBRARY_SUBHEAD_VIEW,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_VIEW,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_VIEW,
    PROCUREMENT_PERMS.LIBRARY_SUBACCOUNTING_VIEW,
    PROCUREMENT_PERMS.LIBRARY_ADDRESS_VIEW,
  ])
  const canCreateItems = canAnyProcurementPermission([
    PROCUREMENT_PERMS.CREATE_LIBRARY_CATEGORY,
    PROCUREMENT_PERMS.LIBRARY_HEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBHEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_ADDRESS_MANAGE,
  ])
  const canCreateCategoryLibrary = libraryCrudAccess.category.create(canProcurementPermission)
  const canUpdateCategoryLibrary = libraryCrudAccess.category.update(canProcurementPermission)
  const canDeleteCategoryLibrary = libraryCrudAccess.category.delete(canProcurementPermission)
  const inventoryViewScope =
    getProcurementScope(PROCUREMENT_PERMS.READ_LIBRARY_CATEGORY) ||
    getProcurementScope(PROCUREMENT_PERMS.READ_REQUISITION)
  const libraryScope = (inventoryViewScope ?? 'VESSEL') as 'ALL' | 'COMPANY' | 'VESSEL'
  const tenantCgaId = useMemo(() => procurementTenantCgaId(currentUser), [currentUser])
  const tenantVesselId = useMemo(() => procurementTenantVesselId(currentUser), [currentUser])
  const isGlobalScopeUser = libraryScope === 'ALL'
  const isCompanyScopeUser = libraryScope === 'COMPANY'
  const showEquipmentCompanyCol = isGlobalScopeUser
  const showEquipmentVesselCol = isGlobalScopeUser || isCompanyScopeUser
  const equipmentBaseCols = 14 // columns excluding company & vessel
  const equipmentColumnCount =
    equipmentBaseCols +
    (showEquipmentCompanyCol ? 1 : 0) +
    (showEquipmentVesselCol ? 1 : 0)
  const showSubComponentCompanyCol = isGlobalScopeUser
  const showSubComponentVesselCol = isGlobalScopeUser || isCompanyScopeUser
  const subComponentBaseCols = 14 // excluding company & vessel
  const subComponentColumnCount =
    subComponentBaseCols +
    (showSubComponentCompanyCol ? 1 : 0) +
    (showSubComponentVesselCol ? 1 : 0)
  const showPartCompanyCol = isGlobalScopeUser
  const showPartVesselCol = isGlobalScopeUser || isCompanyScopeUser
  const partBaseCols = 16 // excluding company & vessel
  const partColumnCount =
    partBaseCols +
    (showPartCompanyCol ? 1 : 0) +
    (showPartVesselCol ? 1 : 0)

  // ---- Lazy mapping helpers & caches (inside component) ----

  // Caches scoped to this component instance
  const partCache = useRef<Map<number, PartDto>>(new Map());
  const vesselCache = useRef<Map<number, Vessel>>(new Map());

  // Sub-Catalogue cache (id -> SubCatalogue)
  const subcatCache = useRef<Map<number, SubCatalogue>>(new Map());

  // Sub-Component cache (id -> SubComponent)
  const subComponentCache = useRef<Map<number, SubcomponentDto>>(new Map());

  // Small concurrency limiter
  const concurrency = 5;
  const limit = useCallback(<T,>(pool: number, tasks: Array<() => Promise<T>>): Promise<Promise<T>[]> => {
    const results: Promise<T>[] = [];
    let i = 0;
    const run = async (): Promise<void> => {
      while (i < tasks.length) {
        const idx = i++;
        results[idx] = tasks[idx]();
        await results[idx].catch(() => undefined as unknown as T);
      }
    };
    return Promise.all(Array.from({ length: Math.min(pool, tasks.length) }, run)).then(() => results);
  }, []);

  // Resolve & fill mapping for visible rows only
  const lazyMapInventoryCells = useCallback(async (rows: InventoryItemDisplay[]) => {
    try {
      // collect unique ids
      const partIds = Array.from(new Set(rows.map(r => r.partId).filter(Boolean))) as number[];
      const vesselIds = Array.from(new Set(rows.map(r => r.vesselId).filter(Boolean))) as number[];
      const subComponentIds = Array.from(new Set(rows.map(r => r.subcomponentId).filter(Boolean))) as number[];

      // PARTS
      const partTasks = partIds
        .filter(id => !partCache.current.has(id))
        .map(id => async () => {
          try {
            const p = await getPartById(id);
            partCache.current.set(id, p as unknown as PartDto);
          } catch { /* noop */ }
        });

      // VESSELS
      const vesselTasks = vesselIds
        .filter(id => !vesselCache.current.has(id))
        .map(id => async () => {
          try {
            const v = await getVesselById(id);
            vesselCache.current.set(id, v);
          } catch { /* noop */ }
        });

      // SUB COMPONENTS - fetch all once and cache
      if (subComponentIds.length > 0 && subComponentCache.current.size === 0) {
        try {
          const equipmentList = await getMachineryEquipmentList(vesselId ?? undefined)
          const subs = await Promise.all(
            (equipmentList || [])
              .filter(eq => eq.id)
              .map(eq => getSubcomponentsByEquipment(eq.id!, false))
          )
          subs.flat().forEach(sc => {
            if (sc?.id) subComponentCache.current.set(sc.id, sc);
          });
        } catch { /* noop */ }
      }

      await Promise.all([
        limit(concurrency, partTasks),
        limit(concurrency, vesselTasks),
      ]);

      // Apply mapped values in one state update to avoid reflows
      setInventoryData((prev: InventoryItemDisplay[]) =>
        prev.map((r: InventoryItemDisplay) => {
          const p = r.partId ? partCache.current.get(r.partId) : undefined;
          const v = r.vesselId ? vesselCache.current.get(r.vesselId) : undefined;
          const subComp = r.subcomponentId ? subComponentCache.current.get(r.subcomponentId) : undefined;
          const hasPart = Boolean(p);

          // Category from Part->SubCatalogue
            const categoryName =
              (p as any)?.subcatalogue?.subCatalogueType?.name ||
              (p as any)?.subcatalogue?.name ||
              '';

          // Sub Accounting from:
          // 1) subcatCache by r.subcatId
          // 2) or from part->subcatalogue if present
          // 3) fallback to 'N/A'
          let subAcc = 'N/A';
          if (r.subcatId && subcatCache.current.has(r.subcatId)) {
            const sc = subcatCache.current.get(r.subcatId)!;
            // try common property names safely
            // @ts-ignore
            subAcc = sc?.subaccountCodes || sc?.subAccountCodes || sc?.subAccountCode || 'N/A';
          } else if ((p as any)?.subcatalogue) {
            // @ts-ignore
            subAcc = (p as any).subcatalogue?.subaccountCodes || (p as any).subcatalogue?.subAccountCodes || (p as any).subcatalogue?.subAccountCode || 'N/A';
          }

          // REPLACE the returned object inside lazyMapInventoryCells mapping
          const mappedUom = (p as any)?.uom?.code || '';

          return {
            ...r,
            itemName: hasPart ? (p?.name || r.itemName) : r.itemName,
            partNumber: hasPart ? ((p as any)?.partNo || (p as any)?.code || r.partNumber) : r.partNumber,
            manufacturer: hasPart ? ((p as any)?.manufacturer || r.manufacturer) : r.manufacturer,
            uom: hasPart ? (mappedUom || r.uom) : r.uom,
            quantity: hasPart ? `${r.currentQty} ${mappedUom.trim()}`.trim() : r.quantity,
            category: hasPart ? categoryName : r.category,
            subAccounting: (r.subcatId || hasPart) ? subAcc : r.subAccounting,
            subComponent: subComp?.name || r.subComponent || '',
            isSpare: hasPart ? ((p as any)?.isSpare ?? r.isSpare) : r.isSpare,
            partType: (hasPart ? ((p as any)?.isSpare ?? r.isSpare) : r.isSpare) === undefined
              ? (r.partType || '')
              : (((p as any)?.isSpare ?? r.isSpare) ? 'Spare' : 'In Use'),

            // Only fill these if we *didn't* already get them from the API
            company: r.company || (v as any)?.companyGroupAdmin?.name || (v as any)?.companyAdmin?.name || '',
            vesselName: r.vesselName || (v as any)?.fleet_name || '',
            vesselType: r.vesselType || (v as any)?.vesselType || '',
          };
        })
      );
    } finally {
      // mark mapping finished (stops per-cell loaders)
      setMapReady(true);
    }
  }, [limit, setInventoryData]);


  // Load INVENTORY immediately on mount & whenever page/size/vessel changes
  useEffect(() => {
    loadInventory(); // no gating Â shows skeleton first, then rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, vesselId])

  // OPTIONAL: once parts arrive, re-run to enrich names/UOM without blocking first paint
  useEffect(() => {
    if (parts.length > 0) {
      loadInventory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts])

  // Keep a local cache for fast id -> subCatalogue lookups
  useEffect(() => {
    if (Array.isArray(subCatalogueData) && subCatalogueData.length > 0) {
      const map = new Map<number, SubCatalogue>();
      for (const sc of subCatalogueData) {
        if (sc?.id != null) map.set(sc.id as number, sc);
      }
      subcatCache.current = map;
    }
  }, [subCatalogueData]);

  // Debug logging for permissions
  useEffect(() => {
    console.log('InventoryList - Library scope:', libraryScope)
    console.log('InventoryList - Can Create Items:', canCreateItems)
    console.log('InventoryList - Current User:', currentUser)
  }, [libraryScope, canCreateItems, currentUser])

  // Redirect unauthorized users away from Add Item tab
  useEffect(() => {
    if (activeTab === 'addItem' && !canCreateItems) {
      setActiveTab('catalogue')
      setGeneralItemsLibraryOpen(false)
    }
  }, [activeTab, canCreateItems])

  // Load general items when the modal opens
  useEffect(() => {
    if (generalItemsLibraryOpen) {
      loadGeneralItemData()
    }
  }, [generalItemsLibraryOpen])

  const [filters, setFilters] = useState({
    category: '',
    location: '',
    status: ''
  })

  // useEffect(() => {
  //   loadInventory();
  //   loadInitialData()
  //   loadCategoryData() // Load category data when component mounts
  //   loadAccountingCodeData() // Load accounting code data when component mounts
  //   loadSubAccountingData() // Load sub accounting data when component mounts
  //   loadSubCatalogueData() // Load sub catalogue data when component mounts
  //   loadCompaniesForFilter() // Load companies for filter
  // }, [])

  // ? First paint: inventory only (sub-catalogues loaded on-demand when tab is opened)
  useEffect(() => {
    loadInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const loadInitialData = async () => {
    setIsLoading(true)
    try {
      const [categoriesData, partsData] = await Promise.all([
        getSubcatalogueTypes(),
        (async () => {
          const equipment = await getMachineryEquipmentList(vesselId ?? undefined)
          const subs = await Promise.all(
            (equipment || [])
              .filter(eq => eq.id)
              .map(eq => getSubcomponentsByEquipment(eq.id!, false))
          )
          const partsLists = await Promise.all(
            subs.flat().filter(sc => sc.id).map(sc => getPartsBySubcomponent(sc.id!))
          )
          return partsLists.flat()
        })()
      ])
      setCategories(categoriesData)
      setParts(partsData as PartDto[])
    } catch (error) {
      console.error('Error loading initial data:', error)
      toast.error('Failed to load initial data. Please refresh the page.')
    } finally {
      setIsLoading(false)
    }
  }

  const loadInventory = async () => {
    setListLoading(true);
    try {
      const cgaIdToUse = libraryScope === 'COMPANY' ? tenantCgaId : undefined;

      let effectiveVesselId = vesselId;
      if (libraryScope === 'ALL') {
        effectiveVesselId = null;
      } else if (effectiveVesselId == null && tenantVesselId) {
        effectiveVesselId = tenantVesselId;
        setVesselId(tenantVesselId);
      } else if (effectiveVesselId == null) {
        const companyUsername =
          (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username;
        if (companyUsername) {
          try {
            const vessels = await getVesselsByCompany(companyUsername);
            if (vessels.length > 0) {
              effectiveVesselId = vessels[0].id;
              setVesselId(vessels[0].id);
            }
          } catch (error) {
            console.error('Error loading vessels:', error);
          }
        }
      }

      // 1) fetch ONLY the page of base rows
      const pageRes = await getInventory(effectiveVesselId, currentPage - 1, rowsPerPage, cgaIdToUse);

      // 2) paint ASAP with placeholders (no secondary mapping calls here)
      // REPLACE the baseRows mapping with this version
      const baseRows: InventoryItemDisplay[] = pageRes.content.map((it: any) => {
        const vesselObj = it?.vessel; // <- NEW: directly from updated API
        const companyName =
          vesselObj?.companyGroupAdmin?.name ??
          vesselObj?.companyAdmin?.name ??
          '';

        const vType = vesselObj?.vesselType ?? '';
        const vName = vesselObj?.fleet_name ?? '';
        const partIsSpare = it?.part?.isSpare ?? undefined;
        const isGeneralItem = !it?.partId && (it?.manualItemName || it?.unitOfMeasurement);
        const manualName = it?.manualItemName ?? '';
        const manualUom = it?.unitOfMeasurement ?? '';

        // Extract sub accounting from the subcat object if available
        const subcatObj = it?.subcat;
        let subAccounting = 'N/A';
        if (subcatObj?.subaccounts) {
          subAccounting = subcatObj.subaccounts?.code || subcatObj.subaccounts?.name || (typeof subcatObj.subaccounts === 'string' ? subcatObj.subaccounts : 'N/A');
        }

        const parsedUnitCost = Number(it?.unitCost);
        const unitCostValue = Number.isFinite(parsedUnitCost) ? parsedUnitCost : 0;

        return {
          id: it.id,
          itemId: `INV-${String(it.id).padStart(3, '0')}`,
          itemName: isGeneralItem ? manualName : '',          // (still lazy mapped from part if needed)
          partNumber: isGeneralItem ? '-' : '',        // (still lazy mapped)
          category: isGeneralItem ? 'General Stores' : '',          // (still lazy mapped)
          subAccounting: subAccounting,  // Get from API response
          subComponent: '',      // (lazy mapped)
          isSpare: partIsSpare,
          partType: partIsSpare === undefined ? '' : (partIsSpare ? 'Spare' : 'In Use'),
          quantity: isGeneralItem
            ? `${it.currentQty} ${manualUom}`.trim()
            : `${it.currentQty}`, // UOM lazy mapped
          location: it.location,
          status: 'Active',
          stockStatus:
            it.currentQty <= it.minThreshold ? 'Critical' :
              it.currentQty <= it.reorderLevel ? 'Low Stock' : '',
          currentQty: it.currentQty,
          reorderLevel: it.reorderLevel,
          minThreshold: it.minThreshold,
          maxThreshold: it.maxThreshold,
          unitCost: unitCostValue,
          manufacturer: '',      // lazy mapped
          uom: isGeneralItem ? manualUom : '',               // lazy mapped
          expiryDate: it.expiryDate,
          serialNumber: it.serialNumber ?? null,

          vesselId: it.vesselId,
          partId: it.partId ?? null,
          subcatId: it.subcatId ?? null,
          subcomponentId: it.subcomponentId ?? null,

          // NEW: fill immediately from response so we don't need extra GETs
          company: companyName,
          vesselName: vName,
          vesselType: vType,
        };
      });

      setInventoryData(baseRows);
      setTotalElements(pageRes.totalElements);

      // 3) after paint: lazy-map heavy fields with small concurrency and caching
      //    (no getAllSubCatalogues; we avoid N*N calls)
      // 3) after paint: lazy-map heavy fields with small concurrency and caching
      setMapReady(false);
      queueMicrotask(() => {
        lazyMapInventoryCells(baseRows).finally(() => setMapReady(true));
      });
    } catch (e) {
      console.error('Error loading inventory:', e);
    } finally {
      setIsLoading(false);
      setListLoading(false);
      if (bootLoading) setBootLoading(false);
    }
  };


  const handleViewItem = (item: InventoryItemDisplay) => {
    setSelectedInventoryItem(item)
    setIsViewModalVisible(true)
  }

  const handleDeleteItem = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this inventory item?')) return

    try {
      await deleteInventory(id)
      await loadInventory()
      toast.success('Inventory item deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting inventory item:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete inventory item'

      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this inventory item because it has associated records. Please check dependencies first.')
      } else {
        toast.error('Failed to delete inventory item')
      }
    }
  }

  const handleDeleteSubComponent = async (id?: number) => {
    if (!id) return
    // Check if sub component has associated inventory items
    const associatedInventory = inventoryData.filter(item => item.subcomponentId === id)

    if (associatedInventory.length > 0) {
      toast.error(`Cannot delete this sub component because it has ${associatedInventory.length} associated inventory item(s). Please delete the inventory items first.`)
      return
    }

    if (!window.confirm('Are you sure you want to delete this sub component?')) return

    try {
      await deleteSubcomponent(id)
      await loadSubComponentData()
      toast.success('Sub component deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting sub component:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete sub component'

      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this sub component because it has associated inventory items. Please delete the inventory items first.')
      } else {
        toast.error('Failed to delete sub component')
      }
    }
  }

  const handleDeleteEquipment = async (id?: number) => {
    if (!id) return

    const associatedSubComponents = subComponentData.filter(
      sc => (sc.equipmentId ?? (sc as any)?.equipment?.id) === id
    )

    if (associatedSubComponents.length > 0) {
      toast.error(`Cannot delete this equipment because it has ${associatedSubComponents.length} associated sub component(s). Please delete or reassign the sub components first.`)
      return
    }

    const associatedParts = partData.filter(pt => (pt as any)?.equipmentId === id)
    if (associatedParts.length > 0) {
      toast.error(`Cannot delete this equipment because it has ${associatedParts.length} associated part(s). Please delete or reassign the parts first.`)
      return
    }

    if (!window.confirm('Are you sure you want to delete this equipment?')) return

    try {
      await deleteMachineryEquipment(id)
      await loadEquipmentData()
      toast.success('Equipment deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting equipment:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete equipment'

      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this equipment because it has associated records. Please delete or reassign the records first.')
      } else {
        toast.error('Failed to delete equipment')
      }
    }
  }

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return

    try {
      await deleteInventoryCategory(id)
      await loadCategoryData()
      toast.success('Category deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting category:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete category'

      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this category because it has associated items. Please delete or reassign the items first.')
      } else {
        toast.error('Failed to delete category')
      }
    }
  }

  const handleDeleteAccountingCode = async (id: number) => {
    // Check if accounting code has associated sub accounts
    const associatedSubAccounts = subAccountingData.filter(subAccount =>
      (subAccount as any).accountingAccountId === id || subAccount.accountCode === accountingCodeData.find(ac => ac.id === id)?.accountCodes
    )

    if (associatedSubAccounts.length > 0) {
      toast.error(`Cannot delete this accounting code because it has ${associatedSubAccounts.length} associated sub account(s). Please delete or reassign the sub accounts first.`)
      return
    }

    if (!window.confirm('Are you sure you want to delete this accounting code?')) return

    try {
      await deleteAccountingAccount(id)
      await loadAccountingCodeData()
      toast.success('Accounting code deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting accounting code:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete accounting code'

      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this accounting code because it has associated sub accounts. Please delete or reassign the sub accounts first.')
      } else {
        toast.error('Failed to delete accounting code')
      }
    }
  }

  const handleDeleteSubAccount = async (id: number) => {
    // Check if sub account has associated sub catalogues
    const associatedSubCatalogues = subCatalogueData.filter(subCat =>
      (subCat as any).subaccountId === id
    )

    if (associatedSubCatalogues.length > 0) {
      toast.error(`Cannot delete this sub accounting code because it has ${associatedSubCatalogues.length} associated sub catalogue(s). Please delete or reassign the sub catalogues first.`)
      return
    }

    if (!window.confirm('Are you sure you want to delete this sub accounting code?')) return

    try {
      await deleteSubAccount(id)
      await loadSubAccountingData()
      toast.success('Sub accounting code deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting sub accounting code:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete sub accounting code'

      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this sub accounting code because it has associated sub catalogues. Please delete or reassign the sub catalogues first.')
      } else {
        toast.error('Failed to delete sub accounting code')
      }
    }
  }

  const handleDeleteSubCatalogue = async (id: number) => {
    // Check if sub catalogue has associated parts
    const associatedParts = partData.filter(part => (part as any).subcatalogue?.id === id)

    if (associatedParts.length > 0) {
      toast.error(`Cannot delete this sub catalogue because it has ${associatedParts.length} associated part(s). Please delete or reassign the parts first.`)
      return
    }

    if (!window.confirm('Are you sure you want to delete this sub catalogue?')) return

    try {
      await deleteSubCatalogue(id)
      await loadSubCatalogueData()
      toast.success('Sub catalogue deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting sub catalogue:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete sub catalogue'

      // Check if it's a foreign key constraint error
      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this sub catalogue because it has associated parts. Please delete or reassign the parts first.')
      } else {
        toast.error('Failed to delete sub catalogue')
      }
    }
  }

  const handleDeletePart = async (id?: number) => {
    if (!id) return
    // Check if part has associated inventory items
    const associatedInventory = inventoryData.filter(item => item.partId === id)

    if (associatedInventory.length > 0) {
      toast.error(`Cannot delete this part because it has ${associatedInventory.length} associated inventory item(s). Please delete the inventory items first.`)
      return
    }

    // Check if part has associated sub components
    const associatedSubComponents = subComponentData.filter(subComp => subComp.part?.id === id)

    if (associatedSubComponents.length > 0) {
      toast.error(`Cannot delete this part because it has ${associatedSubComponents.length} associated sub component(s). Please delete the sub components first.`)
      return
    }

    if (!window.confirm('Are you sure you want to delete this part?')) return

    try {
      await deleteMachineryPart(id)
      await loadPartData()
      toast.success('Part deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting part:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete part'

      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this part because it has associated items. Please delete or reassign the items first.')
      } else {
        toast.error('Failed to delete part')
      }
    }
  }

  const handleViewPart = async (id?: number) => {
    if (!id) return

    try {
      setMappedManualsLoading(true)
      const cachedPart = partData.find(p => p.id === id)
      const part = cachedPart ?? await getPartById(id)

      if (!part) {
        toast.error('Part not found')
        setMappedManualsForPart([])
        return
      }

      const manuals = await listMappedManualPlansForPart(id)
      setMappedManualsForPart(manuals)
      setViewDetail({ open: true, kind: 'part', data: part })
    } catch (error: any) {
      console.error('Error fetching part details:', error)
      setMappedManualsForPart([])
      toast.error('Failed to load part details')
    } finally {
      setMappedManualsLoading(false)
    }
  }

  // Calculate total items based on active tab
  const totalItems = useMemo(() => {
    if (activeTab === 'catalogue') {
      return inventoryData.filter(item => item.status === 'Active').length
    } else if (activeTab === 'addItem') {
      switch (addItemSubTab) {
        case 'category':
          return categoryData.length
        case 'head':
          return headData.length
        case 'subHead':
          return subHeadData.length
        case 'generalItem':
          return generalItemData.length
        case 'equipment':
          return equipmentData.length
        case 'accountingCode':
          return accountingCodeData.length
        case 'subAccounting':
          return subAccountingData.length
        case 'subCatalogue':
          return subCatalogueData.length
        case 'part':
          return partData.length
        case 'subComponent':
          return subComponentData.length
        default:
          return 0
      }
    }
    return 0
  }, [activeTab, addItemSubTab, inventoryData, categoryData, headData, subHeadData, generalItemData, equipmentData, accountingCodeData, subAccountingData, subCatalogueData, partData, subComponentData])

  const lowStockItems = inventoryData.filter(item => item.stockStatus === 'Low Stock').length
  const criticalItems = inventoryData.filter(item => item.stockStatus === 'Critical').length
  const totalValue = inventoryData.reduce((sum, item) => sum + (item.unitCost * item.currentQty), 0) / 1000

  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const filteredInventoryData = useMemo(() => {
    console.log('inventoryData', inventoryData);
    return inventoryData.filter(record => {
      const matchesSearch = searchTerm === '' ||
        record.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.partNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.location.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesCategory = filters.category === '' || record.category === filters.category
      const matchesStatus = filters.status === '' || record.status === filters.status

      return matchesSearch && matchesCategory && matchesStatus
    })
  }, [searchTerm, inventoryData, filters])

  const sortedData = useMemo(() => {
    let sortedRecords = [...filteredInventoryData]

    if (sortConfig.key !== null) {
      sortedRecords.sort((a: any, b: any) => {
        const aVal = String(a[sortConfig.key!] || '').toLowerCase()
        const bVal = String(b[sortConfig.key!] || '').toLowerCase()

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [filteredInventoryData, sortConfig])

  // Delay showing "No inventory..." so users first see skeleton instead of flicker
  useEffect(() => {
    if (bootLoading || listLoading || isLoading) {
      setNoDataDelayPassed(false);
      return;
    }
    const t = setTimeout(() => setNoDataDelayPassed(true), 600); // 0.6s feels snappy-but-smooth
    return () => clearTimeout(t);
  }, [bootLoading, listLoading, isLoading, sortedData.length]);


  // Sorted data for each tab
  const sortedCategoryData = useMemo(() => {
    if (!categoryCompanyFilter) {
      // Reverse the data to show newest first
      let sortedRecords = [...categoryData].reverse()
      if (categorySortConfig.key !== null) {
        sortedRecords.sort((a: any, b: any) => {
          let aVal = ''
          let bVal = ''

          switch (categorySortConfig.key) {
            case 'id':
              aVal = String(a.id || '')
              bVal = String(b.id || '')
              break
            case 'name':
              aVal = String(a.name || '').toLowerCase()
              bVal = String(b.name || '').toLowerCase()
              break
            case 'company':
              aVal = String(a.cgaid?.name || '').toLowerCase()
              bVal = String(b.cgaid?.name || '').toLowerCase()
              break
          case 'scope':
            aVal = 'company-wide'
            bVal = 'company-wide'
            break
            default:
              aVal = String(a[categorySortConfig.key!] || '').toLowerCase()
              bVal = String(b[categorySortConfig.key!] || '').toLowerCase()
          }

          if (aVal < bVal) return categorySortConfig.direction === 'asc' ? -1 : 1
          if (aVal > bVal) return categorySortConfig.direction === 'asc' ? 1 : -1
          return 0
        })
      }
      return sortedRecords
    }

    let filteredRecords = categoryData.filter(category =>
      category.cgaid?.id === parseInt(categoryCompanyFilter)
    )

    // Reverse the filtered data to show newest first
    filteredRecords = filteredRecords.reverse()

    if (categorySortConfig.key !== null) {
      filteredRecords.sort((a: any, b: any) => {
        let aVal = ''
        let bVal = ''

        switch (categorySortConfig.key) {
          case 'id':
            aVal = String(a.id || '')
            bVal = String(b.id || '')
            break
          case 'name':
            aVal = String(a.name || '').toLowerCase()
            bVal = String(b.name || '').toLowerCase()
            break
          case 'company':
            aVal = String(a.cgaid?.name || '').toLowerCase()
            bVal = String(b.cgaid?.name || '').toLowerCase()
            break
          case 'scope':
            aVal = 'company-wide'
            bVal = 'company-wide'
            break
          default:
            aVal = String(a[categorySortConfig.key!] || '').toLowerCase()
            bVal = String(b[categorySortConfig.key!] || '').toLowerCase()
        }

        if (aVal < bVal) return categorySortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return categorySortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return filteredRecords
  }, [categoryData, categoryCompanyFilter, categorySortConfig])

  const sortedHeadData = useMemo(() => {
    let sortedRecords = [...headData].reverse()
    if (headSortConfig.key !== null) {
      sortedRecords.sort((a: any, b: any) => {
        let aVal = ''
        let bVal = ''

        switch (headSortConfig.key) {
          case 'id':
            aVal = String(a.id || '')
            bVal = String(b.id || '')
            break
          case 'name':
            aVal = String(a.name || '').toLowerCase()
            bVal = String(b.name || '').toLowerCase()
            break
          case 'inventoryItemCategoryName':
            aVal = String(a.inventoryItemCategoryName || '').toLowerCase()
            bVal = String(b.inventoryItemCategoryName || '').toLowerCase()
            break
          case 'company':
            aVal = String(a.cgaid?.name || '').toLowerCase()
            bVal = String(b.cgaid?.name || '').toLowerCase()
            break
          case 'vesselType':
            aVal = String(a.vesselName || (typeof a.vesselType === 'string' ? a.vesselType : a.vesselType?.fleet_name || '')).toLowerCase()
            bVal = String(b.vesselName || (typeof b.vesselType === 'string' ? b.vesselType : b.vesselType?.fleet_name || '')).toLowerCase()
            break
          default:
            aVal = String(a[headSortConfig.key!] || '').toLowerCase()
            bVal = String(b[headSortConfig.key!] || '').toLowerCase()
        }

        if (aVal < bVal) return headSortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return headSortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [headData, headSortConfig])

  const sortedSubHeadData = useMemo(() => {
    let sortedRecords = [...subHeadData].reverse()
    if (subHeadSortConfig.key !== null) {
      sortedRecords.sort((a: any, b: any) => {
        let aVal = ''
        let bVal = ''

        switch (subHeadSortConfig.key) {
          case 'id':
            aVal = String(a.id || '')
            bVal = String(b.id || '')
            break
          case 'name':
            aVal = String(a.name || '').toLowerCase()
            bVal = String(b.name || '').toLowerCase()
            break
          case 'inventoryItemHeadName':
            aVal = String(a.inventoryItemHeadName || '').toLowerCase()
            bVal = String(b.inventoryItemHeadName || '').toLowerCase()
            break
          case 'company':
            aVal = String(a.cgaid?.name || '').toLowerCase()
            bVal = String(b.cgaid?.name || '').toLowerCase()
            break
          case 'vesselType':
            aVal = String(a.vesselName || (typeof a.vesselType === 'string' ? a.vesselType : a.vesselType?.fleet_name || '')).toLowerCase()
            bVal = String(b.vesselName || (typeof b.vesselType === 'string' ? b.vesselType : b.vesselType?.fleet_name || '')).toLowerCase()
            break
          default:
            aVal = String(a[subHeadSortConfig.key!] || '').toLowerCase()
            bVal = String(b[subHeadSortConfig.key!] || '').toLowerCase()
        }

        if (aVal < bVal) return subHeadSortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return subHeadSortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [subHeadData, subHeadSortConfig])

  const generalItemCompanyOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        (generalItemData || [])
          .map(
            (item) =>
              item?.vessel?.companyGroupAdmin?.name ||
              item?.vessel?.companyAdmin?.name ||
              ''
          )
          .filter(Boolean)
      )
    )
    return values.sort((a, b) => a.localeCompare(b))
  }, [generalItemData])

  const generalItemVesselOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        (generalItemData || [])
          .map((item) => item?.vessel?.fleet_name || '')
          .filter(Boolean)
      )
    )
    return values.sort((a, b) => a.localeCompare(b))
  }, [generalItemData])

  const generalItemLocationOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        (generalItemData || [])
          .map((item) => item?.location || '')
          .filter(Boolean)
      )
    )
    return values.sort((a, b) => a.localeCompare(b))
  }, [generalItemData])

  const filteredGeneralItemData = useMemo(() => {
    return (generalItemData || []).filter((item) => {
      const companyName =
        item?.vessel?.companyGroupAdmin?.name ||
        item?.vessel?.companyAdmin?.name ||
        ''
      const vesselName = item?.vessel?.fleet_name || ''
      const locationName = item?.location || ''

      const companyMatch =
        generalItemCompanyFilter === 'ALL' || companyName === generalItemCompanyFilter

      const vesselMatch =
        generalItemVesselFilter === 'ALL' || vesselName === generalItemVesselFilter

      const locationMatch =
        generalItemLocationFilter === 'ALL' || locationName === generalItemLocationFilter

      return companyMatch && vesselMatch && locationMatch
    })
  }, [
    generalItemData,
    generalItemCompanyFilter,
    generalItemVesselFilter,
    generalItemLocationFilter,
  ])

  const sortedGeneralItemData = useMemo(() => {
    const sortableItems = [...filteredGeneralItemData]

    if (!generalItemSortConfig.key) {
      return sortableItems
    }

    const sortKey = generalItemSortConfig.key as string

    if (sortKey) {
      sortableItems.sort((a, b) => {
        const getValue = (item: any, key: string) => {
          switch (key) {
            case 'manualItemName':
              return item?.manualItemName || ''
            case 'unitOfMeasurement':
              return item?.unitOfMeasurement || ''
            case 'currentQty':
              return Number(item?.currentQty ?? 0)
            case 'location':
              return item?.location || ''
            case 'vesselName':
              return item?.vessel?.fleet_name || ''
            case 'company':
              return (
                item?.vessel?.companyGroupAdmin?.name ||
                item?.vessel?.companyAdmin?.name ||
                ''
              )
            default:
              return ''
          }
        }

        const aValue = getValue(a, sortKey)
        const bValue = getValue(b, sortKey)

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return generalItemSortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue
        }

        return generalItemSortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue))
      })
    }

    return sortableItems
  }, [filteredGeneralItemData, generalItemSortConfig])

  const sortedAccountingCodeData = useMemo(() => {
    // Filter by company if filter is set
    let filteredRecords = accountingCodeCompanyFilter
      ? accountingCodeData.filter(code => {
        const cgaId = typeof code.cgaid === 'object' ? (code.cgaid as any)?.id : code.cgaid
        return cgaId === parseInt(accountingCodeCompanyFilter)
      })
      : [...accountingCodeData]

    // Reverse the data to show newest first
    let sortedRecords = filteredRecords.reverse()
    if (accountingCodeSortConfig.key !== null) {
      sortedRecords.sort((a: any, b: any) => {
        let aVal = ''
        let bVal = ''

        switch (accountingCodeSortConfig.key) {
          case 'id':
            aVal = String(a.id || '')
            bVal = String(b.id || '')
            break
          case 'accountCodes':
            aVal = String(a.accountCodes || '').toLowerCase()
            bVal = String(b.accountCodes || '').toLowerCase()
            break
          case 'company':
            aVal = String(a.cgaid?.name || '').toLowerCase()
            bVal = String(b.cgaid?.name || '').toLowerCase()
            break
          case 'vesselType':
            aVal = String(a.vesselName || a.vesselType || '').toLowerCase()
            bVal = String(b.vesselName || b.vesselType || '').toLowerCase()
            break
          default:
            aVal = String(a[accountingCodeSortConfig.key!] || '').toLowerCase()
            bVal = String(b[accountingCodeSortConfig.key!] || '').toLowerCase()
        }

        if (aVal < bVal) return accountingCodeSortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return accountingCodeSortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [accountingCodeData, accountingCodeCompanyFilter, accountingCodeSortConfig])

  const sortedSubAccountingData = useMemo(() => {
    // Filter by company if filter is set
    let filteredRecords = subAccountingCompanyFilter
      ? subAccountingData.filter(subAccount => {
        const cgaId = typeof subAccount.cgaid === 'object' ? (subAccount.cgaid as any)?.id : subAccount.cgaid
        return cgaId === parseInt(subAccountingCompanyFilter)
      })
      : [...subAccountingData]

    // Reverse the data to show newest first
    let sortedRecords = filteredRecords.reverse()
    if (subAccountingSortConfig.key !== null) {
      sortedRecords.sort((a: any, b: any) => {
        let aVal = ''
        let bVal = ''

        switch (subAccountingSortConfig.key) {
          case 'id':
            aVal = String(a.id || '')
            bVal = String(b.id || '')
            break
          case 'subaccountCodes':
            aVal = String(a.subaccountCodes || '').toLowerCase()
            bVal = String(b.subaccountCodes || '').toLowerCase()
            break
          case 'accountCode':
            aVal = String(a.accountCode || '').toLowerCase()
            bVal = String(b.accountCode || '').toLowerCase()
            break
          case 'company':
            aVal = String(a.cgaid?.name || '').toLowerCase()
            bVal = String(b.cgaid?.name || '').toLowerCase()
            break
          default:
            aVal = String(a[subAccountingSortConfig.key!] || '').toLowerCase()
            bVal = String(b[subAccountingSortConfig.key!] || '').toLowerCase()
        }

        if (aVal < bVal) return subAccountingSortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return subAccountingSortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [subAccountingData, subAccountingCompanyFilter, subAccountingSortConfig])

  const sortedSubCatalogueData = useMemo(() => {
    // Filter by company if filter is set
    let filteredRecords = subCatalogueCompanyFilter
      ? subCatalogueData.filter(subCat => {
        const cgaId = typeof subCat.cgaid === 'object' ? (subCat.cgaid as any)?.id : subCat.cgaid
        return cgaId === parseInt(subCatalogueCompanyFilter)
      })
      : [...subCatalogueData]

    // Reverse the data to show newest first
    let sortedRecords = filteredRecords.reverse()
    if (subCatalogueSortConfig.key !== null) {
      sortedRecords.sort((a: any, b: any) => {
        let aVal = ''
        let bVal = ''

        switch (subCatalogueSortConfig.key) {
          case 'id':
            aVal = String(a.id || '')
            bVal = String(b.id || '')
            break
          case 'name':
            aVal = String(a.name || '').toLowerCase()
            bVal = String(b.name || '').toLowerCase()
            break
          case 'company':
            aVal = String(a.subCatalogueType?.name || '').toLowerCase()
            bVal = String(b.subCatalogueType?.name || '').toLowerCase()
            break
          case 'vesselType':
            aVal = String((a as any).vesselName || a.vesselType || '').toLowerCase()
            bVal = String((b as any).vesselName || b.vesselType || '').toLowerCase()
            break
          default:
            aVal = String(a[subCatalogueSortConfig.key!] || '').toLowerCase()
            bVal = String(b[subCatalogueSortConfig.key!] || '').toLowerCase()
        }

        if (aVal < bVal) return subCatalogueSortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return subCatalogueSortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [subCatalogueData, subCatalogueCompanyFilter, subCatalogueSortConfig])

  const sortedPartData = useMemo(() => {
    // Filter by company if filter is set
    let filteredRecords = partCompanyFilter
      ? partData.filter(part => {
        const cgaId = (part as any)?.vessel?.companyGroupAdmin?.id ?? (part as any)?.vessel?.companyGroupAdminId
        return cgaId === parseInt(partCompanyFilter)
      })
      : [...partData]

    // Reverse the data to show newest first
    let sortedRecords = filteredRecords.reverse()
    if (partSortConfig.key !== null) {
      sortedRecords.sort((a: any, b: any) => {
        let aVal = ''
        let bVal = ''

        switch (partSortConfig.key) {
          case 'id':
            aVal = String(a.id || '')
            bVal = String(b.id || '')
            break
          case 'name':
            aVal = String(a.name || '').toLowerCase()
            bVal = String(b.name || '').toLowerCase()
            break
          case 'code':
            aVal = String(a.code || '').toLowerCase()
            bVal = String(b.code || '').toLowerCase()
            break
          case 'manufacturer':
            aVal = String(a.manufacturer || '').toLowerCase()
            bVal = String(b.manufacturer || '').toLowerCase()
            break
          case 'partNo':
            aVal = String(a.partNo || '').toLowerCase()
            bVal = String(b.partNo || '').toLowerCase()
            break
          case 'serialNumber':
            aVal = String(a.serialNumber || '').toLowerCase()
            bVal = String(b.serialNumber || '').toLowerCase()
            break
          case 'itemCode':
            aVal = String(a.itemCode || '').toLowerCase()
            bVal = String(b.itemCode || '').toLowerCase()
            break
          default:
            aVal = String(a[partSortConfig.key!] || '').toLowerCase()
            bVal = String(b[partSortConfig.key!] || '').toLowerCase()
        }

        if (aVal < bVal) return partSortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return partSortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [partData, partCompanyFilter, partSortConfig])

  const sortedSubComponentData = useMemo(() => {
    // Filter by company if filter is set
    let filteredRecords = subComponentCompanyFilter
      ? subComponentData.filter(subComp => {
        const cgaId = typeof subComp.companyId === 'number' ? subComp.companyId : (subComp as any)?.companyId
        return cgaId === parseInt(subComponentCompanyFilter)
      })
      : [...subComponentData]

    let sortedRecords = filteredRecords
    if (subComponentSortConfig.key !== null) {
      sortedRecords.sort((a: any, b: any) => {
        let aVal = ''
        let bVal = ''

        switch (subComponentSortConfig.key) {
          case 'name':
            aVal = String(a.name || '').toLowerCase()
            bVal = String(b.name || '').toLowerCase()
            break
          case 'subComponentCode':
            aVal = String((a as any).subComponentCode || '').toLowerCase()
            bVal = String((b as any).subComponentCode || '').toLowerCase()
            break
          case 'equipmentName':
            aVal = String((a as any).equipmentName || '').toLowerCase()
            bVal = String((b as any).equipmentName || '').toLowerCase()
            break
          case 'equipmentCode':
            aVal = String((a as any).equipmentCode || '').toLowerCase()
            bVal = String((b as any).equipmentCode || '').toLowerCase()
            break
          case 'criticality':
            aVal = String((a as any).criticality || '')
            bVal = String((b as any).criticality || '')
            break
          case 'instanceCount':
            aVal = String((a as any).instanceCount || 0)
            bVal = String((b as any).instanceCount || 0)
            break
          default:
            aVal = String(a[subComponentSortConfig.key!] || '').toLowerCase()
            bVal = String(b[subComponentSortConfig.key!] || '').toLowerCase()
        }

        if (aVal < bVal) return subComponentSortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return subComponentSortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [subComponentData, subComponentCompanyFilter, subComponentSortConfig])

  const handleSort = (key: any) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  // Sort handlers for each tab
  const handleCategorySort = (key: string) => {
    setCategorySortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleAccountingCodeSort = (key: string) => {
    setAccountingCodeSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleSubAccountingSort = (key: string) => {
    setSubAccountingSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleSubCatalogueSort = (key: string) => {
    setSubCatalogueSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handlePartSort = (key: string) => {
    setPartSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleSubComponentSort = (key: string) => {
    setSubComponentSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.ceil(sortedData.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const handleAddItemSuccess = () => {
    loadInitialData().then(() => loadInventory())
  }

  const loadCompanyOptions = async () => {
    if (companyOptions.length > 0) return
    try {
      const data = await getCompanyGroupAdmins()
      setCompanyOptions(data || [])
    } catch (error) {
      console.error('Error loading companies', error)
      setCompanyOptions([])
    }
  }

  const loadVesselsForCompany = async (companyId: string, setter: (v: Vessel[]) => void) => {
    const company = companyOptions.find(c => String(c.id) === String(companyId))
    if (!company?.username) {
      setter([])
      return
    }
    try {
      const data = await getVesselsByCompany(company.username)
      setter(data || [])
    } catch (error) {
      console.error('Error loading vessels by company', error)
      setter([])
    }
  }

  const loadUnitOptions = async () => {
    try {
      const data = await getUnitsOfMeasure()
      setUnitOptions(data || [])
    } catch (error) {
      console.error('Error loading units of measure', error)
      setUnitOptions([])
    }
  }

  const loadStorageLocations = async () => {
    try {
      const data = await getPartStorageLocations()
      setStorageLocations(data || [])
    } catch (error) {
      console.error('Error loading storage locations', error)
      setStorageLocations([])
    }
  }

  // Category pagination logic
  // Category pagination logic

  const categoryIndexOfLastRecord = categoryCurrentPage * categoryRowsPerPage
  const categoryIndexOfFirstRecord = categoryIndexOfLastRecord - categoryRowsPerPage
  const categoryCurrentRecords = sortedCategoryData.slice(categoryIndexOfFirstRecord, categoryIndexOfLastRecord)
  const categoryTotalPages = Math.ceil(sortedCategoryData.length / categoryRowsPerPage)

  const handleCategoryPageChange = (page: number) => {
    if (page > 0 && page <= categoryTotalPages) {
      setCategoryCurrentPage(page)
    }
  }

  const handleCategoryRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategoryRowsPerPage(parseInt(e.target.value))
    setCategoryCurrentPage(1)
  }

  // Head pagination logic
  const headIndexOfLastRecord = headCurrentPage * headRowsPerPage
  const headIndexOfFirstRecord = headIndexOfLastRecord - headRowsPerPage
  const headCurrentRecords = sortedHeadData.slice(headIndexOfFirstRecord, headIndexOfLastRecord)
  const headTotalPages = Math.ceil(sortedHeadData.length / headRowsPerPage)

  const handleHeadPageChange = (page: number) => {
    if (page > 0 && page <= headTotalPages) {
      setHeadCurrentPage(page)
    }
  }

  const handleHeadRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHeadRowsPerPage(parseInt(e.target.value))
    setHeadCurrentPage(1)
  }

  const handleHeadSort = (key: string) => {
    setHeadSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  // Sub Head pagination logic
  const subHeadIndexOfLastRecord = subHeadCurrentPage * subHeadRowsPerPage
  const subHeadIndexOfFirstRecord = subHeadIndexOfLastRecord - subHeadRowsPerPage
  const subHeadCurrentRecords = sortedSubHeadData.slice(subHeadIndexOfFirstRecord, subHeadIndexOfLastRecord)
  const subHeadTotalPages = Math.ceil(sortedSubHeadData.length / subHeadRowsPerPage)

  const handleSubHeadPageChange = (page: number) => {
    if (page > 0 && page <= subHeadTotalPages) {
      setSubHeadCurrentPage(page)
    }
  }

  const handleSubHeadRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSubHeadRowsPerPage(parseInt(e.target.value))
    setSubHeadCurrentPage(1)
  }

  const handleSubHeadSort = (key: string) => {
    setSubHeadSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  // General Items pagination logic
  const generalItemIndexOfLastRecord = generalItemCurrentPage * generalItemRowsPerPage
  const generalItemIndexOfFirstRecord = generalItemIndexOfLastRecord - generalItemRowsPerPage
  const generalItemCurrentRecords = sortedGeneralItemData.slice(
    generalItemIndexOfFirstRecord,
    generalItemIndexOfLastRecord
  )
  const generalItemTotalPages = Math.ceil(sortedGeneralItemData.length / generalItemRowsPerPage)

  const handleGeneralItemPageChange = (page: number) => {
    if (page > 0 && page <= generalItemTotalPages) {
      setGeneralItemCurrentPage(page)
    }
  }

  const handleGeneralItemRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGeneralItemRowsPerPage(parseInt(e.target.value))
    setGeneralItemCurrentPage(1)
  }

  const handleGeneralItemSort = (key: string) => {
    setGeneralItemSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  useEffect(() => {
    setGeneralItemCurrentPage(1)
  }, [
    generalItemCompanyFilter,
    generalItemVesselFilter,
    generalItemLocationFilter,
  ])

  // Accounting Code pagination logic
  const accountingCodeIndexOfLastRecord = accountingCodeCurrentPage * accountingCodeRowsPerPage
  const accountingCodeIndexOfFirstRecord = accountingCodeIndexOfLastRecord - accountingCodeRowsPerPage
  const accountingCodeCurrentRecords = sortedAccountingCodeData.slice(accountingCodeIndexOfFirstRecord, accountingCodeIndexOfLastRecord)
  const accountingCodeTotalPages = Math.ceil(sortedAccountingCodeData.length / accountingCodeRowsPerPage)

  const handleAccountingCodePageChange = (page: number) => {
    if (page > 0 && page <= accountingCodeTotalPages) {
      setAccountingCodeCurrentPage(page)
    }
  }

  const handleAccountingCodeRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAccountingCodeRowsPerPage(parseInt(e.target.value))
    setAccountingCodeCurrentPage(1)
  }

  // Sub Accounting pagination logic
  const subAccountingIndexOfLastRecord = subAccountingCurrentPage * subAccountingRowsPerPage
  const subAccountingIndexOfFirstRecord = subAccountingIndexOfLastRecord - subAccountingRowsPerPage
  const subAccountingCurrentRecords = sortedSubAccountingData.slice(subAccountingIndexOfFirstRecord, subAccountingIndexOfLastRecord)
  const subAccountingTotalPages = Math.ceil(sortedSubAccountingData.length / subAccountingRowsPerPage)

  const handleSubAccountingPageChange = (page: number) => {
    if (page > 0 && page <= subAccountingTotalPages) {
      setSubAccountingCurrentPage(page)
    }
  }

  const handleSubAccountingRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSubAccountingRowsPerPage(parseInt(e.target.value))
    setSubAccountingCurrentPage(1)
  }

  // Sub Catalogue pagination logic
  const subCatalogueIndexOfLastRecord = subCatalogueCurrentPage * subCatalogueRowsPerPage
  const subCatalogueIndexOfFirstRecord = subCatalogueIndexOfLastRecord - subCatalogueRowsPerPage
  const subCatalogueCurrentRecords = sortedSubCatalogueData.slice(subCatalogueIndexOfFirstRecord, subCatalogueIndexOfLastRecord)
  const subCatalogueTotalPages = Math.ceil(sortedSubCatalogueData.length / subCatalogueRowsPerPage)

  const handleSubCataloguePageChange = (page: number) => {
    if (page > 0 && page <= subCatalogueTotalPages) {
      setSubCatalogueCurrentPage(page)
    }
  }

  const handleSubCatalogueRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSubCatalogueRowsPerPage(parseInt(e.target.value))
    setSubCatalogueCurrentPage(1)
  }

  // Part pagination logic
  const partIndexOfLastRecord = partCurrentPage * partRowsPerPage
  const partIndexOfFirstRecord = partIndexOfLastRecord - partRowsPerPage
  const partCurrentRecords = sortedPartData.slice(partIndexOfFirstRecord, partIndexOfLastRecord)
  const partTotalPages = Math.ceil(sortedPartData.length / partRowsPerPage)

  const handlePartPageChange = (page: number) => {
    if (page > 0 && page <= partTotalPages) {
      setPartCurrentPage(page)
    }
  }

  const handlePartRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPartRowsPerPage(parseInt(e.target.value))
    setPartCurrentPage(1)
  }

  // Sub Component pagination
  const subComponentIndexOfLastRecord = subComponentCurrentPage * subComponentRowsPerPage
  const subComponentIndexOfFirstRecord = subComponentIndexOfLastRecord - subComponentRowsPerPage
  const subComponentCurrentRecords = sortedSubComponentData.slice(subComponentIndexOfFirstRecord, subComponentIndexOfLastRecord)
  const subComponentTotalPages = Math.ceil(sortedSubComponentData.length / subComponentRowsPerPage)

  const toggleEquipment = (id: number) => {
    setExpandedEquipments(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSubcomponentNode = (id: number) => {
    setExpandedSubcomponents(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const renderHierarchy = () => {
    const equipmentByVessel = new Map<number, EquipmentDto[]>()
    equipmentData.forEach(eq => {
      if (!eq.vesselId) return
      if (!equipmentByVessel.has(eq.vesselId)) equipmentByVessel.set(eq.vesselId, [])
      equipmentByVessel.get(eq.vesselId)!.push(eq)
    })

    if (equipmentByVessel.size === 0) {
      return (
        <div className='text-center py-6 text-muted border rounded-3 bg-light'>
          No equipment found. Use filters or add equipment to view hierarchy.
        </div>
      )
    }

    const subByEq = new Map<number, SubcomponentDto[]>()
    subComponentData.forEach(sc => {
      if (!sc.equipmentId) return
      if (!subByEq.has(sc.equipmentId)) subByEq.set(sc.equipmentId, [])
      subByEq.get(sc.equipmentId)!.push(sc)
    })

    const partsBySub = new Map<number, PartDto[]>()
    partData.forEach(pt => {
      const sid = pt.subcomponent?.id ?? pt.subcomponentId
      if (!sid) return
      if (!partsBySub.has(sid)) partsBySub.set(sid, [])
      partsBySub.get(sid)!.push(pt)
    })

    return (
      <div className='d-flex flex-column gap-3'>
        {Array.from(equipmentByVessel.entries()).map(([vesselId, eqList]) => {
          const vesselName = eqList[0]?.vesselName || `Vessel #${vesselId}`
          return (
            <div key={vesselId} className='border rounded-3 p-3 bg-white shadow-sm'>
              <div className='d-flex align-items-center justify-content-between mb-2'>
                <div>
                  <div className='fw-bold text-dark'>{vesselName}</div>
                  <div className='text-muted fs-8'>{eqList.length} equipment</div>
                </div>
              </div>
              <div className='d-flex flex-column gap-2'>
                {eqList.map(eq => {
                  const eqId = eq.id!
                  const isEqOpen = expandedEquipments.has(eqId)
                  const scList = subByEq.get(eqId) || []
                  return (
                    <div key={eqId} className='border rounded-3 p-2 bg-light'>
                      <div className='d-flex align-items-center justify-content-between'>
                        <div className='d-flex align-items-center gap-2'>
                          <button className='btn btn-icon btn-sm btn-light-primary' onClick={() => toggleEquipment(eqId)}>
                            <KTSVG path={`/media/icons/duotune/arrows/arr0${isEqOpen ? '72' : '71'}.svg`} className='svg-icon-2' />
                          </button>
                          <div>
                            <div className='fw-bold text-dark'>{eq.name || '-'}</div>
                            <div className='text-muted fs-8'>
                              {eq.code || '-'} ? {formatYesNo(eq.criticality as any)} ? {valueOrDash(eq.maker)}
                            </div>
                          </div>
                        </div>
                        <div className='d-flex gap-1'>
                          <button className='btn btn-sm btn-light' onClick={() => setViewDetail({ open: true, kind: 'equipment', data: eq })}>View</button>
                          <button className='btn btn-sm btn-light-primary' onClick={() => handleEditEquipment(eq)}>Edit</button>
                          <button className='btn btn-sm btn-light-danger' onClick={() => handleDeleteEquipment(eqId)}>Delete</button>
                          <button className='btn btn-sm btn-primary' onClick={() => { setSubComponentParentEquipmentId(eqId); openCreateSubComponentModal(); }}>Add Subcomponent</button>
                        </div>
                      </div>
                      {isEqOpen && (
                        <div className='ms-9 mt-2 d-flex flex-column gap-2'>
                          {scList.length === 0 ? (
                            <div className='text-muted fs-8'>No sub components.</div>
                          ) : (
                            scList.map(sc => {
                              const scId = sc.id!
                              const isScOpen = expandedSubcomponents.has(scId)
                              const partList = partsBySub.get(scId) || []
                              return (
                                <div key={scId} className='border rounded-3 p-2 bg-white'>
                                  <div className='d-flex align-items-center justify-content-between'>
                                    <div className='d-flex align-items-center gap-2'>
                                      <button className='btn btn-icon btn-xs btn-light-primary' onClick={() => toggleSubcomponentNode(scId)}>
                                        <KTSVG path={`/media/icons/duotune/arrows/arr0${isScOpen ? '72' : '71'}.svg`} className='svg-icon-2' />
                                      </button>
                                      <div>
                                        <div className='fw-semibold text-dark'>{sc.name || '-'}</div>
                                        <div className='text-muted fs-8'>
                                          {sc.subComponentCode || '-'} ? {formatYesNo(sc.criticality as any)} ? Jobs: {formatYesNo(sc.hasOpenJob as any)} ? Instances: {valueOrDash(sc.instanceCount)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className='d-flex gap-1'>
                                      <button className='btn btn-sm btn-light' onClick={() => setViewDetail({ open: true, kind: 'subcomponent', data: sc })}>View</button>
                                      <button className='btn btn-sm btn-light-primary' onClick={() => handleEditSubComponent(sc as any)}>Edit</button>
                                      <button className='btn btn-sm btn-light-danger' onClick={() => handleDeleteSubComponent(scId)}>Delete</button>
                                      <button className='btn btn-sm btn-primary' onClick={() => { setPartParentSubComponentId(scId); setPartParentEquipmentId(eqId); openCreatePartModal(); }}>Add Part</button>
                                    </div>
                                  </div>
                                  {isScOpen && (
                                    <div className='ms-7 mt-2 d-flex flex-column gap-2'>
                                      {partList.length === 0 ? (
                                        <div className='text-muted fs-8'>No parts.</div>
                                      ) : (
                                        partList.map(pt => (
                                          <div key={pt.id} className='d-flex align-items-center justify-content-between border rounded-3 p-2 bg-light'>
                                            <div>
                                              <div className='fw-semibold text-dark'>{pt.name}</div>
                                              <div className='text-muted fs-8'>
                                                {pt.code || '-'} ? {valueOrDash(pt.manufacturer)} ? Spare: {formatYesNo(pt.isSpare as any)}
                                              </div>
                                            </div>
                                            <div className='d-flex gap-1'>
                                              <button className='btn btn-sm btn-light' onClick={() => handleViewPart(pt.id)}>View</button>
                                              <button className='btn btn-sm btn-light-primary' onClick={() => handleEditPart(pt)}>Edit</button>
                                              <button className='btn btn-sm btn-light-danger' onClick={() => handleDeletePart(pt.id)}>Delete</button>
                                            </div>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </div>
                              )
                            })
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const handleSubComponentPageChange = (page: number) => {
    if (page > 0 && page <= subComponentTotalPages) {
      setSubComponentCurrentPage(page)
    }
  }

  const handleSubComponentRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSubComponentRowsPerPage(parseInt(e.target.value))
    setSubComponentCurrentPage(1)
  }

  // Load companies for category filter
  const loadCompaniesForFilter = async () => {
    if (!isGlobalScopeUser) return

    setIsLoadingCompanies(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompaniesForFilter(companiesData)
    } catch (error) {
      console.error('Error loading companies for filter:', error)
    } finally {
      setIsLoadingCompanies(false)
    }
  }

  const loadVesselsForFilter = async (companyUsername?: string) => {
    if (!companyUsername) {
      setVesselsForFilter([])
      return
    }
    setIsLoadingVessels(true)
    try {
      const vessels = await getVesselsByCompany(companyUsername)
      setVesselsForFilter(vessels)
    } catch (error) {
      console.error('Error loading vessels for filter:', error)
      setVesselsForFilter([])
    } finally {
      setIsLoadingVessels(false)
    }
  }

  // Load data functions for each tab
  const loadCategoryData = async () => {
    setCategoryLoading(true);
    try {
      let data: InventoryCategory[] = []

      if (libraryScope === 'ALL') {
        if (categoryCompanyFilter) {
          data = await getAllInventoryCategoriesForCompany(parseInt(categoryCompanyFilter))
        } else {
          data = await getAllInventoryCategoriesForAllCompanies()
        }
      } else {
        const effectiveCgaId = tenantCgaId
        if (!effectiveCgaId) {
          setCategoryData([])
          return
        }
        data = await getAllInventoryCategoriesForCompany(effectiveCgaId)
      }

      console.log('loadCategoryData - Loaded', data.length, 'categories (scope:', libraryScope, ')')
      setCategoryData(data)
    } catch (error) {
      console.error('Error loading category data:', error)
      setCategoryData([])
    } finally {
      setCategoryLoading(false)
    }
  }

  useEffect(() => {
    if (!isGlobalScopeUser) return
    if (!categoryCompanyFilter) {
      setCategoryVesselFilter('')
      setVesselsForFilter([])
      return
    }
    const selectedCompany = companiesForFilter.find(c => c.id === parseInt(categoryCompanyFilter))
    loadVesselsForFilter(selectedCompany?.username)
  }, [categoryCompanyFilter, companiesForFilter, isGlobalScopeUser])

  const loadHeadData = async () => {
    setHeadLoading(true)
    try {
      const scopedVesselId =
        libraryScope === 'ALL'
          ? (categoryVesselFilter ? parseInt(categoryVesselFilter) : undefined)
          : libraryScope === 'COMPANY'
          ? undefined
          : (vesselId ?? tenantVesselId ?? undefined)
      const scopedCgaId =
        libraryScope === 'ALL' && !scopedVesselId && categoryCompanyFilter
          ? parseInt(categoryCompanyFilter)
          : undefined

      const data = await getInventoryItemHeads(scopedVesselId, scopedCgaId)
      console.log('loadHeadData - Loaded', data.length, 'heads')
      setHeadData(data)
    } catch (error) {
      console.error('Error loading head data:', error)
      setHeadData([])
    } finally {
      setHeadLoading(false)
    }
  }

  const loadSubHeadData = async () => {
    setSubHeadLoading(true)
    try {
      const scopedVesselId =
        libraryScope === 'ALL'
          ? (categoryVesselFilter ? parseInt(categoryVesselFilter) : undefined)
          : libraryScope === 'COMPANY'
          ? undefined
          : (vesselId ?? tenantVesselId ?? undefined)
      const scopedCgaId =
        libraryScope === 'ALL' && !scopedVesselId && categoryCompanyFilter
          ? parseInt(categoryCompanyFilter)
          : undefined

      const data = await getInventoryItemSubHeads(scopedVesselId, undefined, scopedCgaId)
      console.log('loadSubHeadData - Loaded', data.length, 'sub heads')
      setSubHeadData(data)
    } catch (error) {
      console.error('Error loading sub head data:', error)
      setSubHeadData([])
    } finally {
      setSubHeadLoading(false)
    }
  }

  const loadGeneralItemData = async () => {
    setGeneralItemLoading(true)
    try {
      let effectiveVesselId = vesselId
      if (libraryScope === 'ALL') {
        effectiveVesselId = null
      } else if (effectiveVesselId === null && tenantVesselId) {
        effectiveVesselId = tenantVesselId
        setVesselId(tenantVesselId)
      } else if (effectiveVesselId === null) {
        const companyUsername =
          (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
        if (companyUsername) {
          try {
            const vessels = await getVesselsByCompany(companyUsername)
            if (vessels.length > 0) {
              effectiveVesselId = vessels[0].id
              setVesselId(vessels[0].id)
            }
          } catch (error) {
            console.error('Error loading vessels for general items:', error)
          }
        }
      }
      const data = await getGeneralInventoryItems(effectiveVesselId ?? undefined)
      setGeneralItemData(data || [])
    } catch (error) {
      console.error('Error loading general item data:', error)
      setGeneralItemData([])
    } finally {
      setGeneralItemLoading(false)
    }
  }

  const loadAccountingCodeData = async (forceCgaId?: number, forceVesselId?: number) => {
    setAccountingLoading(true);
    try {
      if (libraryScope === 'ALL') {
        if (forceCgaId) {
          const scoped = await getAccountingAccountsWithParams(forceCgaId, forceVesselId)
          setAccountingCodeData(scoped)
        } else {
          const allAccountingCodes = await getAllAccountingAccounts()
          const filtered = forceVesselId
            ? allAccountingCodes.filter(code => code.vesselId === forceVesselId)
            : allAccountingCodes
          setAccountingCodeData(filtered)
        }
      } else if (libraryScope === 'COMPANY') {
        const effectiveCgaId = forceCgaId ?? tenantCgaId
        if (!effectiveCgaId) {
          setAccountingCodeData([])
          return
        }
        if (forceVesselId) {
          const scoped = await getAccountingAccountsWithParams(effectiveCgaId, forceVesselId)
          setAccountingCodeData(scoped)
        } else {
          const companyUsername = (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
          if (!companyUsername) {
            setAccountingCodeData([])
          } else {
            const vessels = await getVesselsByCompany(companyUsername)
            const vesselIds = vessels.map(v => v.id)
            const results = await Promise.all(
              vesselIds.map(id =>
                getAccountingAccountsWithParams(effectiveCgaId, id).then(c => c || []).catch(() => [])
              )
            )
            const all: AccountingAccount[] = []
            const seen = new Set<number>()
            results.forEach(list => list.forEach(code => {
              if (!seen.has(code.id)) { seen.add(code.id); all.push(code); }
            }))
            setAccountingCodeData(all)
          }
        }
      } else {
        const vesselForScope = forceVesselId ?? tenantVesselId
        if (tenantCgaId && vesselForScope) {
          const scoped = await getAccountingAccountsWithParams(tenantCgaId, vesselForScope)
          setAccountingCodeData(scoped)
        } else {
          const allAccountingCodes = await getAllAccountingAccounts()
          setAccountingCodeData(allAccountingCodes)
        }
      }
    } catch (error) {
      console.error('Error loading accounting code data:', error)
      setAccountingCodeData([])
    } finally {
      setAccountingLoading(false);
    }
  }

  const loadSubAccountingData = async () => {
    setSubAccountingLoading(true);
    try {
      const effectiveCgaId = libraryScope === 'COMPANY' ? tenantCgaId : undefined

      const allData = await getAllSubAccounts(effectiveCgaId)
      const dataArray = Array.isArray(allData) ? allData : [allData]

      if (libraryScope === 'COMPANY' && effectiveCgaId) {
        console.log('Filtering sub accounts for company:', effectiveCgaId)
        const filteredData = dataArray.filter(item => {
          const itemCgaId = typeof item.cgaid === 'object' ? item.cgaid?.id : item.cgaid
          return itemCgaId === effectiveCgaId
        })
        console.log(`Filtered ${filteredData.length} sub accounts out of ${dataArray.length} for company ${effectiveCgaId}`)
        setSubAccountingData(filteredData)
      } else {
        setSubAccountingData(dataArray)
      }
    } catch (error) {
      console.error('Error loading sub accounting data:', error)
      setSubAccountingData([])
    } finally {
      setSubAccountingLoading(false);
    }
  }

  const loadSubCatalogueData = async () => {
    setSubCatalogueLoading(true);
    try {
      const effectiveCgaId = libraryScope === 'COMPANY' ? tenantCgaId : undefined

      let allData: SubCatalogue[] = []

      if (libraryScope === 'ALL') {
        console.log('ALL scope: Fetching all sub catalogues without parameters...')
        allData = await getAllSubCatalogues()
        console.log(`Loaded ${allData.length} sub catalogues`)
        setSubCatalogueData(allData)
      } else if (libraryScope === 'COMPANY' && effectiveCgaId) {
        console.log('COMPANY scope: Fetching sub catalogues with parameters...')

        const subAccounts = await getAllSubAccounts()
        const companySubAccounts = subAccounts.filter(sa => {
          const saCgaId = typeof sa.cgaid === 'object' ? sa.cgaid?.id : sa.cgaid
          return saCgaId === effectiveCgaId
        })

        console.log(`Found ${companySubAccounts.length} sub accounts for company ${effectiveCgaId}`)

        // Get vessels for this company to determine vessel ids
        const companyUsername = (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
        let vesselIds: number[] = []

        if (companyUsername) {
          try {
            const vessels = await getVesselsByCompany(companyUsername)
            vesselIds = Array.from(new Set(vessels.map(v => v.id).filter(Boolean)))
            console.log(`Found ${vesselIds.length} vessels for company`)
          } catch (error) {
            console.error('Error loading vessels:', error)
          }
        }

        if (vesselIds.length === 0) {
          console.log('No vessels found for company, skipping sub catalogue fetch')
          setSubCatalogueData([])
          return
        }

        // Fetch sub catalogues for each combination
        const allSubCatalogues: SubCatalogue[] = []
        const seenIds = new Set<number>()

        const combinations = vesselIds.flatMap(vesselId =>
          companySubAccounts.map(sa => ({ vesselId, subaccountId: sa.id }))
        )

        console.log(`Fetching ${combinations.length} combinations...`)

        // Process in batches
        const BATCH_SIZE = 20
        for (let i = 0; i < combinations.length; i += BATCH_SIZE) {
          const batch = combinations.slice(i, i + BATCH_SIZE)
          const batchPromises = batch.map(({ vesselId, subaccountId }) =>
            getSubCatalogues(vesselId, subaccountId).catch(() => [])
          )

          const batchResults = await Promise.all(batchPromises)

          batchResults.forEach(data => {
            if (Array.isArray(data) && data.length > 0) {
              data.forEach(item => {
                if (!seenIds.has(item.id)) {
                  seenIds.add(item.id)
                  allSubCatalogues.push(item)
                }
              })
            }
          })
        }

        console.log(`? Company user: Loaded ${allSubCatalogues.length} sub catalogues`)
        setSubCatalogueData(allSubCatalogues)
      } else {
        console.log('VESSEL / fallback scope: loading all sub catalogues...')
        allData = await getAllSubCatalogues()
        setSubCatalogueData(allData)
      }
    } catch (error) {
      console.error('Error loading sub catalogue data:', error)
      setSubCatalogueData([])
    } finally {
      setSubCatalogueLoading(false);
    }
  }

  const loadPartData = async () => {
    setPartLoading(true);
    try {
      const parts = await getParts(undefined, vesselId ?? undefined)
      setPartData((parts || []).filter(p => p.id) as PartDto[])
    } catch (error) {
      console.error('Error loading part data:', error)
      setPartData([])
    } finally {
      setPartLoading(false);
    }
  }

  const loadSubComponentData = async () => {
    setIsLoadingSubComponents(true)
    try {
      const subs = await getSubcomponents(true, vesselId ?? undefined)
      setSubComponentData((subs || []).filter(sc => sc.id) as SubcomponentDto[])
    } catch (error) {
      console.error('Error loading sub component data:', error)
      setSubComponentData([]) // Set empty array on error
    } finally {
      setIsLoadingSubComponents(false)
    }
  }

  const loadEquipmentData = async () => {
    setEquipmentLoading(true)
    try {
      const data = await getMachineryEquipmentList(vesselId ?? undefined)
      setEquipmentData((data || []).filter(eq => eq.id) as EquipmentDto[])
    } catch (error) {
      console.error('Error loading equipment data:', error)
      setEquipmentData([])
    } finally {
      setEquipmentLoading(false)
    }
  }

  // Handle modal submissions for each tab
  const handleCategorySubmit = async () => {
    console.log('Category submitted, refreshing category data...')
    // Add a small delay to ensure the category is fully created
    setTimeout(() => {
      loadCategoryData()
    }, 500)
  }

  const handleEditCategory = (category: InventoryCategory) => {
    setSelectedCategory(category)
    setIsEditCategoryModalVisible(true)
  }

  const handleEditCategorySubmit = async () => {
    console.log('Category updated, refreshing category data...')
    setTimeout(() => {
      loadCategoryData()
    }, 500)
  }

  const handleHeadSubmit = async (payload: CreateInventoryItemHeadRequest) => {
    try {
      await createInventoryItemHead(payload)
      await loadHeadData()
    } catch (error) {
      throw error
    }
  }

  const handleEditHead = (head: InventoryItemHead) => {
    setSelectedHead(head)
    setIsEditHeadModalVisible(true)
  }

  const handleEditHeadSubmit = async (id: number, payload: CreateInventoryItemHeadRequest) => {
    try {
      await updateInventoryItemHead(id, payload)
      await loadHeadData()
    } catch (error) {
      throw error
    }
  }

  const handleDeleteHead = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this head?')) return

    try {
      await deleteInventoryItemHead(id)
      await loadHeadData()
      toast.success('Head deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting head:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete head'

      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this head because it has associated items. Please delete or reassign the items first.')
      } else {
        toast.error('Failed to delete head')
      }
    }
  }

  const handleSubHeadSubmit = async (payload: { name: string; inventoryItemHeadId: number }) => {
    try {
      await createInventoryItemSubHead(payload)
      await loadSubHeadData()
    } catch (error) {
      throw error
    }
  }

  const handleGeneralItemSubmit = async () => {
    await loadGeneralItemData()
  }

  const handleEditSubHead = (subHead: InventoryItemSubHead) => {
    setSelectedSubHead(subHead)
    setIsEditSubHeadModalVisible(true)
  }

  const handleEditSubHeadSubmit = async (id: number, payload: { name: string; inventoryItemHeadId: number }) => {
    try {
      await updateInventoryItemSubHead(id, payload)
      await loadSubHeadData()
    } catch (error) {
      throw error
    }
  }

  const handleDeleteSubHead = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this sub head?')) return

    try {
      await deleteInventoryItemSubHead(id)
      await loadSubHeadData()
      toast.success('Sub head deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting sub head:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete sub head'

      if (errorMessage.includes('foreign key constraint') || errorMessage.includes('Cannot delete or update a parent row')) {
        toast.error('Cannot delete this sub head because it has associated items. Please delete or reassign the items first.')
      } else {
        toast.error('Failed to delete sub head')
      }
    }
  }

  const handleAccountingCodeSubmit = async (cgaId?: number, vesselId?: number) => {
    console.log('Accounting code submitted, refreshing accounting code data with cgaId:', cgaId, 'vesselId:', vesselId)

    // Accounting refresh: ALL scope reloads full list; tenant scopes use explicit ids when provided
    if (libraryScope === 'ALL') {
      await loadAccountingCodeData()
      console.log('All accounting codes refreshed (ALL scope)')
    } else {
      await loadAccountingCodeData(cgaId, vesselId)
      console.log('Accounting code data refreshed for tenant scope')
    }

    setTimeout(async () => {
      console.log('Backup refresh of accounting code data...')
      if (libraryScope === 'ALL') {
        await loadAccountingCodeData()
      } else {
        await loadAccountingCodeData(cgaId, vesselId)
      }
      console.log('Backup accounting code data refreshed')
    }, 1000)
  }

  const handleEditAccountingCode = (accountingCode: AccountingAccount) => {
    setSelectedAccountingCode(accountingCode)
    setIsEditAccountingCodeModalVisible(true)
  }

  const handleEditAccountingCodeSubmit = async () => {
    console.log('Accounting code updated, refreshing accounting code data...')
    setTimeout(() => {
      loadAccountingCodeData()
    }, 500)
  }

  const handleSubAccountingSubmit = () => {
    console.log('InventoryList - Sub accounting submitted, refreshing data...')
    loadSubAccountingData()
  }

  const handleEditSubAccounting = (subAccount: SubAccount) => {
    setSelectedSubAccount(subAccount)
    setIsEditSubAccountingModalVisible(true)
  }

  const handleEditSubAccountingSubmit = async () => {
    console.log('Sub accounting updated, refreshing sub accounting data...')
    setTimeout(() => {
      loadSubAccountingData()
    }, 500)
  }

  const handleSubCatalogueSubmit = async () => {
    console.log('InventoryList - Sub catalogue submitted, refreshing data...')
    await loadSubCatalogueData()
    console.log('InventoryList - Sub catalogue data refreshed successfully')
    setAddItemModalRefreshTrigger(prev => prev + 1)
  }

  const handleEditSubCatalogue = (subCatalogue: SubCatalogue) => {
    setSelectedSubCatalogue(subCatalogue)
    setIsEditSubCatalogueModalVisible(true)
  }

  const handleEditSubCatalogueSubmit = async () => {
    console.log('Sub catalogue updated, refreshing sub catalogue data...')
    await loadSubCatalogueData()
    setAddItemModalRefreshTrigger(prev => prev + 1)
  }

  const handleEditPart = async (part: PartDto) => {
    const subId = part.subcomponentId ?? (part as any)?.subcomponent?.id ?? null
    if (!subId) {
      toast.error('This part is missing a linked sub component; cannot edit.')
      return
    }

    let partToEdit: PartDto = part
    if (part.id) {
      try {
        const fullPart = await getPartById(part.id)
        partToEdit = {
          ...part,
          ...(fullPart as any),
          rob:
            (fullPart as any)?.rob ??
            (fullPart as any)?.robQuantity ??
            (part as any)?.rob ??
            (part as any)?.robQuantity,
        } as PartDto
      } catch (error) {
        console.warn('Failed to fetch full part details before edit, using list row data:', error)
      }
    }

    setSelectedPart(partToEdit)
    setPartParentSubComponentId(subId)
    setIsPartModalVisible(true)
  }

  const handlePartSubmit = async (data?: any) => {
    try {
      const subcomponentId =
        (data?.subcomponentId ?? data?.subcomponent?.id) ??
        (partSubcomponentId ? Number(partSubcomponentId) : undefined) ??
        partParentSubComponentId

      if (!subcomponentId) {
        toast.error('Sub component is required')
        return
      }

      // Derive storage location path into location string (backend-valid: "Location: Sub: Segment")
      const computedLocation = (() => {
        const loc = storageLocations.find(l => String(l.id) === storageLocationId)
        const sub = loc?.subLocations?.find(s => String(s.id) === storageSubLocationId)
        const seg = sub?.segments?.find(sg => String(sg.id) === storageSegmentId)
        if (!loc?.name) return undefined
        const parts: string[] = [loc.name]
        if (sub?.name) parts.push(sub.name)
        if (seg?.name) parts.push(seg.name)
        return parts.join(': ')
      })()

      const payload = {
        name: (partName || data?.name || '').trim(),
        code: (partCode || data?.code || '').trim(),
        location: (partLocation || data?.location || computedLocation || '').trim(),
        subcomponent: { id: Number(subcomponentId) },
        rob: Number(partRob || data?.rob || 0),
        ...(partVesselId ? { vesselId: Number(partVesselId) } : data?.vesselId ? { vesselId: data.vesselId } : {}),
        ...(partEquipmentId ? { equipmentId: Number(partEquipmentId) } : partParentEquipmentId ? { equipmentId: partParentEquipmentId } : {}),
        isSpare: !!(data?.isSpare ?? isSparePart),
        manufacturer: (partManufacturer || data?.manufacturer || '').trim(),
        partNo: (partNumber || data?.partNo || '').trim(),
        serialNumber: (partSerialNumber || data?.serialNumber || '').trim(),
        ...(partUnitId ? { uom: { id: Number(partUnitId) } } : data?.uom?.id ? { uom: { id: Number(data.uom.id) } } : {}),
      }

      const isUpdate = !!data?.id
      if (isUpdate) {
        await machineryUpdatePart(data.id, payload as any)
      } else {
        await machineryCreatePart({
          subcomponentId: payload.subcomponent.id,
          name: payload.name,
          code: payload.code,
          location: payload.location,
          vesselId: payload.vesselId,
          equipmentId: payload.equipmentId,
          isSpare: payload.isSpare,
          manufacturer: payload.manufacturer,
          partNo: payload.partNo,
          serialNumber: payload.serialNumber,
          uomId: payload.uom?.id,
        } as any)
      }
      toast.success(isUpdate ? 'Part updated successfully!' : 'Part added successfully!')
      await loadPartData()
    } catch (error: any) {
      toast.error('Failed to save part')
    } finally {
      setIsPartModalVisible(false)
      setSelectedPart(null)
      setPartParentEquipmentId(null)
      setPartParentSubComponentId(null)
    }
  }

  const handleEditSubComponent = (subComponent: SubcomponentDto) => {
    const eqId = subComponent.equipmentId ?? (subComponent as any)?.equipment?.id ?? null
    if (!eqId) {
      toast.error('This sub component is missing a linked equipment; cannot edit.')
      return
    }
    setSelectedSubComponent(subComponent)
    setSubComponentParentEquipmentId(eqId)
    setIsSubComponentModalVisible(true)
  }

  const openCreateSubComponentModal = () => {
    setSelectedSubComponent(null)
    setSubComponentParentEquipmentId(null)
    setSubcomponentCompanyFilter('')
    setSubcomponentVesselId('')
    setSubcomponentVesselOptions([])
    setSubcomponentEquipmentOptions([])
    setSubcomponentName('')
    setSubcomponentCode('')
    setSubcomponentDesc('')
    setSubcomponentCriticality(false)
    loadCompanyOptions()
    setShowSubComponentCreateModal(true)
  }

  const openCreatePartModal = () => {
    setSelectedPart(null)
    setPartParentEquipmentId(null)
    setPartParentSubComponentId(null)
    setPartCompanyPicker('')
    setPartVesselId('')
    setPartVesselOptions([])
    setPartEquipmentOptions([])
    setPartSubcomponentOptions([])
    setPartEquipmentId('')
    setPartSubcomponentId('')
    setPartName('')
    setPartCode('')
    setPartNumber('')
    setPartUnitId('')
    setPartLocation('')
    setPartManufacturer('')
    setPartSerialNumber('')
    setPartRob('')
    setStorageLocationId('')
    setStorageSubLocationId('')
    setStorageSegmentId('')
    setIsSparePart(false)
    setUnitOptions([])
    setStorageLocations([])
    loadCompanyOptions()
    loadUnitOptions()
    loadStorageLocations()
    setShowPartCreateModal(true)
  }

  const handleEquipmentSubmit = async (data?: any) => {
    try {
      if (data?.id) {
        await machineryUpdateEquipment(data.id, data)
      } else {
        await machineryCreateEquipment(data)
      }
      toast.success(data?.id ? 'Equipment updated successfully!' : 'Equipment added successfully!')
      await loadEquipmentData()
    } catch (error: any) {
      toast.error('Failed to save equipment')
    } finally {
      setEquipmentModalMode('create')
      setSelectedEquipment(null)
      setIsEquipmentModalVisible(false)
    }
  }

  const openCreateEquipmentModal = () => {
    setEquipmentModalMode('create')
    setSelectedEquipment(null)
    setIsEquipmentModalVisible(true)
  }

  const handleEditEquipment = (equipment: EquipmentDto) => {
    setSelectedEquipment(equipment)
    setEquipmentModalMode('edit')
    setIsEquipmentModalVisible(true)
  }

  // ? REPLACE with one-time loaders per tab/subtab
  const loadedFlags = useRef({
    add_category: false,
    add_head: false,
    add_subHead: false,
    add_generalItem: false,
    add_accountingCode: false,
    add_subAccounting: false,
    add_subCatalogue: false,
    add_part: false,
    add_subComponent: false,
    add_equipment: false,
    companies_for_filter: false,
  });

  useEffect(() => {
    if (activeTab !== 'addItem') return;

    const needs = (key: keyof typeof loadedFlags.current) => !loadedFlags.current[key];

    if (addItemSubTab === 'category') {
      if (needs('add_category')) {
        loadCategoryData().finally(() => { loadedFlags.current.add_category = true; });
      }
      if (isGlobalScopeUser && needs('companies_for_filter')) {
        loadCompaniesForFilter().finally(() => { loadedFlags.current.companies_for_filter = true; });
      }
    }

    if (addItemSubTab === 'head' && needs('add_head')) {
      loadHeadData().finally(() => { loadedFlags.current.add_head = true; });
    }

    if (addItemSubTab === 'subHead' && needs('add_subHead')) {
      loadSubHeadData().finally(() => { loadedFlags.current.add_subHead = true; });
    }

    if (addItemSubTab === 'generalItem' && needs('add_generalItem')) {
      loadGeneralItemData().finally(() => { loadedFlags.current.add_generalItem = true; });
    }

    if (addItemSubTab === 'equipment' && needs('add_equipment')) {
      loadEquipmentData().finally(() => { loadedFlags.current.add_equipment = true; });
    }

    if (addItemSubTab === 'accountingCode' && needs('add_accountingCode')) {
      loadAccountingCodeData().finally(() => { loadedFlags.current.add_accountingCode = true; });
    }

    if (addItemSubTab === 'subAccounting' && needs('add_subAccounting')) {
      loadSubAccountingData().finally(() => { loadedFlags.current.add_subAccounting = true; });
    }

    if (addItemSubTab === 'subCatalogue' && needs('add_subCatalogue')) {
      loadSubCatalogueData().finally(() => { loadedFlags.current.add_subCatalogue = true; });
    }

    if (addItemSubTab === 'part' && needs('add_part')) {
      loadPartData().finally(() => { loadedFlags.current.add_part = true; });
    }

    if (addItemSubTab === 'subComponent' && needs('add_subComponent')) {
      loadSubComponentData().finally(() => { loadedFlags.current.add_subComponent = true; });
    }
  }, [activeTab, addItemSubTab, isGlobalScopeUser]);

  useEffect(() => {
    if (activeTab !== 'addItem' || addItemSubTab !== 'generalItem') return
    loadGeneralItemData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, addItemSubTab, vesselId])


  // Reset company filter when switching away from category tab
  useEffect(() => {
    if (addItemSubTab !== 'category') {
      setCategoryCompanyFilter('')
    }
  }, [addItemSubTab])

  // Reload category data when company filter changes
  useEffect(() => {
    if (addItemSubTab === 'category') {
      loadCategoryData()
    }
  }, [categoryCompanyFilter, addItemSubTab])

    // Export function for Add Item tabs
  const exportAddItemTab = () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', format: 'a4' });
      const today = new Date().toLocaleDateString();

      // Common header
      doc.setFontSize(16);
      doc.setFontSize(10);

      let title = '';
      let tableData: any[] = [];
      let headers: string[] = [];

      switch (addItemSubTab) {
        case 'category':
          title = 'Inventory Categories';
          headers = ['Sr. No.', 'Category Name', 'Company', 'Scope'];
          tableData = sortedCategoryData.map((cat, idx) => [
            (idx + 1).toString(),
            cat.name || '',
            (cat.cgaid as any)?.name || 'N/A',
            'Company-wide',
          ]);
          break;

        case 'head':
          title = 'Inventory Item Heads';
          headers = ['Sr. No.', 'Head Name', 'Category', 'Company', 'Vessel'];
          tableData = sortedHeadData.map((head, idx) => [
            (idx + 1).toString(),
            head.name || '',
            head.inventoryItemCategoryName || 'N/A',
            head.cgaid?.name || 'N/A',
            head.vesselName ||
              (typeof head.vesselType === 'string'
                ? head.vesselType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                : head.vesselType?.fleet_name || 'N/A')
          ]);
          break;

        case 'subHead':
          title = 'Inventory Item Sub Heads';
          headers = ['Sr. No.', 'Sub Head Name', 'Head', 'Company', 'Vessel'];
          tableData = sortedSubHeadData.map((subHead, idx) => [
            (idx + 1).toString(),
            subHead.name || '',
            subHead.inventoryItemHeadName || 'N/A',
            subHead.cgaid?.name || 'N/A',
            subHead.vesselName ||
              (typeof subHead.vesselType === 'string'
                ? subHead.vesselType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                : subHead.vesselType?.fleet_name || 'N/A')
          ]);
          break;

        case 'generalItem':
          title = 'General Items';
          headers = ['Sr. No.', 'Item Name', 'Unit', 'Quantity', 'Location', 'Vessel', 'Company'];
          tableData = sortedGeneralItemData.map((item, idx) => [
            (idx + 1).toString(),
            item.manualItemName || '',
            item.unitOfMeasurement || '',
            `${item.currentQty ?? 0} ${(item.unitOfMeasurement || '').trim()}`.trim(),
            item.location || '',
            item.vessel?.fleet_name || 'N/A',
            item.vessel?.companyGroupAdmin?.name || item.vessel?.companyAdmin?.name || 'N/A'
          ]);
          break;

        case 'equipment':
          title = 'Equipment';
          headers = ['Sr. No.', 'Name', 'Code', 'Short Form'];
          if (showEquipmentCompanyCol) headers.push('Company');
          if (showEquipmentVesselCol) headers.push('Vessel');
          headers.push(
            'Criticality',
            'Maker',
            'Model',
            'Serial Number',
            'Installation Date',
            'Location',
            'Class Critical',
            'Survey Relevant',
            'Function Description'
          );

          tableData = equipmentData.map((eq, idx) => {
            const row: (string | number)[] = [
              (idx + 1).toString(),
              eq.name || '',
              eq.code || '',
              valueOrDash(eq.shortForm),
            ];
            if (showEquipmentCompanyCol) {
              row.push(valueOrDash(eq.companyName));
            }
            if (showEquipmentVesselCol) {
              row.push(valueOrDash(eq.vesselName));
            }
            row.push(
              formatYesNo(eq.criticality as any),
              valueOrDash(eq.maker),
              valueOrDash(eq.model),
              valueOrDash(eq.serialNumber),
              formatDateSafe(eq.installationDate as any),
              valueOrDash(eq.location),
              formatYesNo(eq.isClassCritical as any),
              formatYesNo(eq.isSurveyRelevant as any),
              valueOrDash((eq as any).equipmentFunctionDescription)
            );
            return row;
          });
          break;

        case 'accountingCode':
          title = 'Accounting Codes';
          headers = ['Sr. No.', 'Account Code', 'Company', 'Vessel'];
          tableData = sortedAccountingCodeData.map((acc, idx) => [
            (idx + 1).toString(),
            acc.accountCodes || '',
            typeof acc.cgaid === 'object' && acc.cgaid !== null ? (acc.cgaid as any).name || 'N/A' : 'N/A',
            acc.vesselName ||
              (typeof acc.vesselType === 'string'
                ? acc.vesselType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                : 'N/A')
          ]);
          break;

        case 'subAccounting':
          title = 'Sub Accounting Codes';
          headers = ['Sr. No.', 'Sub Account Code', 'Account Code', 'Company', 'Vessel'];
          tableData = sortedSubAccountingData.map((sub, idx) => [
            (idx + 1).toString(),
            (sub as any).subaccountCodes || (sub as any).subAccountCodes || '',
            (sub as any).accountCode || 'N/A',
            (sub as any).cgaid?.name || 'N/A',
            (sub as any).vesselName ||
              (typeof (sub as any).vesselType === 'string'
                ? (sub as any).vesselType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                : 'N/A')
          ]);
          break;

        case 'subCatalogue':
          title = 'Sub Catalogues';
          headers = ['Sr. No.', 'Sub Catalogue Name', 'Company', 'Vessel'];
          tableData = sortedSubCatalogueData.map((sub, idx) => [
            (idx + 1).toString(),
            sub.name || '',
            (sub as any).cgaid?.name || 'N/A',
            (sub as any).vesselName ||
              (typeof (sub as any).vesselType === 'string'
                ? (sub as any).vesselType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                : 'N/A')
          ]);
          break;

        case 'part':
          title = 'Parts';
          headers = ['Sr. No.', 'Part Name', 'Part Code', 'Sub Component Name', 'Sub Component Code'];
          if (showPartCompanyCol) headers.push('Company');
          if (showPartVesselCol) headers.push('Vessel');
          headers.push(
            'Manufacturer',
            'Part No.',
            'Serial Number',
            'Spare',
            'UOM',
            'Item Code',
            'Short Description',
            'Long Description',
            'Drawing No.',
            'Drawing Position No.',
            'Location'
          );
          tableData = sortedPartData.map((part, idx) => {
            const row: (string | number)[] = [
              (idx + 1).toString(),
              part.name || '',
              (part as any).code || '',
              part.subcomponent?.name || '',
              (part.subcomponent as any)?.subComponentCode || '',
            ];
            if (showPartCompanyCol) row.push((part.vessel as any)?.companyGroupAdmin?.name || '');
            if (showPartVesselCol) row.push(part.vessel?.fleet_name || '');
            row.push(
              part.manufacturer || '',
              part.partNo || '',
              part.serialNumber || '',
              formatYesNo(part.isSpare as any),
              part.uom?.code || part.uom?.name || '',
              part.itemCode || '',
              part.itemShortDescription || '',
              part.itemLongDescription || '',
              part.drawNo || '',
              part.drawingPositionNo || '',
              part.location || ''
            );
            return row;
          });
          break;

        case 'subComponent':
          title = 'Sub Components';
          headers = ['Sr. No.', 'Sub Component Name', 'Sub Component Code', 'Equipment Name', 'Equipment Code'];
          if (showSubComponentCompanyCol) headers.push('Company');
          if (showSubComponentVesselCol) headers.push('Vessel');
          headers.push(
            'Maker',
            'Criticality',
            'Open Job',
            'Instance Count',
            'Function Description',
            'Particulars',
            'Instance No.',
            'Instance Description'
          );
          tableData = sortedSubComponentData.map((comp, idx) => {
            const row: (string | number)[] = [
              (idx + 1).toString(),
              comp.name || '',
              (comp as any).subComponentCode || '',
              (comp as any).equipmentName || '',
              (comp as any).equipmentCode || '',
            ];
            if (showSubComponentCompanyCol) row.push((comp as any).companyName || '');
            if (showSubComponentVesselCol) row.push((comp as any).vesselName || '');
            row.push(
              (comp as any).maker || '',
              formatYesNo(comp.criticality as any),
              formatYesNo((comp as any).hasOpenJob as any),
              valueOrDash((comp as any).instanceCount),
              valueOrDash((comp as any).subComponentFunctionDescription),
              valueOrDash((comp as any).subComponentParticulars),
              valueOrDash((comp as any).subComponentInstanceNumber),
              valueOrDash((comp as any).subComponentInstanceDescription)
            );
            return row;
          });
          break;
      }

      // Add title and metadata
      doc.text(title, 14, 15);
      doc.text(`Generated on: ${today}`, 14, 22);
      doc.text(`Total Records: ${tableData.length}`, 14, 28);

      // Generate table
      (doc as any).autoTable({
        head: [headers],
        body: tableData,
        startY: 35,
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: 'linebreak',
          cellWidth: 'wrap',
          minCellHeight: 6,
          valign: 'middle',
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 10,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250]
        },
        margin: { top: 35, left: 10, right: 10 },
        pageBreak: 'auto',
        tableLineWidth: 0.1,
        tableLineColor: [200, 200, 200],
      });

      // Save PDF
      const fileName = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);
      toast.success('PDF exported successfully!');

    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    }
  };

  if (procurementPermissionsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading procurement permissions...</span>
        </div>
      </div>
    )
  }

  if (!canViewInventory) {
    return <div className='alert alert-warning m-6'>You do not have inventory library access.</div>
  }

  return (
    <>
      {viewDetail.open && (
        <div className='modal fade show d-block' tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className='modal-dialog modal-lg modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title fw-bold'>
                  {viewDetail.kind === 'equipment' && 'Equipment Details'}
                  {viewDetail.kind === 'subcomponent' && 'Sub Component Details'}
                  {viewDetail.kind === 'part' && 'Part Details'}
                </h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    setViewDetail({ open: false })
                    setMappedManualsForPart([])
                    setMappedManualsLoading(false)
                  }}
                ></button>
              </div>
              <div className='modal-body'>
                {viewDetail.kind === 'equipment' && viewDetail.data && (
                  <div className='row g-3'>
                    <div className='col-md-6'><strong>Name:</strong> {valueOrDash(viewDetail.data.name)}</div>
                    <div className='col-md-6'><strong>Code:</strong> {valueOrDash(viewDetail.data.code)}</div>
                    <div className='col-md-6'><strong>Short Form:</strong> {valueOrDash(viewDetail.data.shortForm)}</div>
                    <div className='col-md-6'><strong>Criticality:</strong> {formatYesNo(viewDetail.data.criticality)}</div>
                    <div className='col-md-6'><strong>Maker:</strong> {valueOrDash(viewDetail.data.maker)}</div>
                    <div className='col-md-6'><strong>Model:</strong> {valueOrDash(viewDetail.data.model)}</div>
                    <div className='col-md-6'><strong>Serial:</strong> {valueOrDash(viewDetail.data.serialNumber)}</div>
                    <div className='col-md-6'><strong>Installation Date:</strong> {formatDateSafe(viewDetail.data.installationDate)}</div>
                    <div className='col-md-6'><strong>Location:</strong> {valueOrDash(viewDetail.data.location)}</div>
                    <div className='col-md-6'><strong>Class Critical:</strong> {formatYesNo(viewDetail.data.isClassCritical)}</div>
                    <div className='col-md-6'><strong>Survey Relevant:</strong> {formatYesNo(viewDetail.data.isSurveyRelevant)}</div>
                    <div className='col-md-12'><strong>Function Description:</strong> {valueOrDash(viewDetail.data.equipmentFunctionDescription)}</div>
                  </div>
                )}
                {viewDetail.kind === 'subcomponent' && viewDetail.data && (
                  <div className='row g-3'>
                    <div className='col-md-6'><strong>Name:</strong> {valueOrDash(viewDetail.data.name)}</div>
                    <div className='col-md-6'><strong>Code:</strong> {valueOrDash(viewDetail.data.subComponentCode)}</div>
                    <div className='col-md-6'><strong>Criticality:</strong> {formatYesNo(viewDetail.data.criticality)}</div>
                    <div className='col-md-6'><strong>Open Job:</strong> {formatYesNo(viewDetail.data.hasOpenJob)}</div>
                    <div className='col-md-6'><strong>Instance Count:</strong> {valueOrDash(viewDetail.data.instanceCount)}</div>
                    <div className='col-md-6'><strong>Instance No.:</strong> {valueOrDash(viewDetail.data.subComponentInstanceNumber)}</div>
                    <div className='col-md-12'><strong>Instance Description:</strong> {valueOrDash(viewDetail.data.subComponentInstanceDescription)}</div>
                    <div className='col-md-12'><strong>Function Description:</strong> {valueOrDash(viewDetail.data.subComponentFunctionDescription)}</div>
                    <div className='col-md-12'><strong>Particulars:</strong> {valueOrDash(viewDetail.data.subComponentParticulars)}</div>
                  </div>
                )}
                {viewDetail.kind === 'part' && viewDetail.data && (
                  <>
                    <div className='row g-3'>
                      <div className='col-md-6'><strong>Name:</strong> {valueOrDash(viewDetail.data.name)}</div>
                      <div className='col-md-6'><strong>Code:</strong> {valueOrDash(viewDetail.data.code)}</div>
                      <div className='col-md-6'><strong>Manufacturer:</strong> {valueOrDash(viewDetail.data.manufacturer)}</div>
                      <div className='col-md-6'><strong>Part No.:</strong> {valueOrDash(viewDetail.data.partNo)}</div>
                      <div className='col-md-6'><strong>Serial Number:</strong> {valueOrDash(viewDetail.data.serialNumber)}</div>
                      <div className='col-md-6'><strong>Spare:</strong> {formatYesNo(viewDetail.data.isSpare)}</div>
                      <div className='col-md-6'><strong>UOM:</strong> {valueOrDash(viewDetail.data.uom?.code || viewDetail.data.uom?.name)}</div>
                      <div className='col-md-6'><strong>Item Code:</strong> {valueOrDash(viewDetail.data.itemCode)}</div>
                      <div className='col-md-6'><strong>Short Description:</strong> {valueOrDash(viewDetail.data.itemShortDescription)}</div>
                      <div className='col-md-12'><strong>Long Description:</strong> {valueOrDash(viewDetail.data.itemLongDescription)}</div>
                      <div className='col-md-6'><strong>Drawing No.:</strong> {valueOrDash(viewDetail.data.drawNo)}</div>
                      <div className='col-md-6'><strong>Drawing Position No.:</strong> {valueOrDash(viewDetail.data.drawingPositionNo)}</div>
                      <div className='col-md-6'><strong>Location:</strong> {valueOrDash(viewDetail.data.location)}</div>
                    </div>
                    <div className='separator my-6'></div>
                    <div>
                      <h6 className='fw-bold mb-3'>Related Manuals</h6>
                      {mappedManualsLoading ? (
                        <div className='text-muted'>Loading mapped manuals/plans...</div>
                      ) : mappedManualsForPart.length === 0 ? (
                        <div className='text-muted'>No related manuals.</div>
                      ) : (
                        <div
                          className='d-grid gap-3'
                          style={{
                            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                            maxHeight: mappedManualsForPart.length > 6 ? '235px' : undefined,
                            overflowY: mappedManualsForPart.length > 6 ? 'auto' : 'visible',
                            paddingRight: mappedManualsForPart.length > 6 ? '4px' : 0,
                          }}
                        >
                          {mappedManualsForPart.map((manual) => (
                            <button
                              key={manual.id}
                              type='button'
                              className='btn text-start text-decoration-none border border-gray-300 rounded px-4 py-3 bg-white'
                              style={{minHeight: '86px'}}
                              onClick={() =>
                                setSelectedMappedManual({
                                  id: manual.id,
                                  name: manual.name,
                                  uploadedDate: manual.uploadedDate || '',
                                  file: {name: manual.name},
                                  remarks: manual.remarks || undefined,
                                })
                              }
                            >
                              <div className='d-flex align-items-center gap-3'>
                                <div
                                  className='d-flex align-items-center justify-content-center rounded'
                                  style={{
                                    width: '38px',
                                    height: '38px',
                                    backgroundColor: '#EEF6FF',
                                    flexShrink: 0,
                                  }}
                                >
                                  <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-2 text-primary' />
                                </div>
                                <span className='text-dark fw-semibold text-break'>{manual.name}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => {
                    setViewDetail({ open: false })
                    setMappedManualsForPart([])
                    setMappedManualsLoading(false)
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedMappedManual && (
        <ManualPlanViewer manual={selectedMappedManual} onClose={() => setSelectedMappedManual(null)} />
      )}

      <style>{`
        @media print {
          /* Hide non-essential elements */
          .card-header,
          .card-toolbar,
          .pagination-wrapper,
          button,
          .btn,
          nav,
          .sidebar,
          .app-sidebar,
          #kt_app_sidebar,
          .header,
          .app-header,
          #kt_app_header,
          .footer,
          .app-footer {
            display: none !important;
          }

          /* Make table fit on page */
          body {
            margin: 0;
            padding: 5px;
          }

          .app-main,
          .app-content,
          .card,
          .card-body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            visibility: visible !important;
          }

          .table-responsive {
            overflow: visible !important;
            page-break-inside: auto !important;
            display: block !important;
            visibility: visible !important;
          }

          .report-table {
            page-break-inside: auto !important;
            display: block !important;
            visibility: visible !important;
          }
          
          /* Ensure all divs containing the table are visible */
          div {
            visibility: visible !important;
          }

          table {
            width: 100% !important;
            font-size: 6px !important;
            page-break-inside: auto !important;
            page-break-after: auto !important;
            border-collapse: collapse !important;
            table-layout: fixed !important;
            display: table !important;
            visibility: visible !important;
          }

          thead {
            display: table-header-group !important;
            visibility: visible !important;
          }

          tbody {
            display: table-row-group !important;
            visibility: visible !important;
          }

          tr {
            page-break-inside: avoid !important;
            page-break-after: auto !important;
            display: table-row !important;
            visibility: visible !important;
          }

          td, th {
            display: table-cell !important;
            visibility: visible !important;
          }

          tfoot {
            display: table-footer-group !important;
          }

          th, td {
            padding: 2px 1px !important;
            font-size: 6px !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            max-width: 100px !important;
          }

          /* More aggressive column width reduction for A4 */
          th:nth-child(1), td:nth-child(1) { width: 4% !important; max-width: 40px !important; }
          th:nth-child(2), td:nth-child(2) { width: 7% !important; max-width: 60px !important; }
          th:nth-child(3), td:nth-child(3) { width: 7% !important; max-width: 60px !important; }
          th:nth-child(4), td:nth-child(4) { width: 7% !important; max-width: 60px !important; }
          th:nth-child(5), td:nth-child(5) { width: 6% !important; max-width: 50px !important; }
          th:nth-child(6), td:nth-child(6) { width: 7% !important; max-width: 60px !important; }
          th:nth-child(7), td:nth-child(7) { width: 4% !important; max-width: 35px !important; }
          th:nth-child(8), td:nth-child(8) { width: 3% !important; max-width: 30px !important; }
          th:nth-child(9), td:nth-child(9) { width: 4% !important; max-width: 40px !important; }
          th:nth-child(10), td:nth-child(10) { width: 3% !important; max-width: 30px !important; }
          th:nth-child(11), td:nth-child(11) { width: 3% !important; max-width: 30px !important; }
          th:nth-child(12), td:nth-child(12) { width: 5% !important; max-width: 45px !important; }
          th:nth-child(13), td:nth-child(13) { width: 5% !important; max-width: 50px !important; }
          th:nth-child(14), td:nth-child(14) { width: 6% !important; max-width: 55px !important; }
          th:nth-child(15), td:nth-child(15) { width: 6% !important; max-width: 55px !important; }
          th:nth-child(16), td:nth-child(16) { width: 4% !important; max-width: 40px !important; }
          th:nth-child(17), td:nth-child(17) { width: 3% !important; max-width: 30px !important; }

          /* Hide action column in print */
          th:last-child, td:last-child {
            display: none !important;
          }

          /* Page settings for proper multi-page printing */
          @page {
            size: auto;
            margin: 10mm;
            orphans: 2;
            widows: 2;
          }
        }
      `}</style>
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='d-flex flex-column flex-column-fluid'>
          <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
            <div className='card'>
              <div className='card-header border-0 pt-6 d-flex justify-content-between align-items-center bg-white'>
                <div>
                  <h3 className='card-label text-dark fw-bold'>Inventory Management</h3>
                  <p className='text-muted mb-0'>Manage vessel inventory, stock levels, and equipment spares</p>
                  {!canCreateItems && (
                    <small className='text-warning'>
                      <i className='fas fa-info-circle me-1'></i>
                      View-only access - Creation permissions limited to Super Admin and Company roles
                    </small>
                  )}
                </div>
                <div className='card-toolbar d-flex gap-2'>
                  <button
                    type='button'
                    className='btn btn_primary'
                    style={{
                      backgroundColor: '',
                      color: 'white',
                      border: 'none'
                    }}
                    disabled={listLoading}
                    onClick={async () => {
                      // Determine which tab is active and export accordingly
                      if (activeTab === 'addItem') {
                        // Export Add Item tabs
                        exportAddItemTab();
                        return;
                      }

                      // Export Inventory Catalogue
                      const originalPage = currentPage;
                      const originalRowsPerPage = rowsPerPage;

                      // Temporarily set to fetch all records
                      setRowsPerPage(totalElements || 1000);
                      setCurrentPage(1);

                      // Wait for loading to complete by polling the listLoading state
                      const waitForLoad = async () => {
                        let attempts = 0;
                        const targetCount = totalElements || 1000;
                        while (attempts < 100) { // Max 10 seconds (100 * 100ms)
                          await new Promise(resolve => setTimeout(resolve, 100));
                          // Check if data has been loaded by comparing inventory length
                          console.log(`Waiting for data: ${inventoryData.length} / ${targetCount}, mapReady: ${mapReady}`);
                          if (inventoryData.length >= targetCount - 2) { // Tighter margin
                            console.log('Data loaded, waiting for lazy mapping to complete...');
                            break;
                          }
                          attempts++;
                        }
                      };

                      await waitForLoad();

                      // Wait for lazy mapping to complete (mapReady state)
                      const waitForMapping = async () => {
                        let attempts = 0;
                        while (attempts < 100 && !mapReady) { // Max 10 seconds
                          await new Promise(resolve => setTimeout(resolve, 100));
                          console.log(`Waiting for mapping: mapReady = ${mapReady}`);
                          attempts++;
                        }
                        console.log(`Mapping complete: mapReady = ${mapReady}`);
                        // Extra wait to ensure DOM is fully rendered
                        await new Promise(resolve => setTimeout(resolve, 1000));
                      };

                      await waitForMapping();

                      console.log(`Final inventory count: ${inventoryData.length}, ready to export`);

                      // Export to PDF using jsPDF
                      try {
                        const doc = new jsPDF({ orientation: 'landscape', format: 'a3' });

                        // Title
                        doc.setFontSize(16);
                        doc.text('Inventory Catalogue', 14, 15);

                        // Subtitle with date
                        doc.setFontSize(10);
                        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
                        doc.text(`Total Items: ${inventoryData.length}`, 14, 28);

                        // Prepare table data
                        const tableData = inventoryData.map(item => [
                          item.itemId || '',
                          item.itemName || '',
                          item.company || '',
                          item.vesselName || item.vesselType || '',
                          item.partNumber || '',
                          item.category || '',
                          item.partType || '',
                          item.currentQty?.toString() || '',
                          item.uom || '',
                          item.reorderLevel?.toString() || '',
                          item.minThreshold?.toString() || '',
                          item.maxThreshold?.toString() || '',
                          `$${item.unitCost?.toFixed(2) || '0.00'}`,
                          item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '',
                          item.serialNumber || '',
                          item.location || '',
                          item.status || ''
                        ]);

                        // Generate table
                        (doc as any).autoTable({
                          head: [[
                            'Item ID', 'Item Name', 'Company', 'Vessel', 'Part Number',
                            'Category', 'Type', 'Qty', 'UOM', 'Reorder', 'Min', 'Max',
                            'Unit Cost', 'Expiry', 'Serial No.', 'Location', 'Status'
                          ]],
                          body: tableData,
                          startY: 35,
                          styles: {
                            fontSize: 7,
                            cellPadding: 2.5,
                            overflow: 'linebreak',
                            cellWidth: 'wrap',
                            minCellHeight: 6,
                            valign: 'middle',
                          },
                          headStyles: {
                            fillColor: [240, 240, 240],
                            textColor: [0, 0, 0],
                            fontStyle: 'bold',
                            fontSize: 8,
                          },
                          alternateRowStyles: {
                            fillColor: [250, 250, 250]
                          },
                          columnStyles: {
                            0: { cellWidth: 20 },  // Item ID
                            1: { cellWidth: 25 },  // Item Name
                            2: { cellWidth: 25 },  // Company
                            3: { cellWidth: 25 },  // Vessel
                            4: { cellWidth: 22 },  // Part Number
                            5: { cellWidth: 25 },  // Category
                            6: { cellWidth: 15 },  // Type
                            7: { cellWidth: 12 },  // Qty
                            8: { cellWidth: 12 },  // UOM
                            9: { cellWidth: 15 },  // Reorder
                            10: { cellWidth: 12 }, // Min
                            11: { cellWidth: 12 }, // Max
                            12: { cellWidth: 18 }, // Unit Cost
                            13: { cellWidth: 20 }, // Expiry
                            14: { cellWidth: 22 }, // Serial No
                            15: { cellWidth: 22 }, // Location
                            16: { cellWidth: 15 }, // Status
                          },
                          margin: { top: 35, left: 10, right: 10 },
                          pageBreak: 'auto',
                          tableLineWidth: 0.1,
                          tableLineColor: [200, 200, 200],
                        });

                        // Save the PDF
                        doc.save(`Inventory_Catalogue_${new Date().toISOString().slice(0, 10)}.pdf`);
                        toast.success('PDF exported successfully!');
                      } catch (error) {
                        console.error('PDF export failed:', error);
                        toast.error('Failed to export PDF');
                      }

                      // Restore original pagination
                      setTimeout(() => {
                        setRowsPerPage(originalRowsPerPage);
                        setCurrentPage(originalPage);
                      }, 500);
                    }}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    Export
                  </button>
                  {activeTab === 'addItem' ? (
                    <>
                      {addItemSubTab === 'category' && canCreateCategoryLibrary && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          onClick={() => setIsCategoryModalVisible(true)}
                        >
                          <span className='me-2'>+</span>
                          Add Category
                        </button>
                      )}
                      {addItemSubTab === 'head' && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          onClick={() => setIsHeadModalVisible(true)}
                        >
                          <span className='me-2'>+</span>
                          Add Head
                        </button>
                      )}
                      {addItemSubTab === 'subHead' && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          onClick={() => setIsSubHeadModalVisible(true)}
                        >
                          <span className='me-2'>+</span>
                          Add Sub Head
                        </button>
                      )}
                      {isShowingGeneralItems && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          onClick={() => setIsGeneralItemModalVisible(true)}
                        >
                          <span className='me-2'>+</span>
                          Add General Item
                        </button>
                      )}
                      {addItemSubTab === 'accountingCode' && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          onClick={() => setIsAccountingCodeModalVisible(true)}
                        >
                          <span className='me-2'>+</span>
                          Add Accounting Code
                        </button>
                      )}
                      {addItemSubTab === 'subAccounting' && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          onClick={() => setIsSubAccountingModalVisible(true)}
                        >
                          <span className='me-2'>+</span>
                          Add Sub Accounting
                        </button>
                      )}
                      {addItemSubTab === 'subCatalogue' && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          onClick={() => setIsSubCatalogueModalVisible(true)}
                        >
                          <span className='me-2'>+</span>
                          Add Sub Catalogue
                        </button>
                      )}
                    {showLegacyInventoryTabs && addItemSubTab === 'equipment' && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          onClick={openCreateEquipmentModal}
                        >
                          <span className='me-2'>+</span>
                          Add Equipment
                        </button>
                      )}
                    {showLegacyInventoryTabs && addItemSubTab === 'part' && (
                      <>
                        <button
                          type='button'
                          className='btn btn_primary me-2'
                          onClick={openCreatePartModal}
                        >
                          <span className='me-2'>+</span>
                          Add Part
                        </button>
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => setIsStorageLibraryVisible(true)}
                        >
                          Storage Location Library
                        </button>
                      </>
                    )}
                      {addItemSubTab === 'subComponent' && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          onClick={openCreateSubComponentModal}
                        >
                          <span className='me-2'>+</span>
                          Add Sub Component
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      type='button'
                      className='btn btn_primary'
                      onClick={() => setIsUpdateModalVisible(true)}
                    >
                      <span className='me-2'>?</span>
                      Update Inventory
                    </button>
                  )}
                </div>
              </div>

              <div className='card-body py-4 bg-white border-top'>
                <div className='d-flex justify-content-between align-items-center mb-4'>
                  <div className='btn-group' style={{ marginBottom: '10px' }}>
                    <button
                      className={`btn btn-sm ${activeTab === 'catalogue' ? 'btn_primary' : 'btn-light'
                        }`}
                      onClick={() => setActiveTab('catalogue')}
                    >
                      Inventory Catalogue ({inventoryData.length})
                    </button>
                  </div>
                  {/* General Items Library moved to Machinery page */}
                </div>

                <div className="row mb-4">
                  {[
                    { title: "Total Items", value: totalItems.toString(), path: "/media/map/box-delivery.svg" },
                    { title: "Low Stock Items", value: lowStockItems.toString(), path: "/media/map/block-alerted.svg" },
                    { title: "Critical Items", value: criticalItems.toString(), path: "/media/map/box.svg" },
                    { title: "Total Value", value: `${totalValue.toFixed(2)}K`, path: "/media/map/tags-category.svg" },
                  ].map((card, index) => (
                    <div key={index} className="col-md-3 mb-3">
                      <div className="custom-card p-3">
                        <div className='d-flex justify-content-between'>
                          <div>
                            <h2 className="fw-bold mb-1">{card.value}</h2>
                            <h6 className="card-title">{card.title}</h6>
                          </div>
                          <KTSVG path={card.path} className='svg-icon svg-icon-2x' />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {activeTab === 'catalogue' && (
                  <div className='row gx-3 gy-3 mb-4'>
                    <div className='col-md-4'>
                      <label className='form-label text-muted fw-semibold fs-7 mb-2'>Search</label>
                      <div className='position-relative'>
                        <div
                          className='position-absolute ms-3'
                          style={{ top: '50%', transform: 'translateY(-50%)' }}
                        >
                          <KTSVG
                            path='/media/icons/duotune/general/gen021.svg'
                            className='svg-icon-2'
                          />
                        </div>
                        <input
                          type='text'
                          className='form-control form-control-sm ps-10'
                          placeholder='Search items, part numbers, or descriptions...'
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className='col-md-2'>
                      <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                        All Locations
                      </label>
                      <SearchableSelect
                        className='form-select'
                        style={{
                          border: '1px solid #E4E6EF',
                          borderRadius: '6px',
                          fontSize: '14px',
                          padding: '8px 12px',
                          color: '#5E6278',
                        }}
                        value={filters.location}
                        onChange={(e) => handleFilterChange('location', e.target.value)}
                      >
                        <option value=''>All Locations</option>
                        <option value='Engine Room'>Engine Room</option>
                        <option value='Bridge'>Bridge</option>
                        <option value='Deck Store'>Deck Store</option>
                        <option value='Safety Equipment Locker'>Safety Equipment Locker</option>
                        <option value='Workshop'>Workshop</option>
                      </SearchableSelect>
                    </div>
                  </div>
                )}

                {showLegacyInventoryTabs && activeTab === 'hierarchy' && (
                  <div className='card card-flush shadow-sm border mb-5'>
                    <div className='card-header bg-white border-0 pt-5 pb-2'>
                      <div>
                        <h4 className='mb-1 fw-bold text-dark'>Inventory Hierarchy</h4>
                        <span className='text-muted fs-7'>Vessel ? Equipment ? Sub Component ? Part</span>
                      </div>
                      <div>
                        <button
                          className='btn btn-sm btn-light'
                          onClick={async () => {
                            setHierarchyLoading(true)
                            try {
                              await Promise.all([loadEquipmentData(), loadSubComponentData(), loadPartData()])
                              toast.success('Hierarchy refreshed')
                            } finally {
                              setHierarchyLoading(false)
                            }
                          }}
                        >
                          <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2 me-1' />
                          Refresh
                        </button>
                      </div>
                    </div>
                    <div className='card-body pt-0'>
                      {hierarchyLoading ? (
                        <div className='text-center py-6'>
                          <span className='spinner-border text-primary' role='status'></span>
                          <div className='text-muted mt-3'>Loading hierarchy?</div>
                        </div>
                      ) : (
                        renderHierarchy()
                      )}
                    </div>
                  </div>
                )}

                {showLegacyInventoryTabs && activeTab === 'addItem' && !canCreateItems && (
                  <div className='card shadow-sm'>
                    <div className='card-body text-center py-5'>
                      <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-4x text-muted mb-3' />
                      <h5 className='text-muted mb-2'>Access Restricted</h5>
                      <p className='text-muted mb-0'>
                        You don't have permission to create inventory items.
                        <br />
                        Only Super Admin and Company roles can access this functionality.
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === 'addItem' && canCreateItems && (showLegacyInventoryTabs || showGeneralItemLibraryContent) && (
                  <div style={{ overflow: 'visible', position: 'relative' }}>
                    {/* Nested tabs for Add Item section */}
                    {showLegacyInventoryTabs && (
                    <div className='d-flex flex-column gap-2 mb-4' style={{ overflow: 'visible', minHeight: '40px' }}>
                      <div className='d-flex justify-content-between align-items-center'>
                        <div className='btn-group'>
                          <button
                            className={`btn btn-sm ${addItemSubTab === 'category' ? 'btn_primary' : 'btn-light'}`}
                            onClick={() => setAddItemSubTab('category')}
                          >
                            Category
                          </button>
                          <button
                            className={`btn btn-sm ${addItemSubTab === 'head' ? 'btn_primary' : 'btn-light'}`}
                            onClick={() => setAddItemSubTab('head')}
                          >
                            Head
                          </button>
                          <button
                            className={`btn btn-sm ${addItemSubTab === 'subHead' ? 'btn_primary' : 'btn-light'}`}
                            onClick={() => setAddItemSubTab('subHead')}
                          >
                            Sub Head
                          </button>
                          <button
                            className={`btn btn-sm ${addItemSubTab === 'generalItem' ? 'btn_primary' : 'btn-light'}`}
                            onClick={() => setAddItemSubTab('generalItem')}
                          >
                            General Items
                          </button>
                        </div>

                        <div className='btn-group'>
                          <button
                            className={`btn btn-sm ${addItemSubTab === 'accountingCode' ? 'btn_primary' : 'btn-light'}`}
                            onClick={() => setAddItemSubTab('accountingCode')}
                          >
                            Accounting Code
                          </button>
                          <button
                            className={`btn btn-sm ${addItemSubTab === 'subAccounting' ? 'btn_primary' : 'btn-light'}`}
                            onClick={() => setAddItemSubTab('subAccounting')}
                          >
                            Sub Accounting
                          </button>
                        </div>
                      </div>

                      <div className='d-flex justify-content-between align-items-center'>
                        <div className='btn-group'>
                          <button
                            className={`btn btn-sm ${addItemSubTab === 'equipment' ? 'btn_primary' : 'btn-light'}`}
                            onClick={() => setAddItemSubTab('equipment')}
                          >
                            Equipment
                          </button>
                          <button
                            className={`btn btn-sm ${addItemSubTab === 'subComponent' ? 'btn_primary' : 'btn-light'}`}
                            onClick={() => setAddItemSubTab('subComponent')}
                          >
                            Sub Component
                          </button>
                          <button
                            className={`btn btn-sm ${addItemSubTab === 'part' ? 'btn_primary' : 'btn-light'}`}
                            onClick={() => setAddItemSubTab('part')}
                          >
                            Part
                          </button>
                        </div>

                        {isGlobalScopeUser && addItemSubTab !== 'generalItem' && (
                          <div className='d-flex align-items-center gap-2'>
                            <SearchableSelect
                              className='form-select form-select-sm'
                              value={
                                addItemSubTab === 'category' ? categoryCompanyFilter :
                                  addItemSubTab === 'accountingCode' ? accountingCodeCompanyFilter :
                                    addItemSubTab === 'subAccounting' ? subAccountingCompanyFilter :
                                        addItemSubTab === 'subCatalogue' ? subCatalogueCompanyFilter :
                                          addItemSubTab === 'part' ? partCompanyFilter :
                                            addItemSubTab === 'equipment' ? equipmentCompanyFilter :
                                              subComponentCompanyFilter
                              }
                              onChange={(e) => {
                                if (addItemSubTab === 'category') {
                                  setCategoryCompanyFilter(e.target.value)
                                  setCategoryCurrentPage(1)
                                } else if (addItemSubTab === 'accountingCode') {
                                  setAccountingCodeCompanyFilter(e.target.value)
                                  setAccountingCodeCurrentPage(1)
                                } else if (addItemSubTab === 'subAccounting') {
                                  setSubAccountingCompanyFilter(e.target.value)
                                  setSubAccountingCurrentPage(1)
                                } else if (addItemSubTab === 'subCatalogue') {
                                  setSubCatalogueCompanyFilter(e.target.value)
                                  setSubCatalogueCurrentPage(1)
                                } else if (addItemSubTab === 'part') {
                                  setPartCompanyFilter(e.target.value)
                                  setPartCurrentPage(1)
                                } else if (addItemSubTab === 'equipment') {
                                  setEquipmentCompanyFilter(e.target.value)
                                } else if (addItemSubTab === 'subComponent') {
                                  setSubComponentCompanyFilter(e.target.value)
                                  setSubComponentCurrentPage(1)
                                }
                              }}
                              disabled={isLoadingCompanies}
                              style={{
                                width: '180px',
                                border: '1px solid #E4E6EF',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                fontSize: '13px'
                              }}
                            >
                              <option value=''>All Companies</option>
                              {companiesForFilter.map(company => (
                                <option key={company.id} value={company.id}>{company.name}</option>
                              ))}
                            </SearchableSelect>
                          </div>
                        )}
                    </div>
                  </div>
                    )}

                    {!showLegacyInventoryTabs && showGeneralItemLibraryContent && (
                      <div className='d-flex justify-content-between align-items-center mb-3'>
                        <h5 className='fw-bold mb-0 text-dark'>General Items Library</h5>
                        <button
                          className='btn btn-light btn-sm'
                          onClick={() => {
                            setGeneralItemsLibraryOpen(false)
                            setActiveTab('catalogue')
                          }}
                        >
                          Close
                        </button>
                      </div>
                    )}

                    {/* Category Sub-tab */}
                    {showLegacyInventoryTabs && addItemSubTab === 'category' && (
                      <div className='report-table table-responsive'>
                        <div style={{ overflowX: 'auto' }}>
                          <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead className='table-header text-start'>
                              <tr>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '90px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>Sr. No.</span>
                                  </div>
                                </th>
                                <th onClick={() => handleCategorySort('name')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>CATEGORY NAME</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${categorySortConfig.key === 'name' ? categorySortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleCategorySort('company')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>COMPANY</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${categorySortConfig.key === 'company' ? categorySortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleCategorySort('scope')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>SCOPE</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${categorySortConfig.key === 'scope' ? categorySortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th className='align-middle text-center' style={{ padding: '12px 16px', width: '120px' }}>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ACTIONS</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className='table-body text-start'>
                              {(categoryLoading) ? (
                                <TableSkeleton rows={8} cols={COLS.category} />
                              ) : (categoryCurrentRecords.length === 0 ? (
                                <tr>
                                  <td colSpan={COLS.category} className='text-center text-muted py-5'>
                                    No categories found for the selected criteria.
                                  </td>
                                </tr>
                              ) : (
                                categoryCurrentRecords.map((category, index) => (
                                  <tr key={category.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                    <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {categoryIndexOfFirstRecord + index + 1}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500 }}>
                                      {category.name}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {category.cgaid?.name || 'N/A'}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      Company-wide
                                    </td>
                                    <td className='text-center' style={{ padding: '12px 16px' }}>
                                      <div className='d-flex justify-content-center align-items-center gap-1'>
                                        {canUpdateCategoryLibrary && (
                                        <button
                                          className='btn btn-sm p-1'
                                          title='Edit'
                                          style={{ width: '28px', height: '28px' }}
                                          onClick={() => handleEditCategory(category)}
                                        >
                                          <KTSVG path='/media/map/edit-active.svg' className='svg-icon-5' />
                                        </button>
                                        )}
                                        {canDeleteCategoryLibrary && (
                                        <button
                                          className='btn btn-sm p-1'
                                          title='Delete'
                                          style={{ width: '28px', height: '28px' }}
                                          onClick={() => handleDeleteCategory(category.id)}
                                        >
                                          <KTSVG path='/media/map/trash.svg' className='svg-icon-5' />
                                        </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ))}

                            </tbody>
                          </table>
                        </div>

                        <div
                          className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                          style={{
                            position: 'static',
                            bottom: 0,
                            backgroundColor: '#fff',
                            zIndex: 10,
                            borderTop: '1px solid #dee2e6',
                            marginTop: 'auto'
                          }}
                        >
                          <div className='d-flex align-items-center'>
                            <span className='text-muted me-2'>Rows per page</span>
                            <SearchableSelect
                              className='form-select'
                              style={{
                                borderRadius: '20px',
                                width: '70px',
                                border: '1px solid #dee2e6',
                                fontSize: '14px',
                                padding: '4px 8px'
                              }}
                              value={categoryRowsPerPage}
                              onChange={handleCategoryRowsPerPageChange}
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </SearchableSelect>
                          </div>
                          <div className='d-flex align-items-center'>
                            <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                              Showing <strong>{categoryCurrentRecords.length > 0 ? ((categoryCurrentPage - 1) * categoryRowsPerPage) + 1 : 0}-{Math.min(categoryCurrentPage * categoryRowsPerPage, sortedCategoryData.length)}</strong> of <strong>{sortedCategoryData.length}</strong>
                            </span>

                            <nav>
                              <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                                <li className={`page-item ${categoryCurrentPage === 1 ? 'disabled' : ''}`}>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handleCategoryPageChange(categoryCurrentPage - 1)}
                                    disabled={categoryCurrentPage === 1}
                                  >
                                    {'<'}
                                  </button>
                                </li>
                                {(() => {
                                  const pages = []
                                  const showPages = 5
                                  let startPage = Math.max(1, categoryCurrentPage - 2)
                                  let endPage = Math.min(categoryTotalPages, startPage + showPages - 1)

                                  if (endPage - startPage < showPages - 1) {
                                    startPage = Math.max(1, endPage - showPages + 1)
                                  }

                                  for (let i = startPage; i <= endPage; i++) {
                                    pages.push(
                                      <li key={i} className={`page-item ${categoryCurrentPage === i ? 'active' : ''}`}>
                                        <button
                                          className='page-link text-muted'
                                          style={{
                                            backgroundColor: categoryCurrentPage === i ? '#F4F9FF' : 'transparent',
                                            color: categoryCurrentPage === i ? '#1B84FF' : '#7E8299',
                                            border: '1px solid #dee2e6',
                                            padding: '8px 12px',
                                            fontSize: '14px',
                                            borderRadius: '6px'
                                          }}
                                          onClick={() => handleCategoryPageChange(i)}
                                        >
                                          {i}
                                        </button>
                                      </li>
                                    )
                                  }
                                  return pages
                                })()}
                                <li className={`page-item ${categoryCurrentPage === categoryTotalPages || categoryTotalPages === 0 ? 'disabled' : ''}`}>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handleCategoryPageChange(categoryCurrentPage + 1)}
                                    disabled={categoryCurrentPage === categoryTotalPages || categoryTotalPages === 0}
                                  >
                                    {'>'}
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Head Sub-tab */}
                    {showLegacyInventoryTabs && addItemSubTab === 'head' && (
                      <div className='report-table table-responsive'>
                        <div style={{ overflowX: 'auto' }}>
                          <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead className='table-header text-start'>
                              <tr>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '90px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>Sr. No.</span>
                                  </div>
                                </th>
                                <th onClick={() => handleHeadSort('name')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '300px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>HEAD NAME</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${headSortConfig.key === 'name' ? headSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleHeadSort('inventoryItemCategoryName')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '300px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>LEGACY CATEGORY</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${headSortConfig.key === 'inventoryItemCategoryName' ? headSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleHeadSort('company')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '220px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>COMPANY</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${headSortConfig.key === 'company' ? headSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleHeadSort('vesselType')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '220px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>VESSEL</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${headSortConfig.key === 'vesselType' ? headSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th className='align-middle text-center' style={{ padding: '12px 16px', width: '120px' }}>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ACTIONS</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className='table-body text-start'>
                              {headLoading ? (
                                <TableSkeleton rows={8} cols={COLS.head} />
                              ) : (headCurrentRecords.length === 0 ? (
                                <tr>
                                  <td colSpan={COLS.head} className='text-center text-muted py-5'>
                                    No heads found.
                                  </td>
                                </tr>
                              ) : (
                                headCurrentRecords.map((head, index) => (
                                  <tr key={head.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                    <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {headIndexOfFirstRecord + index + 1}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500 }}>
                                      {head.name}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {head.inventoryItemCategoryName || 'N/A'}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {head.cgaid?.name || 'N/A'}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {head.vesselName || (typeof head.vesselType === 'string'
                                        ? head.vesselType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                        : head.vesselType?.fleet_name || 'N/A'
                                      )}
                                    </td>
                                    <td className='text-center' style={{ padding: '12px 16px' }}>
                                      <div className='d-flex justify-content-center align-items-center gap-1'>
                                        <button
                                          className='btn btn-sm p-1'
                                          title='Edit'
                                          style={{ width: '28px', height: '28px' }}
                                          onClick={() => handleEditHead(head)}
                                        >
                                          <KTSVG path='/media/map/edit-active.svg' className='svg-icon-5' />
                                        </button>
                                        <button
                                          className='btn btn-sm p-1'
                                          title='Delete'
                                          style={{ width: '28px', height: '28px' }}
                                          onClick={() => handleDeleteHead(head.id)}
                                        >
                                          <KTSVG path='/media/map/trash.svg' className='svg-icon-5' />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div
                          className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                          style={{
                            position: 'static',
                            bottom: 0,
                            backgroundColor: '#fff',
                            zIndex: 10,
                            borderTop: '1px solid #dee2e6',
                            marginTop: 'auto'
                          }}
                        >
                          <div className='d-flex align-items-center'>
                            <span className='text-muted me-2'>Rows per page</span>
                            <SearchableSelect
                              className='form-select'
                              style={{
                                borderRadius: '20px',
                                width: '70px',
                                border: '1px solid #dee2e6',
                                fontSize: '14px',
                                padding: '4px 8px'
                              }}
                              value={headRowsPerPage}
                              onChange={handleHeadRowsPerPageChange}
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </SearchableSelect>
                          </div>
                          <div className='d-flex align-items-center'>
                            <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                              Showing <strong>{headCurrentRecords.length > 0 ? ((headCurrentPage - 1) * headRowsPerPage) + 1 : 0}-{Math.min(headCurrentPage * headRowsPerPage, headData.length)}</strong> of <strong>{headData.length}</strong>
                            </span>

                            <nav>
                              <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                                <li className={`page-item ${headCurrentPage === 1 ? 'disabled' : ''}`}>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handleHeadPageChange(headCurrentPage - 1)}
                                    disabled={headCurrentPage === 1}
                                  >
                                    {'<'}
                                  </button>
                                </li>
                                {(() => {
                                  const pages = []
                                  const showPages = 5
                                  let startPage = Math.max(1, headCurrentPage - 2)
                                  let endPage = Math.min(headTotalPages, startPage + showPages - 1)

                                  if (endPage - startPage < showPages - 1) {
                                    startPage = Math.max(1, endPage - showPages + 1)
                                  }

                                  for (let i = startPage; i <= endPage; i++) {
                                    pages.push(
                                      <li key={i} className={`page-item ${headCurrentPage === i ? 'active' : ''}`}>
                                        <button
                                          className='page-link text-muted'
                                          style={{
                                            backgroundColor: headCurrentPage === i ? '#F4F9FF' : 'transparent',
                                            color: headCurrentPage === i ? '#1B84FF' : '#7E8299',
                                            border: '1px solid #dee2e6',
                                            padding: '8px 12px',
                                            fontSize: '14px',
                                            borderRadius: '6px'
                                          }}
                                          onClick={() => handleHeadPageChange(i)}
                                        >
                                          {i}
                                        </button>
                                      </li>
                                    )
                                  }
                                  return pages
                                })()}
                                <li className={`page-item ${headCurrentPage === headTotalPages || headTotalPages === 0 ? 'disabled' : ''}`}>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handleHeadPageChange(headCurrentPage + 1)}
                                    disabled={headCurrentPage === headTotalPages || headTotalPages === 0}
                                  >
                                    {'>'}
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sub Head Sub-tab */}
                    {showLegacyInventoryTabs && addItemSubTab === 'subHead' && (
                      <div className='report-table table-responsive'>
                        <div style={{ overflowX: 'auto' }}>
                          <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead className='table-header text-start'>
                              <tr>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '90px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>Sr. No.</span>
                                  </div>
                                </th>
                                <th onClick={() => handleSubHeadSort('name')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '300px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>SUB HEAD NAME</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${subHeadSortConfig.key === 'name' ? subHeadSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleSubHeadSort('inventoryItemHeadName')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '300px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>HEAD</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${subHeadSortConfig.key === 'inventoryItemHeadName' ? subHeadSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleSubHeadSort('company')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '220px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>COMPANY</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${subHeadSortConfig.key === 'company' ? subHeadSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th onClick={() => handleSubHeadSort('vesselType')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '220px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>VESSEL</span>
                                    <div style={{ transform: 'translateY(-2px)' }}>
                                      <KTSVG
                                        path={`/media/map/sort-col-${subHeadSortConfig.key === 'vesselType' ? subHeadSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                        className='svg-icon ms-2 custom-sort-icon'
                                      />
                                    </div>
                                  </div>
                                </th>
                                <th className='align-middle text-center' style={{ padding: '12px 16px', width: '120px' }}>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ACTIONS</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className='table-body text-start'>
                              {subHeadLoading ? (
                                <TableSkeleton rows={8} cols={COLS.subHead} />
                              ) : (subHeadCurrentRecords.length === 0 ? (
                                <tr>
                                  <td colSpan={COLS.subHead} className='text-center text-muted py-5'>
                                    No sub heads found.
                                  </td>
                                </tr>
                              ) : (
                                subHeadCurrentRecords.map((subHead, index) => (
                                  <tr key={subHead.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                    <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {subHeadIndexOfFirstRecord + index + 1}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500 }}>
                                      {subHead.name}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {subHead.inventoryItemHeadName || 'N/A'}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {subHead.cgaid?.name || 'N/A'}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {subHead.vesselName || (typeof subHead.vesselType === 'string'
                                        ? subHead.vesselType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                        : subHead.vesselType?.fleet_name || 'N/A'
                                      )}
                                    </td>
                                    <td className='text-center' style={{ padding: '12px 16px' }}>
                                      <div className='d-flex justify-content-center align-items-center gap-1'>
                                        <button
                                          className='btn btn-sm p-1'
                                          title='Edit'
                                          style={{ width: '28px', height: '28px' }}
                                          onClick={() => handleEditSubHead(subHead)}
                                        >
                                          <KTSVG path='/media/map/edit-active.svg' className='svg-icon-5' />
                                        </button>
                                        <button
                                          className='btn btn-sm p-1'
                                          title='Delete'
                                          style={{ width: '28px', height: '28px' }}
                                          onClick={() => handleDeleteSubHead(subHead.id)}
                                        >
                                          <KTSVG path='/media/map/trash.svg' className='svg-icon-5' />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div
                          className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                          style={{
                            position: 'static',
                            bottom: 0,
                            backgroundColor: '#fff',
                            zIndex: 10,
                            borderTop: '1px solid #dee2e6',
                            marginTop: 'auto'
                          }}
                        >
                          <div className='d-flex align-items-center'>
                            <span className='text-muted me-2'>Rows per page</span>
                            <SearchableSelect
                              className='form-select'
                              style={{
                                borderRadius: '20px',
                                width: '70px',
                                border: '1px solid #dee2e6',
                                fontSize: '14px',
                                padding: '4px 8px'
                              }}
                              value={subHeadRowsPerPage}
                              onChange={handleSubHeadRowsPerPageChange}
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </SearchableSelect>
                          </div>
                          <div className='d-flex align-items-center'>
                            <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                              Showing <strong>{subHeadCurrentRecords.length > 0 ? ((subHeadCurrentPage - 1) * subHeadRowsPerPage) + 1 : 0}-{Math.min(subHeadCurrentPage * subHeadRowsPerPage, subHeadData.length)}</strong> of <strong>{subHeadData.length}</strong>
                            </span>

                            <nav>
                              <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                                <li className={`page-item ${subHeadCurrentPage === 1 ? 'disabled' : ''}`}>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handleSubHeadPageChange(subHeadCurrentPage - 1)}
                                    disabled={subHeadCurrentPage === 1}
                                  >
                                    {'<'}
                                  </button>
                                </li>
                                {(() => {
                                  const pages = []
                                  const showPages = 5
                                  let startPage = Math.max(1, subHeadCurrentPage - 2)
                                  let endPage = Math.min(subHeadTotalPages, startPage + showPages - 1)

                                  if (endPage - startPage < showPages - 1) {
                                    startPage = Math.max(1, endPage - showPages + 1)
                                  }

                                  for (let i = startPage; i <= endPage; i++) {
                                    pages.push(
                                      <li key={i} className={`page-item ${subHeadCurrentPage === i ? 'active' : ''}`}>
                                        <button
                                          className='page-link text-muted'
                                          style={{
                                            backgroundColor: subHeadCurrentPage === i ? '#F4F9FF' : 'transparent',
                                            color: subHeadCurrentPage === i ? '#1B84FF' : '#7E8299',
                                            border: '1px solid #dee2e6',
                                            padding: '8px 12px',
                                            fontSize: '14px',
                                            borderRadius: '6px'
                                          }}
                                          onClick={() => handleSubHeadPageChange(i)}
                                        >
                                          {i}
                                        </button>
                                      </li>
                                    )
                                  }
                                  return pages
                                })()}
                                <li className={`page-item ${subHeadCurrentPage === subHeadTotalPages || subHeadTotalPages === 0 ? 'disabled' : ''}`}>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handleSubHeadPageChange(subHeadCurrentPage + 1)}
                                    disabled={subHeadCurrentPage === subHeadTotalPages || subHeadTotalPages === 0}
                                  >
                                    {'>'}
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* General Items table moved to modal to keep Inventory page clean */}

                    {showLegacyInventoryTabs && addItemSubTab === 'equipment' && (
                      <div className='report-table table-responsive'>
                        <div style={{ overflowX: 'auto' }}>
                          <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead className='table-header text-start'>
                              <tr>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '90px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>Sr. No.</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '220px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>EQUIPMENT NAME</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '160px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>CODE</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '140px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>SHORT FORM</span>
                                  </div>
                                </th>
                                {showEquipmentCompanyCol && (
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>COMPANY</span>
                                    </div>
                                  </th>
                                )}
                                {showEquipmentVesselCol && (
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '220px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>VESSEL</span>
                                    </div>
                                  </th>
                                )}
                                <th className='align-middle' style={{ padding: '12px 16px', width: '130px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>CRITICALITY</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '160px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>MAKER</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '160px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>MODEL</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '160px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>SERIAL NUMBER</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '150px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>INSTALLATION DATE</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '150px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>LOCATION</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '140px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>CLASS CRITICAL</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '150px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>SURVEY RELEVANT</span>
                                  </div>
                                </th>
                                <th className='align-middle' style={{ padding: '12px 16px', width: '220px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>FUNCTION DESCRIPTION</span>
                                  </div>
                                </th>
                                <th className='align-middle text-center' style={{ padding: '12px 16px', width: '140px' }}>
                                  <div className='d-flex align-items-center justify-content-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>Actions</span>
                                  </div>
                                </th>
                              </tr>
                            </thead>
                            <tbody className='table-body text-start'>
                              {(equipmentLoading) ? (
                                <TableSkeleton rows={6} cols={equipmentColumnCount} />
                              ) : (equipmentData.length === 0 ? (
                                <tr>
                                  <td colSpan={equipmentColumnCount} className='text-center text-muted py-5'>
                                    No equipment found for the selected criteria.
                                  </td>
                                </tr>
                              ) : (
                                equipmentData.map((eq, index) => (
                                  <tr key={eq.id ?? index} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                    <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {index + 1}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500 }}>
                                      {eq.name || 'N/A'}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {eq.code || 'N/A'}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {valueOrDash(eq.shortForm)}
                                    </td>
                                    {showEquipmentCompanyCol && (
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(eq.companyName)}
                                      </td>
                                    )}
                                    {showEquipmentVesselCol && (
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(eq.vesselName)}
                                      </td>
                                    )}
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {formatYesNo(eq.criticality as any)}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {valueOrDash(eq.maker)}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {valueOrDash(eq.model)}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {valueOrDash(eq.serialNumber)}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {formatDateSafe(eq.installationDate as any)}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {valueOrDash(eq.location)}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {formatYesNo(eq.isClassCritical as any)}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {formatYesNo(eq.isSurveyRelevant as any)}
                                    </td>
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {valueOrDash((eq as any).equipmentFunctionDescription)}
                                    </td>
                                    <td className='text-center' style={{ padding: '12px 16px' }}>
                                      <button
                                        className='btn btn-sm btn-light-primary'
                                        onClick={() => handleEditEquipment(eq)}
                                        disabled={!eq.id}
                                      >
                                        Edit
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Accounting Code Sub-tab */}
                    {showLegacyInventoryTabs && addItemSubTab === 'accountingCode' && (
                      <>
                        <div className='report-table table-responsive'>
                          <div style={{ overflowX: 'auto' }}>
                            <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                              <thead className='table-header text-start'>
                                <tr>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '90px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>Sr. No.</span>
                                    </div>
                                  </th>
                                  <th onClick={() => handleAccountingCodeSort('accountCodes')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '250px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ACCOUNT CODE</span>
                                      <div style={{ transform: 'translateY(-2px)' }}>
                                        <KTSVG
                                          path={`/media/map/sort-col-${accountingCodeSortConfig.key === 'accountCodes' ? accountingCodeSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                          className='svg-icon ms-2 custom-sort-icon'
                                        />
                                      </div>
                                    </div>
                                  </th>
                                  <th onClick={() => handleAccountingCodeSort('company')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>COMPANY</span>
                                      <div style={{ transform: 'translateY(-2px)' }}>
                                        <KTSVG
                                          path={`/media/map/sort-col-${accountingCodeSortConfig.key === 'company' ? accountingCodeSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                          className='svg-icon ms-2 custom-sort-icon'
                                        />
                                      </div>
                                    </div>
                                  </th>
                                  <th onClick={() => handleAccountingCodeSort('vesselType')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>VESSEL</span>
                                      <div style={{ transform: 'translateY(-2px)' }}>
                                        <KTSVG
                                          path={`/media/map/sort-col-${accountingCodeSortConfig.key === 'vesselType' ? accountingCodeSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                          className='svg-icon ms-2 custom-sort-icon'
                                        />
                                      </div>
                                    </div>
                                  </th>
                                  <th className='align-middle text-center' style={{ padding: '12px 16px', width: '120px' }}>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ACTIONS</span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody className='table-body text-start'>
                                {(accountingLoading) ? (
                                  <TableSkeleton rows={8} cols={COLS.accounting} />
                                ) : (accountingCodeCurrentRecords.length === 0 ? (
                                  <tr>
                                    <td colSpan={COLS.accounting} className='text-center text-muted py-5'>
                                      No accounting codes found for the selected criteria.
                                    </td>
                                  </tr>
                                ) : (
                                  accountingCodeCurrentRecords.map((account, index) => (
                                    <tr key={account.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                      <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {accountingCodeIndexOfFirstRecord + index + 1}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500 }}>
                                        {account.accountCodes}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {(account.cgaid as any)?.name || 'N/A'}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {account.vesselName || (account.vesselType ? account.vesselType.replace(/_/g, ' ') : 'N/A')}
                                      </td>
                                      <td className='text-center' style={{ padding: '12px 16px' }}>
                                        <div className='d-flex justify-content-center align-items-center gap-1'>
                                          <button
                                            className='btn btn-sm p-1'
                                            title='Edit'
                                            style={{ width: '28px', height: '28px' }}
                                            onClick={() => handleEditAccountingCode(account)}
                                          >
                                            <KTSVG path='/media/map/edit-active.svg' className='svg-icon-5' />
                                          </button>
                                          <button
                                            className='btn btn-sm p-1'
                                            title='Delete'
                                            style={{ width: '28px', height: '28px' }}
                                            onClick={() => handleDeleteAccountingCode(account.id)}
                                          >
                                            <KTSVG path='/media/map/trash.svg' className='svg-icon-5' />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div
                            className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                            style={{
                              position: 'static',
                              bottom: 0,
                              backgroundColor: '#fff',
                              zIndex: 10,
                              borderTop: '1px solid #dee2e6',
                              marginTop: 'auto'
                            }}
                          >
                            <div className='d-flex align-items-center'>
                              <span className='text-muted me-2'>Rows per page</span>
                              <SearchableSelect
                                className='form-select'
                                style={{
                                  borderRadius: '20px',
                                  width: '70px',
                                  border: '1px solid #dee2e6',
                                  fontSize: '14px',
                                  padding: '4px 8px'
                                }}
                                value={accountingCodeRowsPerPage}
                                onChange={handleAccountingCodeRowsPerPageChange}
                              >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                              </SearchableSelect>
                            </div>
                            <div className='d-flex align-items-center'>
                              <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                                Showing <strong>{accountingCodeCurrentRecords.length > 0 ? ((accountingCodeCurrentPage - 1) * accountingCodeRowsPerPage) + 1 : 0}-{Math.min(accountingCodeCurrentPage * accountingCodeRowsPerPage, accountingCodeData.length)}</strong> of <strong>{accountingCodeData.length}</strong>
                              </span>

                              <nav>
                                <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                                  <li className={`page-item ${accountingCodeCurrentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handleAccountingCodePageChange(accountingCodeCurrentPage - 1)}
                                      disabled={accountingCodeCurrentPage === 1}
                                    >
                                      {'<'}
                                    </button>
                                  </li>
                                  {(() => {
                                    const pages = []
                                    const showPages = 5
                                    let startPage = Math.max(1, accountingCodeCurrentPage - 2)
                                    let endPage = Math.min(accountingCodeTotalPages, startPage + showPages - 1)

                                    if (endPage - startPage < showPages - 1) {
                                      startPage = Math.max(1, endPage - showPages + 1)
                                    }

                                    for (let i = startPage; i <= endPage; i++) {
                                      pages.push(
                                        <li key={i} className={`page-item ${accountingCodeCurrentPage === i ? 'active' : ''}`}>
                                          <button
                                            className='page-link text-muted'
                                            style={{
                                              backgroundColor: accountingCodeCurrentPage === i ? '#F4F9FF' : 'transparent',
                                              color: accountingCodeCurrentPage === i ? '#1B84FF' : '#7E8299',
                                              border: '1px solid #dee2e6',
                                              padding: '8px 12px',
                                              fontSize: '14px',
                                              borderRadius: '6px'
                                            }}
                                            onClick={() => handleAccountingCodePageChange(i)}
                                          >
                                            {i}
                                          </button>
                                        </li>
                                      )
                                    }
                                    return pages
                                  })()}
                                  <li className={`page-item ${accountingCodeCurrentPage === accountingCodeTotalPages || accountingCodeTotalPages === 0 ? 'disabled' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handleAccountingCodePageChange(accountingCodeCurrentPage + 1)}
                                      disabled={accountingCodeCurrentPage === accountingCodeTotalPages || accountingCodeTotalPages === 0}
                                    >
                                      {'>'}
                                    </button>
                                  </li>
                                </ul>
                              </nav>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Sub Accounting Sub-tab */}
                    {showLegacyInventoryTabs && addItemSubTab === 'subAccounting' && (
                      <>
                        <div className='report-table table-responsive'>
                          <div style={{ overflowX: 'auto' }}>
                            <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                              <thead className='table-header text-start'>
                                <tr>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '90px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>Sr. No.</span>
                                    </div>
                                  </th>
                                  <th onClick={() => handleSubAccountingSort('subaccountCodes')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>SUBACCOUNT CODE</span>
                                      <div style={{ transform: 'translateY(-2px)' }}>
                                        <KTSVG
                                          path={`/media/map/sort-col-${subAccountingSortConfig.key === 'subaccountCodes' ? subAccountingSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                          className='svg-icon ms-2 custom-sort-icon'
                                        />
                                      </div>
                                    </div>
                                  </th>
                                  <th onClick={() => handleSubAccountingSort('accountCode')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ACCOUNT CODE</span>
                                      <div style={{ transform: 'translateY(-2px)' }}>
                                        <KTSVG
                                          path={`/media/map/sort-col-${subAccountingSortConfig.key === 'accountCode' ? subAccountingSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                          className='svg-icon ms-2 custom-sort-icon'
                                        />
                                      </div>
                                    </div>
                                  </th>
                                  <th onClick={() => handleSubAccountingSort('company')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>COMPANY</span>
                                      <div style={{ transform: 'translateY(-2px)' }}>
                                        <KTSVG
                                          path={`/media/map/sort-col-${subAccountingSortConfig.key === 'company' ? subAccountingSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                          className='svg-icon ms-2 custom-sort-icon'
                                        />
                                      </div>
                                    </div>
                                  </th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>VESSEL</span>
                                  </th>
                                  <th className='align-middle text-center' style={{ padding: '12px 16px', width: '120px' }}>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ACTIONS</span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody className='table-body text-start'>
                                {(subAccountingLoading) ? (
                                  <TableSkeleton rows={8} cols={COLS.subAccounting} />
                                ) : (subAccountingCurrentRecords.length === 0 ? (
                                  <tr>
                                    <td colSpan={COLS.subAccounting} className='text-center text-muted py-5'>
                                      No sub accounting codes found for the selected criteria.
                                    </td>
                                  </tr>
                                ) : (
                                  subAccountingCurrentRecords.map((subAccount, index) => (
                                    <tr key={subAccount.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                      <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {subAccountingIndexOfFirstRecord + index + 1}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500 }}>
                                        {subAccount.subaccountCodes}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {subAccount.accountCode || 'N/A'}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {subAccount.cgaid?.name || 'N/A'}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {(subAccount as any).vesselName || (subAccount as any).vesselType || 'N/A'}
                                      </td>
                                      <td className='text-center' style={{ padding: '12px 16px' }}>
                                        <div className='d-flex justify-content-center align-items-center gap-1'>
                                          <button
                                            className='btn btn-sm p-1'
                                            title='Edit'
                                            style={{ width: '28px', height: '28px' }}
                                            onClick={() => handleEditSubAccounting(subAccount)}
                                          >
                                            <KTSVG path='/media/map/edit-active.svg' className='svg-icon-5' />
                                          </button>
                                          <button
                                            className='btn btn-sm p-1'
                                            title='Delete'
                                            style={{ width: '28px', height: '28px' }}
                                            onClick={() => handleDeleteSubAccount(subAccount.id)}
                                          >
                                            <KTSVG path='/media/map/trash.svg' className='svg-icon-5' />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div
                            className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                            style={{
                              position: 'static',
                              bottom: 0,
                              backgroundColor: '#fff',
                              zIndex: 10,
                              borderTop: '1px solid #dee2e6',
                              marginTop: 'auto'
                            }}
                          >
                            <div className='d-flex align-items-center'>
                              <span className='text-muted me-2'>Rows per page</span>
                              <SearchableSelect
                                className='form-select'
                                style={{
                                  borderRadius: '20px',
                                  width: '70px',
                                  border: '1px solid #dee2e6',
                                  fontSize: '14px',
                                  padding: '4px 8px'
                                }}
                                value={subAccountingRowsPerPage}
                                onChange={handleSubAccountingRowsPerPageChange}
                              >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                              </SearchableSelect>
                            </div>
                            <div className='d-flex align-items-center'>
                              <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                                Showing <strong>{subAccountingCurrentRecords.length > 0 ? ((subAccountingCurrentPage - 1) * subAccountingRowsPerPage) + 1 : 0}-{Math.min(subAccountingCurrentPage * subAccountingRowsPerPage, subAccountingData.length)}</strong> of <strong>{subAccountingData.length}</strong>
                              </span>

                              <nav>
                                <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                                  <li className={`page-item ${subAccountingCurrentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handleSubAccountingPageChange(subAccountingCurrentPage - 1)}
                                      disabled={subAccountingCurrentPage === 1}
                                    >
                                      {'<'}
                                    </button>
                                  </li>
                                  {(() => {
                                    const pages = []
                                    const showPages = 5
                                    let startPage = Math.max(1, subAccountingCurrentPage - 2)
                                    let endPage = Math.min(subAccountingTotalPages, startPage + showPages - 1)

                                    if (endPage - startPage < showPages - 1) {
                                      startPage = Math.max(1, endPage - showPages + 1)
                                    }

                                    for (let i = startPage; i <= endPage; i++) {
                                      pages.push(
                                        <li key={i} className={`page-item ${subAccountingCurrentPage === i ? 'active' : ''}`}>
                                          <button
                                            className='page-link text-muted'
                                            style={{
                                              backgroundColor: subAccountingCurrentPage === i ? '#F4F9FF' : 'transparent',
                                              color: subAccountingCurrentPage === i ? '#1B84FF' : '#7E8299',
                                              border: '1px solid #dee2e6',
                                              padding: '8px 12px',
                                              fontSize: '14px',
                                              borderRadius: '6px'
                                            }}
                                            onClick={() => handleSubAccountingPageChange(i)}
                                          >
                                            {i}
                                          </button>
                                        </li>
                                      )
                                    }
                                    return pages
                                  })()}
                                  <li className={`page-item ${subAccountingCurrentPage === subAccountingTotalPages || subAccountingTotalPages === 0 ? 'disabled' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handleSubAccountingPageChange(subAccountingCurrentPage + 1)}
                                      disabled={subAccountingCurrentPage === subAccountingTotalPages || subAccountingTotalPages === 0}
                                    >
                                      {'>'}
                                    </button>
                                  </li>
                                </ul>
                              </nav>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Sub Catalogue Sub-tab - COMMENTED OUT */}
                    {/* {addItemSubTab === 'subCatalogue' && (
                      <>
                        <div className='report-table table-responsive'>
                          <div style={{ overflowX: 'auto' }}>
                            <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                              <thead className='table-header text-start'>
                                <tr>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '90px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>Sr. No.</span>
                                    </div>
                                  </th>
                                  <th onClick={() => handleSubCatalogueSort('name')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>SUB CATALOGUE NAME</span>
                                      <div style={{ transform: 'translateY(-2px)' }}>
                                        <KTSVG
                                          path={`/media/map/sort-col-${subCatalogueSortConfig.key === 'name' ? subCatalogueSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                          className='svg-icon ms-2 custom-sort-icon'
                                        />
                                      </div>
                                    </div>
                                  </th>
                                  <th onClick={() => handleSubCatalogueSort('company')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>COMPANY</span>
                                      <div style={{ transform: 'translateY(-2px)' }}>
                                        <KTSVG
                                          path={`/media/map/sort-col-${subCatalogueSortConfig.key === 'company' ? subCatalogueSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                          className='svg-icon ms-2 custom-sort-icon'
                                        />
                                      </div>
                                    </div>
                                  </th>
                                  <th onClick={() => handleSubCatalogueSort('vesselType')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>VESSEL</span>
                                      <div style={{ transform: 'translateY(-2px)' }}>
                                        <KTSVG
                                          path={`/media/map/sort-col-${subCatalogueSortConfig.key === 'vesselType' ? subCatalogueSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                          className='svg-icon ms-2 custom-sort-icon'
                                        />
                                      </div>
                                    </div>
                                  </th>
                                  <th className='align-middle text-center' style={{ padding: '12px 16px', width: '120px' }}>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ACTIONS</span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody className='table-body text-start'>
                                {(subCatalogueLoading) ? (
                                  <TableSkeleton rows={8} cols={COLS.subCatalogue} />
                                ) : (subCatalogueCurrentRecords.length === 0 ? (
                                  <tr>
                                    <td colSpan={COLS.subCatalogue} className='text-center text-muted py-5'>
                                      No sub catalogues found for the selected criteria.
                                    </td>
                                  </tr>
                                ) : (
                                  subCatalogueCurrentRecords.map((subCatalogue, index) => (
                                    <tr key={subCatalogue.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                      <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {subCatalogueIndexOfFirstRecord + index + 1}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500 }}>
                                        {subCatalogue.name}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {subCatalogue.cgaid?.name || subCatalogue.subCatalogueType?.name || 'N/A'}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {subCatalogue.vesselName || subCatalogue.vesselType || 'N/A'}
                                      </td>
                                      <td className='text-center' style={{ padding: '12px 16px' }}>
                                        <div className='d-flex justify-content-center align-items-center gap-1'>
                                          <button
                                            className='btn btn-sm p-1'
                                            title='Edit'
                                            style={{ width: '28px', height: '28px' }}
                                            onClick={() => handleEditSubCatalogue(subCatalogue)}
                                          >
                                            <KTSVG path='/media/map/edit-active.svg' className='svg-icon-5' />
                                          </button>
                                          <button
                                            className='btn btn-sm p-1'
                                            title='Delete'
                                            style={{ width: '28px', height: '28px' }}
                                            onClick={() => handleDeleteSubCatalogue(subCatalogue.id)}
                                          >
                                            <KTSVG path='/media/map/trash.svg' className='svg-icon-5' />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div
                            className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                            style={{
                              position: 'static',
                              bottom: 0,
                              backgroundColor: '#fff',
                              zIndex: 10,
                              borderTop: '1px solid #dee2e6',
                              marginTop: 'auto'
                            }}
                          >
                            <div className='d-flex align-items-center'>
                              <span className='text-muted me-2'>Rows per page</span>
                              <SearchableSelect
                                className='form-select'
                                style={{
                                  borderRadius: '20px',
                                  width: '70px',
                                  border: '1px solid #dee2e6',
                                  fontSize: '14px',
                                  padding: '4px 8px'
                                }}
                                value={subCatalogueRowsPerPage}
                                onChange={handleSubCatalogueRowsPerPageChange}
                              >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                              </SearchableSelect>
                            </div>
                            <div className='d-flex align-items-center'>
                              <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                                Showing <strong>{subCatalogueCurrentRecords.length > 0 ? ((subCatalogueCurrentPage - 1) * subCatalogueRowsPerPage) + 1 : 0}-{Math.min(subCatalogueCurrentPage * subCatalogueRowsPerPage, subCatalogueData.length)}</strong> of <strong>{subCatalogueData.length}</strong>
                              </span>

                              <nav>
                                <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                                  <li className={`page-item ${subCatalogueCurrentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handleSubCataloguePageChange(subCatalogueCurrentPage - 1)}
                                      disabled={subCatalogueCurrentPage === 1}
                                    >
                                      {'<'}
                                    </button>
                                    const showPages = 5
                                    let startPage = Math.max(1, subCatalogueCurrentPage - 2)
                                    let endPage = Math.min(subCatalogueTotalPages, startPage + showPages - 1)

                                    if (endPage - startPage < showPages - 1) {
                                      startPage = Math.max(1, endPage - showPages + 1)
                                    }

                                    for (let i = startPage; i <= endPage; i++) {
                                      pages.push(
                                        <li key={i} className={`page-item ${subCatalogueCurrentPage === i ? 'active' : ''}`}>
                                          <button
                                            className='page-link text-muted'
                                            style={{
                                              backgroundColor: subCatalogueCurrentPage === i ? '#F4F9FF' : 'transparent',
                                              color: subCatalogueCurrentPage === i ? '#1B84FF' : '#7E8299',
                                              border: '1px solid #dee2e6',
                                              padding: '8px 12px',
                                              fontSize: '14px',
                                              borderRadius: '6px'
                                            }}
                                            onClick={() => handleSubCataloguePageChange(i)}
                                          >
                                            {i}
                                          </button>
                                        </li>
                                      )
                                    }
                                    return pages
                                  })()}
                                  <li className={`page-item ${subCatalogueCurrentPage === subCatalogueTotalPages || subCatalogueTotalPages === 0 ? 'disabled' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handleSubCataloguePageChange(subCatalogueCurrentPage + 1)}
                                      disabled={subCatalogueCurrentPage === subCatalogueTotalPages || subCatalogueTotalPages === 0}
                                    >
                                      {'>'}
                                    </button>
                                  </li>
                                </ul>
                            </div>
                    )} */}

                    {/* Part Sub-tab */}
                    {showLegacyInventoryTabs && addItemSubTab === 'part' && (
                      <>
                        <div className='report-table table-responsive'>
                          <div style={{ overflowX: 'auto' }}>
                            <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                              <thead className='table-header text-start'>
                                <tr>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '70px' }}>Sr. No.</th>
                                  <th onClick={() => handlePartSort('name')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>PART NAME</span>
                                      <KTSVG path={`/media/map/sort-col-${partSortConfig.key === 'name' ? partSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`} className='svg-icon ms-2 custom-sort-icon' />
                                    </div>
                                  </th>
                                  <th onClick={() => handlePartSort('code')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '170px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>PART CODE</span>
                                      <KTSVG path={`/media/map/sort-col-${partSortConfig.key === 'code' ? partSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`} className='svg-icon ms-2 custom-sort-icon' />
                                    </div>
                                  </th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '200px' }}>SUB COMPONENT NAME</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '170px' }}>SUB COMPONENT CODE</th>
                                  {showPartCompanyCol && <th className='align-middle' style={{ padding: '12px 16px', width: '180px' }}>COMPANY</th>}
                                  {showPartVesselCol && <th className='align-middle' style={{ padding: '12px 16px', width: '180px' }}>VESSEL</th>}
                                  <th onClick={() => handlePartSort('manufacturer')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '170px' }}>
                                    <div className='d-flex align-items-center'>
                                      <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>MANUFACTURER</span>
                                      <KTSVG path={`/media/map/sort-col-${partSortConfig.key === 'manufacturer' ? partSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`} className='svg-icon ms-2 custom-sort-icon' />
                                    </div>
                                  </th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '140px' }}>PART NO.</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '140px' }}>SERIAL NUMBER</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '100px' }}>SPARE</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '100px' }}>UOM</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '140px' }}>ITEM CODE</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '180px' }}>SHORT DESCRIPTION</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '200px' }}>LONG DESCRIPTION</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '140px' }}>DRAWING NO.</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '160px' }}>DRAWING POSITION NO.</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', width: '140px' }}>LOCATION</th>
                                  <th className='align-middle text-center' style={{ padding: '12px 16px', width: '120px' }}>ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody className='table-body text-start'>
                                {(partLoading) ? (
                                  <TableSkeleton rows={8} cols={partColumnCount} />
                                ) : (partCurrentRecords.length === 0 ? (
                                  <tr>
                                    <td colSpan={partColumnCount} className='text-center text-muted py-5'>
                                      No parts found for the selected criteria.
                                    </td>
                                  </tr>
                                ) : (
                                  partCurrentRecords.map((part, index) => (
                                    <tr key={part.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {partIndexOfFirstRecord + index + 1}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500 }}>
                                        {valueOrDash(part.name)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.code)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.subcomponent?.name)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash((part.subcomponent as any)?.subComponentCode)}
                                      </td>
                                      {showPartCompanyCol && (
                                        <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                          {valueOrDash((part.vessel as any)?.companyGroupAdmin?.name)}
                                        </td>
                                      )}
                                      {showPartVesselCol && (
                                        <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                          {valueOrDash(part.vessel?.fleet_name)}
                                        </td>
                                      )}
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.manufacturer)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.partNo)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.serialNumber)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {formatYesNo(part.isSpare as any)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.uom?.code || part.uom?.name)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.itemCode)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.itemShortDescription)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.itemLongDescription)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.drawNo)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.drawingPositionNo)}
                                      </td>
                                      <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                        {valueOrDash(part.location)}
                                      </td>
                                      <td className='text-center' style={{ padding: '12px 16px' }}>
                                        <div className='d-flex justify-content-center align-items-center gap-1'>
                                          <button
                                            className='btn btn-sm p-1'
                                            title='Edit'
                                            style={{ width: '28px', height: '28px' }}
                                            onClick={() => handleEditPart(part)}
                                          >
                                            <KTSVG path='/media/map/edit-active.svg' className='svg-icon-5' />
                                          </button>
                                          <button
                                            className='btn btn-sm p-1'
                                            title='Delete'
                                            style={{ width: '28px', height: '28px' }}
                                            onClick={() => handleDeletePart(part.id)}
                                          >
                                            <KTSVG path='/media/map/trash.svg' className='svg-icon-5' />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div
                            className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                            style={{
                              position: 'static',
                              bottom: 0,
                              backgroundColor: '#fff',
                              zIndex: 10,
                              borderTop: '1px solid #dee2e6',
                              marginTop: 'auto'
                            }}
                          >
                            <div className='d-flex align-items-center'>
                              <span className='text-muted me-2'>Rows per page</span>
                              <SearchableSelect
                                className='form-select'
                                style={{
                                  borderRadius: '20px',
                                  width: '70px',
                                  border: '1px solid #dee2e6',
                                  fontSize: '14px',
                                  padding: '4px 8px'
                                }}
                                value={partRowsPerPage}
                                onChange={handlePartRowsPerPageChange}
                              >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                              </SearchableSelect>
                            </div>
                            <div className='d-flex align-items-center'>
                              <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                                Showing <strong>{partCurrentRecords.length > 0 ? ((partCurrentPage - 1) * partRowsPerPage) + 1 : 0}-{Math.min(partCurrentPage * partRowsPerPage, partData.length)}</strong> of <strong>{partData.length}</strong>
                              </span>

                              <nav>
                                <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                                  <li className={`page-item ${partCurrentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        borderRadius: '6px'
                                      }}
                                    onClick={() => handlePartPageChange(partCurrentPage - 1)}
                                    disabled={partCurrentPage === 1}
                                  >
                                      {'<'}
                                    </button>
                                  </li>
                                  {(() => {
                                    const pages = []
                                    const showPages = 5
                                    let startPage = Math.max(1, partCurrentPage - 2)
                                    let endPage = Math.min(partTotalPages, startPage + showPages - 1)

                                    if (endPage - startPage < showPages - 1) {
                                      startPage = Math.max(1, endPage - showPages + 1)
                                    }

                                    for (let i = startPage; i <= endPage; i++) {
                                      pages.push(
                                        <li key={i} className={`page-item ${partCurrentPage === i ? 'active' : ''}`}>
                                          <button
                                            className='page-link text-muted'
                                            style={{
                                              backgroundColor: partCurrentPage === i ? '#F4F9FF' : 'transparent',
                                              color: partCurrentPage === i ? '#1B84FF' : '#7E8299',
                                              border: '1px solid #dee2e6',
                                              padding: '8px 12px',
                                              fontSize: '14px',
                                              borderRadius: '6px'
                                            }}
                                            onClick={() => handlePartPageChange(i)}
                                          >
                                            {i}
                                          </button>
                                        </li>
                                      )
                                    }
                                    return pages
                                  })()}
                                  <li className={`page-item ${partCurrentPage === partTotalPages || partTotalPages === 0 ? 'disabled' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handlePartPageChange(partCurrentPage + 1)}
                                      disabled={partCurrentPage === partTotalPages || partTotalPages === 0}
                                    >
                                      {'>'}
                                    </button>
                                  </li>
                                </ul>
                              </nav>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Sub Component Sub-tab */}
                {showLegacyInventoryTabs && activeTab === 'addItem' && addItemSubTab === 'subComponent' && (
                  <>
                    <div className='report-table table-responsive'>
                      <div style={{ overflowX: 'auto' }}>
                        <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                          <thead className='table-header text-start'>
                            <tr>
                              <th className='align-middle' style={{ padding: '12px 16px', width: '70px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>Sr. No.</span>
                                </div>
                              </th>
                              <th onClick={() => handleSubComponentSort('name')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '220px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>SUB COMPONENT NAME</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${subComponentSortConfig.key === 'name' ? subComponentSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th onClick={() => handleSubComponentSort('subComponentCode')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '170px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>SUB COMPONENT CODE</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${subComponentSortConfig.key === 'subComponentCode' ? subComponentSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th onClick={() => handleSubComponentSort('equipmentName')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>EQUIPMENT NAME</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${subComponentSortConfig.key === 'equipmentName' ? subComponentSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th onClick={() => handleSubComponentSort('equipmentCode')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '170px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>EQUIPMENT CODE</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${subComponentSortConfig.key === 'equipmentCode' ? subComponentSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              {showSubComponentCompanyCol && (
                                <th className='align-middle' style={{ padding: '12px 16px', width: '180px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>COMPANY</span>
                                  </div>
                                </th>
                              )}
                              {showSubComponentVesselCol && (
                                <th className='align-middle' style={{ padding: '12px 16px', width: '180px' }}>
                                  <div className='d-flex align-items-center'>
                                    <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>VESSEL</span>
                                  </div>
                                </th>
                              )}
                              <th className='align-middle' style={{ padding: '12px 16px', width: '160px' }}>
                                <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>MAKER</span>
                              </th>
                              <th onClick={() => handleSubComponentSort('criticality')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '130px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>CRITICALITY</span>
                                  <KTSVG
                                    path={`/media/map/sort-col-${subComponentSortConfig.key === 'criticality' ? subComponentSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 16px', width: '130px' }}>
                                <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>OPEN JOB</span>
                              </th>
                              <th onClick={() => handleSubComponentSort('instanceCount')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '150px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>INSTANCE COUNT</span>
                                  <KTSVG
                                    path={`/media/map/sort-col-${subComponentSortConfig.key === 'instanceCount' ? subComponentSortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>FUNCTION DESCRIPTION</span>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>PARTICULARS</span>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 16px', width: '140px' }}>
                                <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>INSTANCE NO.</span>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 16px', width: '200px' }}>
                                <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>INSTANCE DESCRIPTION</span>
                              </th>
                              <th className='align-middle text-center' style={{ padding: '12px 16px', width: '120px' }}>
                                <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ACTIONS</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className='table-body text-start'>
                            {(isLoadingSubComponents) ? (
                              <TableSkeleton rows={8} cols={subComponentColumnCount} />
                            ) : (subComponentCurrentRecords.length === 0 ? (
                              <tr>
                                <td colSpan={subComponentColumnCount} className='text-center text-muted py-5'>
                                  No sub components found for the selected criteria.
                                </td>
                              </tr>
                            ) : (
                              subComponentCurrentRecords.map((subComponent, index) => (
                                <tr key={subComponent.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                  <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {index + 1 + (subComponentCurrentPage - 1) * subComponentRowsPerPage}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500 }}>
                                    {valueOrDash(subComponent.name)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {valueOrDash(subComponent.subComponentCode)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {valueOrDash(subComponent.equipmentName)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {valueOrDash(subComponent.equipmentCode)}
                                  </td>
                                  {showSubComponentCompanyCol && (
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {valueOrDash(subComponent.companyName)}
                                    </td>
                                  )}
                                  {showSubComponentVesselCol && (
                                    <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                      {valueOrDash(subComponent.vesselName)}
                                    </td>
                                  )}
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {valueOrDash(subComponent.maker)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {formatYesNo(subComponent.criticality as any)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {formatYesNo(subComponent.hasOpenJob as any)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {valueOrDash(subComponent.instanceCount ?? '-')}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {valueOrDash(subComponent.subComponentFunctionDescription)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {valueOrDash(subComponent.subComponentParticulars)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {valueOrDash(subComponent.subComponentInstanceNumber)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    {valueOrDash(subComponent.subComponentInstanceDescription)}
                                  </td>
                                  <td className='text-center' style={{ padding: '12px 16px' }}>
                                    <div className='d-flex justify-content-center align-items-center gap-1'>
                                      <button
                                        className='btn btn-sm p-1'
                                        title='Edit'
                                        style={{ width: '28px', height: '28px' }}
                                        onClick={() => handleEditSubComponent(subComponent)}
                                      >
                                        <KTSVG path='/media/map/edit-active.svg' className='svg-icon-5' />
                                      </button>
                                      <button
                                        className='btn btn-sm p-1'
                                        title='Delete'
                                        onClick={() => handleDeleteSubComponent(subComponent.id)}
                                        style={{ width: '28px', height: '28px' }}
                                      >
                                        <KTSVG path='/media/map/trash.svg' className='svg-icon-5' />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div
                        className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                        style={{
                          position: 'static',
                          bottom: 0,
                          backgroundColor: '#fff',
                          zIndex: 10,
                          borderTop: '1px solid #dee2e6',
                          marginTop: 'auto'
                        }}
                      >
                        <div className='d-flex align-items-center'>
                          <span className='text-muted me-2'>Rows per page</span>
                          <SearchableSelect
                            className='form-select'
                            style={{
                              borderRadius: '20px',
                              width: '70px',
                              border: '1px solid #dee2e6',
                              fontSize: '14px',
                              padding: '4px 8px'
                            }}
                            value={subComponentRowsPerPage}
                            onChange={handleSubComponentRowsPerPageChange}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                          </SearchableSelect>
                        </div>
                        <div className='d-flex align-items-center'>
                          <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                            Showing <strong>{subComponentCurrentRecords.length > 0 ? ((subComponentCurrentPage - 1) * subComponentRowsPerPage) + 1 : 0}-{Math.min(subComponentCurrentPage * subComponentRowsPerPage, subComponentData.length)}</strong> of <strong>{subComponentData.length}</strong>
                          </span>

                          <nav>
                            <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                              <li className={`page-item ${subComponentCurrentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handleSubComponentPageChange(subComponentCurrentPage - 1)}
                                  disabled={subComponentCurrentPage === 1}
                                >
                                  {'<'}
                                </button>
                              </li>
                              {(() => {
                                const pages = []
                                const showPages = 5
                                let startPage = Math.max(1, subComponentCurrentPage - 2)
                                let endPage = Math.min(subComponentTotalPages, startPage + showPages - 1)

                                if (endPage - startPage < showPages - 1) {
                                  startPage = Math.max(1, endPage - showPages + 1)
                                }

                                for (let i = startPage; i <= endPage; i++) {
                                  pages.push(
                                    <li key={i} className={`page-item ${subComponentCurrentPage === i ? 'active' : ''}`}>
                                      <button
                                        className='page-link text-muted'
                                        style={{
                                          backgroundColor: subComponentCurrentPage === i ? '#F4F9FF' : 'transparent',
                                          color: subComponentCurrentPage === i ? '#1B84FF' : '#7E8299',
                                          border: '1px solid #dee2e6',
                                          padding: '8px 12px',
                                          fontSize: '14px',
                                          borderRadius: '6px'
                                        }}
                                        onClick={() => handleSubComponentPageChange(i)}
                                      >
                                        {i}
                                      </button>
                                    </li>
                                  )
                                }
                                return pages
                              })()}
                              <li className={`page-item ${subComponentCurrentPage === subComponentTotalPages || subComponentTotalPages === 0 ? 'disabled' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handleSubComponentPageChange(subComponentCurrentPage + 1)}
                                  disabled={subComponentCurrentPage === subComponentTotalPages || subComponentTotalPages === 0}
                                >
                                  {'>'}
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'catalogue' && (
                  <div className='mb-8'>
                    <h4 className='fw-bold text-primary mb-4'>Inventory Items ({sortedData.length})</h4>


                    <div className='report-table table-responsive'>
                      <div style={{ overflowX: 'auto' }}>
                        <table className='table table-bordered align-middle' style={{ tableLayout: 'fixed', width: '100%' }}>
                          <thead className='table-header text-start'>
                            <tr>
                              <th onClick={() => handleSort('itemId')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '90px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ITEM ID</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${sortConfig.key === 'itemId' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th onClick={() => handleSort('itemName')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '120px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>ITEM NAME</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${sortConfig.key === 'itemName' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th onClick={() => handleSort('company')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '120px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>COMPANY</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${sortConfig.key === 'company' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th onClick={() => handleSort('vesselName')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '160px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>VESSEL</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${sortConfig.key === 'vesselName' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '100px' }}>PART NUMBER</th>
                              <th onClick={() => handleSort('category')} className='cursor-pointer align-middle' style={{ padding: '12px 16px', width: '130px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600, fontSize: '13px' }}>CATEGORY</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${sortConfig.key === 'category' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '90px' }}>TYPE</th>
                              {/* <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '130px' }}>SUB ACCOUNTING</th> */}
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '70px' }}>ROB</th>
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '60px' }}>UOM</th>
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '80px' }}>REORDER</th>
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '70px' }}>MIN</th>
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '70px' }}>MAX</th>
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '90px' }}>UNIT COST</th>
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '100px' }}>EXPIRY</th>
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '120px' }}>SERIAL NO.</th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '120px' }}>LOCATION</th>
                              <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '80px' }}>STATUS</th>
                              <th className='align-middle text-center' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '100px' }}>ACTIONS</th>
                            </tr>
                          </thead>
                          {/* Inventory Lists */}
                          <tbody className='table-body text-start'>
                            {(bootLoading || listLoading || isLoading) ? (
                              // ?? Skeleton: 8 shimmering rows, 19 cells (matches table columns)
                              Array.from({ length: 8 }).map((_, i) => (
                                <tr key={`skeleton-${i}`}>
                                  {Array.from({ length: COLUMNS_COUNT }).map((__, c) => (
                                    <td key={`s-${i}-${c}`} className="py-3">
                                      <div className="placeholder-wave">
                                        <div className="placeholder w-100" style={{ height: 14, borderRadius: 4 }} />
                                      </div>
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : (sortedData.length === 0 && noDataDelayPassed) ? (
                              <tr>
                                <td colSpan={COLUMNS_COUNT} className='text-center text-muted py-5'>
                                  No inventory items found for the selected criteria.
                                </td>
                              </tr>
                            ) : (
                              currentRecords.map((record) => (
                                <tr
                                  key={record.id}
                                  style={{ borderBottom: '1px solid #E4E6EF' }}
                                >
                                  <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    <Link to={`/inventory/${record.id}`} className='text-primary text-hover-primary'>
                                      {record.itemId}
                                    </Link>
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <Cell value={record.itemName || '-'} loading={!mapReady} />
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {record.company || 'N/A'}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <VesselLandingLink
                                      vesselId={record.vesselId ?? null}
                                      label={record.vesselName || (record.vesselType ? record.vesselType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A')}
                                      className='text-dark text-decoration-underline'
                                    />
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px' }}>
                                    <Cell value={record.partNumber || '-'} loading={!mapReady} />
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                    <Cell value={record.category || ''} loading={!mapReady} />
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                    <Cell value={record.partType || '?'} loading={!mapReady} />
                                  </td>
                                  {/* <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                    <Cell value={record.subAccounting || 'N/A'} loading={!mapReady} />
                                  </td> */}
                                  <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px' }}>
                                    {record.currentQty}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px' }}>
                                    <Cell value={record.uom || ''} loading={!mapReady} />
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px' }}>
                                    {record.reorderLevel}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px' }}>
                                    {record.minThreshold}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px' }}>
                                    {record.maxThreshold}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px' }}>
                                    ${formatCurrency(record.unitCost)}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                    {record.expiryDate ? new Date(record.expiryDate).toLocaleDateString() : '-'}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {record.serialNumber || '-'}
                                  </td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {record.location}
                                  </td>
                                  <td style={{ padding: '12px 8px' }}>
                                    <span className={`badge ${record.status === 'Active' ? 'submitted' : 'rejected'} text-white`} style={{ fontSize: '12px' }}>
                                      {record.status}
                                    </span>
                                  </td>
                                  <td className='text-center' style={{ padding: '12px 8px' }}>
                                    <div className='d-flex justify-content-center align-items-center gap-1'>
                                      <button
                                        className='btn btn-sm p-1'
                                        title='View'
                                        style={{ width: '28px', height: '28px' }}
                                        onClick={() => handleViewItem(record)}
                                      >
                                        <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-5' />
                                      </button>
                                      <button className='btn btn-sm p-1' title='Edit' style={{ width: '28px', height: '28px' }}>
                                        <KTSVG path='/media/map/edit-active.svg' className='svg-icon-5' />
                                      </button>
                                      <button
                                        className='btn btn-sm p-1'
                                        title='Delete'
                                        onClick={() => handleDeleteItem(record.id)}
                                        style={{ width: '28px', height: '28px' }}
                                      >
                                        <KTSVG path='/media/map/trash.svg' className='svg-icon-5' />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div
                        className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                        style={{
                          position: 'static',
                          bottom: 0,
                          backgroundColor: '#fff',
                          zIndex: 10,
                          borderTop: '1px solid #dee2e6',
                          marginTop: 'auto'
                        }}
                      >
                        <div className='d-flex align-items-center'>
                          <span className='text-muted me-2'>Rows per page</span>
                          <SearchableSelect
                            className='form-select'
                            style={{
                              borderRadius: '20px',
                              width: '70px',
                              border: '1px solid #dee2e6',
                              fontSize: '14px',
                              padding: '4px 8px'
                            }}
                            value={rowsPerPage}
                            onChange={handleRowsPerPageChange}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                          </SearchableSelect>
                        </div>
                        <div className='d-flex align-items-center'>
                          <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                            Showing <strong>{currentRecords.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0}-{Math.min(currentPage * rowsPerPage, sortedData.length)}</strong> of <strong>{sortedData.length}</strong>
                          </span>

                          <nav>
                            <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                >
                                  {'<'}
                                </button>
                              </li>

                              {(() => {
                                const pages = []
                                const showPages = 5
                                let startPage = Math.max(1, currentPage - 2)
                                let endPage = Math.min(totalPages, startPage + showPages - 1)

                                if (endPage - startPage < showPages - 1) {
                                  startPage = Math.max(1, endPage - showPages + 1)
                                }

                                if (startPage > 1) {
                                  pages.push(
                                    <li key={1} className='page-item'>
                                      <button
                                        className='page-link text-muted'
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: '1px solid #dee2e6',
                                          padding: '8px 12px',
                                          fontSize: '14px',
                                          minWidth: '40px',
                                          borderRadius: '6px'
                                        }}
                                        onClick={() => handlePageChange(1)}
                                      >
                                        1
                                      </button>
                                    </li>
                                  )

                                  if (startPage > 2) {
                                    pages.push(
                                      <li key='ellipsis1' className='page-item disabled'>
                                        <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                      </li>
                                    )
                                  }
                                }

                                for (let i = startPage; i <= endPage; i++) {
                                  pages.push(
                                    <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                      <button
                                        className='page-link text-muted'
                                        style={{
                                          backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                          border: '1px solid #dee2e6',
                                          padding: '8px 12px',
                                          fontSize: '14px',
                                          minWidth: '40px',
                                          borderRadius: '6px',
                                          outline: 'none',
                                          boxShadow: 'none'
                                        }}
                                        onClick={() => handlePageChange(i)}
                                      >
                                        {i}
                                      </button>
                                    </li>
                                  )
                                }

                                if (endPage < totalPages) {
                                  if (endPage < totalPages - 1) {
                                    pages.push(
                                      <li key='ellipsis2' className='page-item disabled'>
                                        <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                      </li>
                                    )
                                  }

                                  pages.push(
                                    <li key={totalPages} className='page-item'>
                                      <button
                                        className='page-link text-muted'
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: '1px solid #dee2e6',
                                          padding: '8px 12px',
                                          fontSize: '14px',
                                          minWidth: '40px',
                                          borderRadius: '6px'
                                        }}
                                        onClick={() => handlePageChange(totalPages)}
                                      >
                                        {totalPages}
                                      </button>
                                    </li>
                                  )
                                }

                                return pages
                              })()}

                              <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === totalPages || totalPages === 0}
                                >
                                  {'>'}
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    </div>


                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <ViewInventoryModal
          visible={isViewModalVisible}
          onClose={() => {
            setIsViewModalVisible(false)
            setSelectedInventoryItem(null)
          }}
          item={selectedInventoryItem}
        />

      <AddItemModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSubmit={handleAddItemSuccess}
        vesselId={vesselId || 0}
        categories={categories}
        parts={parts as any}
        refreshTrigger={addItemModalRefreshTrigger}
      />

      <PartStorageLocationLibraryModal
        visible={isStorageLibraryVisible}
        onClose={() => setIsStorageLibraryVisible(false)}
      />

        <UpdateInventoryModal
          visible={isUpdateModalVisible}
          onClose={() => setIsUpdateModalVisible(false)}
          onSubmit={handleAddItemSuccess}
          vesselId={vesselId || 0}
          categories={categories}
          parts={parts as any}
        />

        {/* Individual Tab Modals */}
        {canCreateCategoryLibrary && (
        <CategoryModal
          visible={isCategoryModalVisible}
          onClose={() => setIsCategoryModalVisible(false)}
          onSubmit={handleCategorySubmit}
          vesselId={vesselId || 0}
        />
        )}

        {canUpdateCategoryLibrary && (
        <EditCategoryModal
          visible={isEditCategoryModalVisible}
          onClose={() => {
            setIsEditCategoryModalVisible(false)
            setSelectedCategory(null)
          }}
          onSubmit={handleEditCategorySubmit}
          category={selectedCategory}
        />
        )}

        <HeadModal
          visible={isHeadModalVisible}
          onClose={() => setIsHeadModalVisible(false)}
          onSubmit={handleHeadSubmit}
          cgaId={categoryCompanyFilter ? parseInt(categoryCompanyFilter) : undefined}
          vesselId={categoryVesselFilter ? parseInt(categoryVesselFilter) : (vesselId || undefined)}
        />

        <EditHeadModal
          visible={isEditHeadModalVisible}
          onClose={() => {
            setIsEditHeadModalVisible(false)
            setSelectedHead(null)
          }}
          onSubmit={handleEditHeadSubmit}
          head={selectedHead}
          cgaId={categoryCompanyFilter ? parseInt(categoryCompanyFilter) : undefined}
          vesselId={categoryVesselFilter ? parseInt(categoryVesselFilter) : (vesselId || undefined)}
        />

        <SubHeadModal
          visible={isSubHeadModalVisible}
          onClose={() => setIsSubHeadModalVisible(false)}
          onSubmit={handleSubHeadSubmit}
          vesselId={categoryVesselFilter ? parseInt(categoryVesselFilter) : (vesselId || undefined)}
        />

        <EditSubHeadModal
          visible={isEditSubHeadModalVisible}
          onClose={() => {
            setIsEditSubHeadModalVisible(false)
            setSelectedSubHead(null)
          }}
          onSubmit={handleEditSubHeadSubmit}
          subHead={selectedSubHead}
          vesselId={categoryVesselFilter ? parseInt(categoryVesselFilter) : (vesselId || undefined)}
        />

        <GeneralItemModal
          visible={isGeneralItemModalVisible}
          onClose={() => setIsGeneralItemModalVisible(false)}
          onSubmit={handleGeneralItemSubmit}
          vesselId={vesselId}
        />

        <AccountingCodeModal
          visible={isAccountingCodeModalVisible}
          onClose={() => setIsAccountingCodeModalVisible(false)}
          onSubmit={handleAccountingCodeSubmit}
          vesselId={vesselId || 0}
        />

        <EditAccountingCodeModal
          visible={isEditAccountingCodeModalVisible}
          onClose={() => {
            setIsEditAccountingCodeModalVisible(false)
            setSelectedAccountingCode(null)
          }}
          onSubmit={handleEditAccountingCodeSubmit}
          accountingCode={selectedAccountingCode}
        />

        <SubAccountingModal
          visible={isSubAccountingModalVisible}
          onClose={() => setIsSubAccountingModalVisible(false)}
          onSubmit={handleSubAccountingSubmit}
        />

        <EditSubAccountingModal
          visible={isEditSubAccountingModalVisible}
          onClose={() => {
            setIsEditSubAccountingModalVisible(false)
            setSelectedSubAccount(null)
          }}
          onSubmit={handleEditSubAccountingSubmit}
          subAccount={selectedSubAccount}
        />

        <SubCatalogueModal
          visible={isSubCatalogueModalVisible}
          onClose={() => setIsSubCatalogueModalVisible(false)}
          onSubmit={handleSubCatalogueSubmit}
        />

        <EditSubCatalogueModal
          visible={isEditSubCatalogueModalVisible}
          onClose={() => {
            setIsEditSubCatalogueModalVisible(false)
            setSelectedSubCatalogue(null)
          }}
          onSubmit={handleEditSubCatalogueSubmit}
          subCatalogue={selectedSubCatalogue}
        />

        <MachineryEquipmentModal
          visible={isEquipmentModalVisible}
          vesselId={vesselId ?? undefined}
          editing={equipmentModalMode === 'edit' ? (selectedEquipment as any) : undefined}
          onClose={() => {
            setIsEquipmentModalVisible(false)
            setEquipmentModalMode('create')
            setSelectedEquipment(null)
          }}
          onSubmit={handleEquipmentSubmit}
        />

        <MachineryPartModal
          visible={isPartModalVisible}
          subcomponentId={partParentSubComponentId || 0}
          editing={selectedPart as any}
          onClose={() => {
            setIsPartModalVisible(false)
            setSelectedPart(null)
            setPartParentEquipmentId(null)
            setPartParentSubComponentId(null)
          }}
          onSubmit={handlePartSubmit}
        />

        <MachinerySubcomponentModal
          visible={isSubComponentModalVisible && !!selectedSubComponent}
          equipmentId={subComponentParentEquipmentId || 0}
          editing={selectedSubComponent as any}
          onClose={() => {
            setIsSubComponentModalVisible(false)
            setSelectedSubComponent(null)
            setSubComponentParentEquipmentId(null)
          }}
          onSubmit={async (dto) => {
            try {
              if (dto?.id) {
                await machineryUpdateSubcomponent(dto.id, dto)
                toast.success('Sub Component updated successfully!')
                await loadSubComponentData()
              }
            } catch (error: any) {
              toast.error('Failed to save sub component')
            } finally {
              setIsSubComponentModalVisible(false)
              setSelectedSubComponent(null)
              setSubComponentParentEquipmentId(null)
            }
          }}
        />

        {showSubComponentCreateModal && (
          <div className='modal fade show d-flex align-items-center justify-content-center' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
            <div className='modal-dialog modal-lg'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Add Sub Component</h5>
                  <button type='button' className='btn-close' onClick={() => setShowSubComponentCreateModal(false)}></button>
                </div>
                <div className='modal-body'>
                  <div className='row g-4'>
                    {isGlobalScopeUser && (
                      <div className='col-md-6'>
                        <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Company</label>
                        <SearchableSelect
                          className='form-select'
                          value={subcomponentCompanyFilter}
                          onChange={(e) => {
                            setSubcomponentCompanyFilter(e.target.value)
                            setSubcomponentVesselId('')
                            setSubComponentParentEquipmentId(null)
                            setSubcomponentVesselOptions([])
                            setSubcomponentEquipmentOptions([])
                            if (e.target.value) loadVesselsForCompany(e.target.value, setSubcomponentVesselOptions)
                          }}
                        >
                          <option value=''>Select company</option>
                          {companyOptions.map(c => (
                            <option key={c.id} value={c.id}>{(c as any).companyGroupAdminName || c.name || (c as any).username || `Company #${c.id}`}</option>
                          ))}
                        </SearchableSelect>
                      </div>
                    )}

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Vessel</label>
                      <SearchableSelect
                        className='form-select'
                        value={subcomponentVesselId}
                        onChange={(e) => {
                          setSubcomponentVesselId(e.target.value)
                          setSubComponentParentEquipmentId(null)
                          setSubcomponentEquipmentOptions([])
                          if (e.target.value) {
                            getMachineryEquipmentList(Number(e.target.value))
                              .then(list => setSubcomponentEquipmentOptions((list || []) as EquipmentDto[]))
                              .catch(() => setSubcomponentEquipmentOptions([]))
                          }
                        }}
                      >
                        <option value=''>Select vessel</option>
                        {(subcomponentVesselOptions.length ? subcomponentVesselOptions : vesselCache.current ? Array.from(vesselCache.current.values()) : []).map(v => (
                          <option key={v.id} value={v.id}>{(v as any).name || (v as any).vesselName || `Vessel #${v.id}`}</option>
                        ))}
                      </SearchableSelect>
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Equipment</label>
                      <SearchableSelect
                        className='form-select'
                        value={subComponentParentEquipmentId ?? ''}
                        onChange={(e) => setSubComponentParentEquipmentId(e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value=''>Select equipment</option>
                        {(subcomponentEquipmentOptions.length ? subcomponentEquipmentOptions : equipmentData)
                          .filter(eq => (!subcomponentVesselId || String(eq.vesselId) === subcomponentVesselId))
                          .map(eq => (
                            <option key={eq.id ?? eq.code ?? eq.name} value={eq.id}>{eq.name || eq.code || `Equipment #${eq.id}`}</option>
                          ))}
                      </SearchableSelect>
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Sub Component Name</label>
                      <input className='form-control' value={subcomponentName} onChange={(e) => setSubcomponentName(e.target.value)} />
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Code</label>
                      <input className='form-control' value={subcomponentCode} onChange={(e) => setSubcomponentCode(e.target.value)} />
                    </div>

                    <div className='col-md-12'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Description</label>
                      <textarea className='form-control' value={subcomponentDesc} onChange={(e) => setSubcomponentDesc(e.target.value)} />
                    </div>

                    <div className='col-md-12'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark me-3'>Criticality</label>
                      <input type='checkbox' className='form-check-input' checked={subcomponentCriticality} onChange={(e) => setSubcomponentCriticality(e.target.checked)} />
                    </div>
                  </div>
                </div>
                <div className='modal-footer'>
                  <button className='btn btn-light btn-sm' onClick={() => setShowSubComponentCreateModal(false)} disabled={subcomponentSaving}>Cancel</button>
                  <button
                    className='btn btn_primary btn-sm'
                    disabled={subcomponentSaving}
                    onClick={async () => {
                      if (!subComponentParentEquipmentId) {
                        toast.error('Equipment is required')
                        return
                      }
                      if (!subcomponentName.trim()) {
                        toast.error('Sub Component name is required')
                        return
                      }
                      setSubcomponentSaving(true)
                      try {
                        await machineryCreateSubcomponent({
                          equipmentId: subComponentParentEquipmentId,
                          name: subcomponentName.trim(),
                          subComponentCode: subcomponentCode || undefined,
                          subComponentFunctionDescription: subcomponentDesc || undefined,
                          criticality: subcomponentCriticality,
                        } as any)
                        toast.success('Sub Component added successfully!')
                        await loadSubComponentData()
                        setShowSubComponentCreateModal(false)
                      } catch (error: any) {
                        toast.error('Failed to add sub component')
                      } finally {
                        setSubcomponentSaving(false)
                      }
                    }}
                  >
                    {subcomponentSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPartCreateModal && (
          <div className='modal fade show d-flex align-items-center justify-content-center' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
            <div className='modal-dialog modal-lg'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Add Part</h5>
                  <button type='button' className='btn-close' onClick={() => setShowPartCreateModal(false)}></button>
                </div>
                <div className='modal-body'>
                  <div className='row g-4'>
                    {isGlobalScopeUser && (
                      <div className='col-md-6'>
                        <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Company</label>
                        <SearchableSelect
                          className='form-select'
                          value={partCompanyPicker}
                          onChange={(e) => {
                            setPartCompanyPicker(e.target.value)
                            setPartVesselId('')
                            setPartEquipmentOptions([])
                            setPartEquipmentId('')
                            setPartSubcomponentOptions([])
                            setPartSubcomponentId('')
                            setPartVesselOptions([])
                            if (e.target.value) loadVesselsForCompany(e.target.value, setPartVesselOptions)
                          }}
                        >
                          <option value=''>Select company</option>
                          {companyOptions.map(c => (
                            <option key={c.id} value={c.id}>{(c as any).companyGroupAdminName || c.name || (c as any).username || `Company #${c.id}`}</option>
                          ))}
                        </SearchableSelect>
                      </div>
                    )}

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Vessel</label>
                      <SearchableSelect
                        className='form-select'
                        value={partVesselId}
                        onChange={(e) => {
                          setPartVesselId(e.target.value)
                          setPartEquipmentOptions([])
                          setPartEquipmentId('')
                          setPartSubcomponentId('')
                          setPartSubcomponentOptions([])
                          if (e.target.value) {
                            getMachineryEquipmentList(Number(e.target.value))
                              .then(list => setPartEquipmentOptions((list || []) as EquipmentDto[]))
                              .catch(() => setPartEquipmentOptions([]))
                          } else {
                            setPartEquipmentOptions([])
                            setPartSubcomponentOptions([])
                          }
                        }}
                      >
                        <option value=''>Select vessel</option>
                        {(partVesselOptions.length ? partVesselOptions : vesselCache.current ? Array.from(vesselCache.current.values()) : []).map(v => (
                          <option key={v.id} value={v.id}>{(v as any).name || (v as any).vesselName || `Vessel #${v.id}`}</option>
                        ))}
                      </SearchableSelect>
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Equipment</label>
                      <SearchableSelect
                        className='form-select'
                        value={partEquipmentId}
                        onChange={(e) => {
                          setPartEquipmentId(e.target.value)
                          setPartSubcomponentId('')
                          if (e.target.value) {
                            getSubcomponentsByEquipment(Number(e.target.value), false)
                              .then(list => setPartSubcomponentOptions((list || []) as SubcomponentDto[]))
                              .catch(() => setPartSubcomponentOptions([]))
                          } else {
                            setPartSubcomponentOptions([])
                          }
                        }}
                      >
                        <option value=''>Select equipment</option>
                        {(partEquipmentOptions.length ? partEquipmentOptions : equipmentData)
                          .filter(eq => (!partVesselId || String(eq.vesselId) === partVesselId))
                          .map(eq => (
                            <option key={eq.id ?? eq.code ?? eq.name} value={eq.id}>{eq.name || eq.code || `Equipment #${eq.id}`}</option>
                          ))}
                      </SearchableSelect>
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Sub Component</label>
                      <SearchableSelect
                        className='form-select'
                        value={partSubcomponentId}
                        onChange={(e) => setPartSubcomponentId(e.target.value)}
                        disabled={!partEquipmentId}
                      >
                        <option value=''>Select sub component</option>
                        {(partSubcomponentOptions.length ? partSubcomponentOptions : subComponentData)
                          .filter(sc => !!partEquipmentId && sc.equipmentId === Number(partEquipmentId))
                          .map(sc => (
                            <option key={sc.id ?? `${sc.name}-${(sc as any).subComponentCode}`} value={sc.id}>
                              {sc.name || (sc as any).subComponentFunctionDescription || `Sub Component #${sc.id}`}
                            </option>
                          ))}
                      </SearchableSelect>
                    </div>

                    <div className='col-md-6 d-flex align-items-center'>
                      <div className='form-check form-switch mt-4'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='isSparePart'
                          checked={isSparePart}
                          onChange={(e) => setIsSparePart(e.target.checked)}
                        />
                        <label className='form-check-label ms-2' htmlFor='isSparePart'>Spare Part</label>
                      </div>
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Part Name</label>
                      <input className='form-control' value={partName} onChange={(e) => setPartName(e.target.value)} />
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Part Code</label>
                      <input className='form-control' value={partCode} onChange={(e) => setPartCode(e.target.value)} />
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Part Number</label>
                      <input className='form-control' value={partNumber} onChange={(e) => setPartNumber(e.target.value)} />
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Unit of Measure</label>
                      <SearchableSelect className='form-select' value={partUnitId} onChange={(e) => setPartUnitId(e.target.value)}>
                        <option value=''>Select Unit</option>
                        {unitOptions.map(u => (
                          <option key={u.id} value={u.id}>{u.name || (u as any).unitName || `Unit #${u.id}`}</option>
                        ))}
                      </SearchableSelect>
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Location</label>
                      <input className='form-control' value={partLocation} onChange={(e) => setPartLocation(e.target.value)} />
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Manufacturer</label>
                      <input className='form-control' value={partManufacturer} onChange={(e) => setPartManufacturer(e.target.value)} />
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Serial Number</label>
                      <input className='form-control' value={partSerialNumber} onChange={(e) => setPartSerialNumber(e.target.value)} />
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Storage Location</label>
                      <SearchableSelect
                        className='form-select'
                        value={storageLocationId}
                        onChange={(e) => {
                          setStorageLocationId(e.target.value)
                          setStorageSubLocationId('')
                          setStorageSegmentId('')
                        }}
                      >
                        <option value=''>Select storage location</option>
                        {storageLocations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </SearchableSelect>
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Sub Location</label>
                      <SearchableSelect
                        className='form-select'
                        value={storageSubLocationId}
                        onChange={(e) => {
                          setStorageSubLocationId(e.target.value)
                          setStorageSegmentId('')
                        }}
                        disabled={!storageLocationId}
                      >
                        <option value=''>Select sub location</option>
                        {storageLocations
                          .find(loc => String(loc.id) === storageLocationId)?.subLocations?.map(sub => (
                            <option key={sub.id} value={sub.id}>{sub.name}</option>
                          ))}
                      </SearchableSelect>
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold fs-6 mb-2 text-dark'>Segment</label>
                      <SearchableSelect
                        className='form-select'
                        value={storageSegmentId}
                        onChange={(e) => setStorageSegmentId(e.target.value)}
                        disabled={!storageSubLocationId}
                      >
                        <option value=''>Select segment</option>
                        {storageLocations
                          .find(loc => String(loc.id) === storageLocationId)?.subLocations
                          ?.find(sub => String(sub.id) === storageSubLocationId)?.segments?.map(seg => (
                            <option key={seg.id} value={seg.id}>{seg.name}</option>
                          ))}
                      </SearchableSelect>
                    </div>
                  </div>
                </div>
                <div className='modal-footer'>
                  <button className='btn btn-light btn-sm' onClick={() => setShowPartCreateModal(false)} disabled={partSaving}>Cancel</button>
                  <button
                    className='btn btn_primary btn-sm'
                    disabled={partSaving}
                    onClick={async () => {
                      if (!partSubcomponentId) {
                        toast.error('Sub component is required')
                        return
                      }
                      if (!partName.trim()) {
                        toast.error('Part name is required')
                        return
                      }
                      setPartSaving(true)
                      try {
                        const payload: any = {
                          subcomponentId: Number(partSubcomponentId),
                          name: partName.trim(),
                          code: partCode || undefined,
                          location: (() => {
                            const loc = storageLocations.find(l => String(l.id) === storageLocationId)
                            const sub = loc?.subLocations?.find(s => String(s.id) === storageSubLocationId)
                            const seg = sub?.segments?.find(sg => String(sg.id) === storageSegmentId)
                            if (!loc?.name) return partLocation || undefined
                            const parts: string[] = [loc.name]
                            if (sub?.name) parts.push(sub.name)
                            if (seg?.name) parts.push(seg.name)
                            return parts.join(': ')
                          })(),
                          vesselId: partVesselId ? Number(partVesselId) : undefined,
                          equipmentId: partEquipmentId ? Number(partEquipmentId) : undefined,
                          isSpare: !!isSparePart,
                          manufacturer: partManufacturer || undefined,
                          partNo: partNumber || undefined,
                          serialNumber: partSerialNumber || undefined,
                          rob: Number(partRob || 0),
                          ...(partUnitId ? { uomId: Number(partUnitId) } : {}),
                        }
                        await machineryCreatePart(payload)
                        toast.success('Part added successfully!')
                        await loadPartData()
                        setShowPartCreateModal(false)
                      } catch (error: any) {
                        toast.error('Failed to add part')
                      } finally {
                        setPartSaving(false)
                      }
                    }}
                  >
                    {partSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

{generalItemsLibraryOpen && (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 23, 42, 0.38)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1050,
      padding: '24px',
      backdropFilter: 'blur(2px)',
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) {
        setGeneralItemsLibraryOpen(false)
      }
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '85vw',
        maxWidth: '85vw',
        height: '85vh',
        maxHeight: '85vh',
        background: '#FFFFFF',
        borderRadius: '18px',
        boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
        border: '1px solid #E9EEF5',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '24px 28px 18px 28px',
          borderBottom: '1px solid #E9EEF5',
          background: '#FFFFFF',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h5
            style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: 700,
              color: '#1E293B',
              lineHeight: 1.2,
            }}
          >
            General Items Library
          </h5>
          <div
            style={{
              marginTop: '6px',
              fontSize: '13px',
              color: '#94A3B8',
              lineHeight: 1.4,
            }}
          >
            Manage general items without leaving inventory
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexShrink: 0,
          }}
        >
          <button
            type='button'
            className='btn btn_primary btn-sm'
            style={{
              minHeight: '38px',
              padding: '10px 16px',
              borderRadius: '10px',
              fontWeight: 600,
            }}
            onClick={() => setIsGeneralItemModalVisible(true)}
          >
            <span className='me-2'>+</span>
            Add General Item
          </button>

          <button
            type='button'
            className='btn btn-light btn-sm'
            style={{
              minHeight: '38px',
              padding: '10px 16px',
              borderRadius: '10px',
              fontWeight: 500,
            }}
            onClick={() => {
              setGeneralItemCompanyFilter('ALL')
              setGeneralItemVesselFilter('ALL')
              setGeneralItemLocationFilter('ALL')
            }}
          >
            Clear Filters
          </button>

          <button
            type='button'
            className='btn btn-light btn-sm'
            style={{
              minHeight: '38px',
              padding: '10px 16px',
              borderRadius: '10px',
              fontWeight: 500,
            }}
            onClick={() => setGeneralItemsLibraryOpen(false)}
          >
            Close
          </button>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          padding: '20px 24px 0 24px',
          background: '#F8FAFC',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid #E9EEF5',
            borderRadius: '14px',
            padding: '16px 18px',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))',
            gap: '14px',
            flexShrink: 0,
          }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 700,
                color: '#475569',
                marginBottom: '8px',
              }}
            >
              Company
            </label>
            <SearchableSelect
              className='form-select'
              value={generalItemCompanyFilter}
              onChange={(e) => setGeneralItemCompanyFilter(e.target.value)}
              style={{
                borderRadius: '10px',
                border: '1px solid #D8E1EC',
                minHeight: '42px',
                fontSize: '14px',
              }}
            >
              <option value='ALL'>All Companies</option>
              {generalItemCompanyOptions.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </SearchableSelect>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 700,
                color: '#475569',
                marginBottom: '8px',
              }}
            >
              Vessel
            </label>
            <SearchableSelect
              className='form-select'
              value={generalItemVesselFilter}
              onChange={(e) => setGeneralItemVesselFilter(e.target.value)}
              style={{
                borderRadius: '10px',
                border: '1px solid #D8E1EC',
                minHeight: '42px',
                fontSize: '14px',
              }}
            >
              <option value='ALL'>All Vessels</option>
              {generalItemVesselOptions.map((vessel) => (
                <option key={vessel} value={vessel}>
                  {vessel}
                </option>
              ))}
            </SearchableSelect>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 700,
                color: '#475569',
                marginBottom: '8px',
              }}
            >
              Location
            </label>
            <SearchableSelect
              className='form-select'
              value={generalItemLocationFilter}
              onChange={(e) => setGeneralItemLocationFilter(e.target.value)}
              style={{
                borderRadius: '10px',
                border: '1px solid #D8E1EC',
                minHeight: '42px',
                fontSize: '14px',
              }}
            >
              <option value='ALL'>All Locations</option>
              {generalItemLocationOptions.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </SearchableSelect>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            background: '#FFFFFF',
            border: '1px solid #E9EEF5',
            borderRadius: '14px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              width: '100%',
            }}
          >
            <table
              className='table align-middle mb-0'
              style={{
                width: '100%',
                minWidth: '1150px',
                tableLayout: 'auto',
              }}
            >
              <thead
                className='text-start'
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  background: '#F8FAFC',
                }}
              >
                <tr>
                  <th
                    style={{
                      padding: '14px 16px',
                      minWidth: '90px',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#475569',
                      borderBottom: '1px solid #E9EEF5',
                      background: '#F8FAFC',
                    }}
                  >
                    Sr. No.
                  </th>

                  <th
                    onClick={() => handleGeneralItemSort('manualItemName')}
                    className='cursor-pointer'
                    style={{
                      padding: '14px 16px',
                      minWidth: '220px',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#475569',
                      borderBottom: '1px solid #E9EEF5',
                      background: '#F8FAFC',
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      ITEM NAME
                      <div style={{ transform: 'translateY(-2px)' }}>
                        <KTSVG
                          path={`/media/map/sort-col-${
                            generalItemSortConfig.key === 'manualItemName'
                              ? generalItemSortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'
                          }.svg`}
                          className='svg-icon ms-2 custom-sort-icon'
                        />
                      </div>
                    </div>
                  </th>

                  <th
                    onClick={() => handleGeneralItemSort('unitOfMeasurement')}
                    className='cursor-pointer'
                    style={{
                      padding: '14px 16px',
                      minWidth: '120px',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#475569',
                      borderBottom: '1px solid #E9EEF5',
                      background: '#F8FAFC',
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      UNIT
                      <div style={{ transform: 'translateY(-2px)' }}>
                        <KTSVG
                          path={`/media/map/sort-col-${
                            generalItemSortConfig.key === 'unitOfMeasurement'
                              ? generalItemSortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'
                          }.svg`}
                          className='svg-icon ms-2 custom-sort-icon'
                        />
                      </div>
                    </div>
                  </th>

                  <th
                    onClick={() => handleGeneralItemSort('currentQty')}
                    className='cursor-pointer'
                    style={{
                      padding: '14px 16px',
                      minWidth: '140px',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#475569',
                      borderBottom: '1px solid #E9EEF5',
                      background: '#F8FAFC',
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      QUANTITY
                      <div style={{ transform: 'translateY(-2px)' }}>
                        <KTSVG
                          path={`/media/map/sort-col-${
                            generalItemSortConfig.key === 'currentQty'
                              ? generalItemSortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'
                          }.svg`}
                          className='svg-icon ms-2 custom-sort-icon'
                        />
                      </div>
                    </div>
                  </th>

                  <th
                    onClick={() => handleGeneralItemSort('location')}
                    className='cursor-pointer'
                    style={{
                      padding: '14px 16px',
                      minWidth: '180px',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#475569',
                      borderBottom: '1px solid #E9EEF5',
                      background: '#F8FAFC',
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      LOCATION
                      <div style={{ transform: 'translateY(-2px)' }}>
                        <KTSVG
                          path={`/media/map/sort-col-${
                            generalItemSortConfig.key === 'location'
                              ? generalItemSortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'
                          }.svg`}
                          className='svg-icon ms-2 custom-sort-icon'
                        />
                      </div>
                    </div>
                  </th>

                  <th
                    onClick={() => handleGeneralItemSort('vesselName')}
                    className='cursor-pointer'
                    style={{
                      padding: '14px 16px',
                      minWidth: '180px',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#475569',
                      borderBottom: '1px solid #E9EEF5',
                      background: '#F8FAFC',
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      VESSEL
                      <div style={{ transform: 'translateY(-2px)' }}>
                        <KTSVG
                          path={`/media/map/sort-col-${
                            generalItemSortConfig.key === 'vesselName'
                              ? generalItemSortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'
                          }.svg`}
                          className='svg-icon ms-2 custom-sort-icon'
                        />
                      </div>
                    </div>
                  </th>

                  <th
                    onClick={() => handleGeneralItemSort('company')}
                    className='cursor-pointer'
                    style={{
                      padding: '14px 16px',
                      minWidth: '200px',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#475569',
                      borderBottom: '1px solid #E9EEF5',
                      background: '#F8FAFC',
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      COMPANY
                      <div style={{ transform: 'translateY(-2px)' }}>
                        <KTSVG
                          path={`/media/map/sort-col-${
                            generalItemSortConfig.key === 'company'
                              ? generalItemSortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'
                          }.svg`}
                          className='svg-icon ms-2 custom-sort-icon'
                        />
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody className='text-start'>
                {generalItemLoading ? (
                  <TableSkeleton rows={8} cols={COLS.generalItem} />
                ) : generalItemCurrentRecords.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLS.generalItem}
                      className='text-center text-muted py-5'
                    >
                      No general items found.
                    </td>
                  </tr>
                ) : (
                  generalItemCurrentRecords.map((item, index) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #EEF2F7' }}>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1E293B',
                        }}
                      >
                        {generalItemIndexOfFirstRecord + index + 1}
                      </td>

                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#1E293B',
                        }}
                      >
                        {item.manualItemName || 'N/A'}
                      </td>

                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: '#334155',
                        }}
                      >
                        {item.unitOfMeasurement || 'N/A'}
                      </td>

                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: '#334155',
                        }}
                      >
                        {`${item.currentQty ?? 0} ${(item.unitOfMeasurement || '').trim()}`.trim()}
                      </td>

                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: '#334155',
                        }}
                      >
                        {item.location || 'N/A'}
                      </td>

                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: '#334155',
                        }}
                      >
                        <VesselLandingLink
                          vesselId={item.vesselId ?? null}
                          label={item.vessel?.fleet_name || 'N/A'}
                          className='text-dark text-decoration-underline'
                        />
                      </td>

                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: '#334155',
                        }}
                      >
                        {item.vessel?.companyGroupAdmin?.name ||
                          item.vessel?.companyAdmin?.name ||
                          'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid #E9EEF5',
            borderRadius: '14px',
            padding: '14px 18px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexShrink: 0,
          }}
        >
          <div className='d-flex align-items-center'>
            <span
              className='text-muted me-2'
              style={{ fontSize: '14px', fontWeight: 500 }}
            >
              Rows per page
            </span>
            <SearchableSelect
              className='form-select'
              style={{
                borderRadius: '20px',
                width: '74px',
                border: '1px solid #D8E1EC',
                fontSize: '14px',
                padding: '4px 8px',
                backgroundColor: '#FFFFFF',
              }}
              value={generalItemRowsPerPage}
              onChange={handleGeneralItemRowsPerPageChange}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </SearchableSelect>
          </div>

          <div className='d-flex align-items-center'>
            <span
              className='text-muted me-3'
              style={{ fontSize: '14px', fontWeight: 500 }}
            >
              Showing{' '}
              <strong>
                {generalItemCurrentRecords.length > 0
                  ? (generalItemCurrentPage - 1) * generalItemRowsPerPage + 1
                  : 0}
                -
                {Math.min(
                  generalItemCurrentPage * generalItemRowsPerPage,
                  sortedGeneralItemData.length
                )}
              </strong>{' '}
              of <strong>{sortedGeneralItemData.length}</strong>
            </span>

            <nav>
              <ul className='pagination pagination-sm mb-0' style={{ gap: '6px' }}>
                <li
                  className={`page-item ${
                    generalItemCurrentPage === 1 ? 'disabled' : ''
                  }`}
                >
                  <button
                    className='page-link text-muted'
                    style={{
                      backgroundColor: '#F8FAFC',
                      border: '1px solid #D8E1EC',
                      padding: '8px 12px',
                      fontSize: '14px',
                      borderRadius: '8px',
                    }}
                    onClick={() =>
                      handleGeneralItemPageChange(generalItemCurrentPage - 1)
                    }
                    disabled={generalItemCurrentPage === 1}
                  >
                    {'<'}
                  </button>
                </li>

                {(() => {
                  const pages = []
                  const showPages = 5
                  let startPage = Math.max(1, generalItemCurrentPage - 2)
                  let endPage = Math.min(
                    generalItemTotalPages,
                    startPage + showPages - 1
                  )

                  if (endPage - startPage < showPages - 1) {
                    startPage = Math.max(1, endPage - showPages + 1)
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <li
                        key={i}
                        className={`page-item ${
                          generalItemCurrentPage === i ? 'active' : ''
                        }`}
                      >
                        <button
                          className='page-link'
                          style={{
                            backgroundColor:
                              generalItemCurrentPage === i ? '#EFF6FF' : '#FFFFFF',
                            color:
                              generalItemCurrentPage === i ? '#2563EB' : '#64748B',
                            border: '1px solid #D8E1EC',
                            padding: '8px 12px',
                            fontSize: '14px',
                            borderRadius: '8px',
                            fontWeight: 600,
                          }}
                          onClick={() => handleGeneralItemPageChange(i)}
                        >
                          {i}
                        </button>
                      </li>
                    )
                  }

                  return pages
                })()}

                <li
                  className={`page-item ${
                    generalItemCurrentPage === generalItemTotalPages ||
                    generalItemTotalPages === 0
                      ? 'disabled'
                      : ''
                  }`}
                >
                  <button
                    className='page-link text-muted'
                    style={{
                      backgroundColor: '#F8FAFC',
                      border: '1px solid #D8E1EC',
                      padding: '8px 12px',
                      fontSize: '14px',
                      borderRadius: '8px',
                    }}
                    onClick={() =>
                      handleGeneralItemPageChange(generalItemCurrentPage + 1)
                    }
                    disabled={
                      generalItemCurrentPage === generalItemTotalPages ||
                      generalItemTotalPages === 0
                    }
                  >
                    {'>'}
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ top: "5rem" }}
        />
      </div>
    </>
  )
}

export { InventoryList } 











